/* ============================================================================
 * CUSTOMER SERVICE — UNIFIED ENGINE (customer-service.js)
 * ----------------------------------------------------------------------------
 * ONE engine for all 5 markets (NL / DE / FR / ES / UK) and all 5 category
 * pages (orders / payment / returns / about / contact). Every market-specific
 * value and every UI string is read from the #cs-config JSON blob rendered by
 * sections/customer-service-main.liquid. No hardcoded translation dictionaries,
 * no per-file forks.
 *
 * Globals kept for inline handlers / cross-IIFE use:
 *   window.handleOrderLookup, window.lookupOrder, window.downloadInvoice,
 *   window.animateFaqOpen, window.animateFaqClose, window.showOrderSkeleton,
 *   window.hideOrderSkeleton, window.setEstimatorCountryOrders,
 *   window.updateEstimatorOrders, window.showSection (compat shim)
 *
 * Defensive: this same file loads on every category page; every getElementById
 * / querySelector that may be absent is null-guarded.
 * ==========================================================================*/
(function () {
    'use strict';


    // Open the Dixa / Strappy chat from anywhere (empty states, errors, cards).
    window.csOpenChat = function () {
        if (typeof window._dixa_ !== 'undefined' && typeof window._dixa_.invoke === 'function') {
            window._dixa_.invoke('setWidgetOpen', true);
        } else if (typeof window.dixa !== 'undefined' && typeof window.dixa._invoke === 'function') {
            window.dixa._invoke('setWidgetOpen', true);
        }
    };

    // ── Parse #cs-config once ──────────────────────────────────────────────
    var CS_CONFIG = {};
    try {
        var cfgEl = document.getElementById('cs-config');
        if (cfgEl) CS_CONFIG = JSON.parse(cfgEl.textContent || '{}') || {};
    } catch (e) {
        CS_CONFIG = {};
    }
    if (!CS_CONFIG.endpoints) CS_CONFIG.endpoints = {};
    if (!CS_CONFIG.contact) CS_CONFIG.contact = {};
    if (!CS_CONFIG.strings) CS_CONFIG.strings = {};
    if (!Array.isArray(CS_CONFIG.invoiceCountries)) CS_CONFIG.invoiceCountries = [];

    // String helper: every old translations[locale][key] becomes t(key).
    // Shopify's `| t` filter HTML-escapes translations, so a string with an
    // apostrophe or ampersand arrives here as "Aujourd&#39;hui" / "Track &amp;
    // Trace". Assigned via textContent that entity would be shown literally, so
    // decode once on the way out. Locale strings are merchant-authored, same
    // trust level as before.
    function csDecodeEntities(s) {
        if (!s || s.indexOf('&') === -1) return s;
        try {
            var ta = document.createElement('textarea');
            ta.innerHTML = s;
            return ta.value;
        } catch (e) { return s; }
    }
    function t(key) {
        return csDecodeEntities((CS_CONFIG.strings && CS_CONFIG.strings[key]) || '');
    }

    // Expose config + helper for any inline/debug needs (read-only intent).
    window.CS_CONFIG = CS_CONFIG;

    // ── Compatibility shim ─────────────────────────────────────────────────
    // Each category is now its own URL; stray inline onclick="showSection('x')"
    // should navigate to that page's link instead of throwing.
    window.showSection = function (cat) {
        var link = document.querySelector(
            '.cs-nav-item[data-section="' + cat + '"], .cs-tab-item[data-section="' + cat + '"]'
        );
        if (link) window.location.href = link.getAttribute('href');
    };

    // ── Carrier registry + tracking URL helpers ────────────────────────────
    var carrierRegistry = {
        'PostNL': {
            countries: ['NL', 'Netherlands'],
            urlTemplate: function (trackingCode, postcode, countryCode) {
                /* jouw.postnl.nl expects {barcode}-{COUNTRY}-{postcode}. It was
                   pinned to NL, which produced a dead link for every export
                   market even though PostNL carries those parcels. */
                var cc = String(countryCode || 'NL').toUpperCase().replace(/[^A-Z]/g, '') || 'NL';
                if (postcode) {
                    return 'https://jouw.postnl.nl/track-and-trace/' + trackingCode + '-' + cc + '-' + postcode;
                }
                return 'https://jouw.postnl.nl/track-and-trace/' + trackingCode;
            },
            requiresPostcode: true
        },
        'Bpost': {
            countries: ['BE', 'Belgium'],
            urlTemplate: function (trackingCode) {
                return 'https://track.bpost.cloud/btr/web/#/search?itemCode=' + trackingCode + '&lang=nl';
            },
            requiresPostcode: false
        },
        'Deutsche Post': {
            countries: ['DE', 'Germany'],
            urlTemplate: function (trackingCode) {
                // The /sendungsverfolgung/verfolgen.html path loads a page that never
                // shows the parcel; /sendungsverfolgung.html does.
                return 'https://www.deutschepost.de/de/s/sendungsverfolgung.html?piececode=' + trackingCode;
            },
            requiresPostcode: false
        },
        'Sending ES': {
            countries: ['ES', 'Spain'],
            urlTemplate: function (trackingCode) {
                return 'https://parcelsapp.com/es/tracking/' + trackingCode;
            },
            requiresPostcode: false
        },
        'Correos': {
            countries: ['ES', 'Spain'],
            urlTemplate: function (trackingCode) {
                return 'https://parcelsapp.com/es/tracking/' + trackingCode;
            },
            requiresPostcode: false
        },
        'La Poste': {
            countries: ['FR', 'France'],
            urlTemplate: function (trackingCode) {
                return 'https://www.laposte.fr/outils/suivre-vos-envois?code=' + trackingCode;
            },
            requiresPostcode: false
        },
        'Royal Mail': {
            countries: ['GB', 'UK', 'United Kingdom', 'IE', 'Ireland'],
            urlTemplate: function (trackingCode) {
                return 'https://www.royalmail.com/track-your-item#/tracking-results/' + trackingCode;
            },
            requiresPostcode: false
        }
    };

    /* Prefer the tracking link the shipping platform put on the fulfilment.

       carrierRegistry below is a hand-maintained guess at each carrier's URL
       format, and it only knows the carriers someone thought to add: it was
       silently sending Deutsche Post Warenpost and LandMark Global parcels to
       the wrong tracker, and its last-resort fallback is Royal Mail, which is
       wrong for every market but the UK. The fulfilment's own tracking_url is
       written by the platform that actually booked the parcel, so it is right
       per carrier by construction and survives a carrier or format change.
       Populated on 98% of this store's fulfilments; the registry stays as the
       fallback for the rest. */
    /* Some stored tracking links are simply wrong, so a known-bad pattern is
       repaired rather than passed through.

       Deutsche Post: the URL written onto Warenpost fulfilments points at
       /de/s/sendungsverfolgung/verfolgen.html, which loads a page that never
       shows the parcel. The working form is /de/s/sendungsverfolgung.html with
       the same piececode. Reported from a real German order, so this repair
       applies wherever the bad path appears, including the value that arrives
       from the fulfilment. */
    function repairTrackingUrl(url) {
        var u = String(url || '');
        if (!u) return u;
        if (u.indexOf('deutschepost.de') !== -1 && u.indexOf('sendungsverfolgung/verfolgen.html') !== -1) {
            u = u.replace('sendungsverfolgung/verfolgen.html', 'sendungsverfolgung.html');
            // The working link carries no language override; a German customer
            // should get the German page.
            u = u.replace(/[?&]lang=[^&]*/g, '').replace(/[?&]cid=[^&]*/g, '');
            // Repairing may have removed the first parameter's separator.
            u = u.replace(/\?&/, '?').replace(/&&+/g, '&');
            if (u.indexOf('?') === -1 && u.indexOf('&') !== -1) u = u.replace('&', '?');
        }
        return u;
    }

    function resolveTrackingUrl(data, carrier) {
        var fromFulfilment = data && typeof data.trackingUrl === 'string' ? data.trackingUrl.trim() : '';
        if (/^https?:\/\//i.test(fromFulfilment)) return repairTrackingUrl(fromFulfilment);
        var addr = (data && data.shippingAddress) || {};
        return repairTrackingUrl(getTrackingUrl(carrier, data && data.trackingNumber, addr.zip, addr.countryCode));
    }

    function getTrackingUrl(carrier, trackingNumber, postcode, countryCode) {
        var carrierConfig = carrierRegistry[carrier];
        if (!carrierConfig) {
            var carrierKey = Object.keys(carrierRegistry).find(function (key) {
                return key.toLowerCase() === String(carrier || '').toLowerCase();
            });
            if (carrierKey) carrierConfig = carrierRegistry[carrierKey];
        }
        if (carrierConfig && carrierConfig.urlTemplate) {
            try {
                return carrierConfig.urlTemplate(trackingNumber, postcode, countryCode);
            } catch (error) {
                /* fall through to fallback */
            }
        }
        return 'https://www.royalmail.com/track-your-item#/tracking-results/' + encodeURIComponent(trackingNumber);
    }

    // Country -> local carrier override (display name).
    function getLocalCarrierName(apiCarrier, countryCode, country) {
        var code = (countryCode || '').toUpperCase().trim();
        var countryName = (country || '').toLowerCase().trim();
        if (code === 'BE' || countryName.indexOf('belgium') !== -1 || countryName.indexOf('belgi') !== -1) return 'Bpost';
        if (code === 'NL' || countryName.indexOf('netherlands') !== -1 || countryName.indexOf('nederland') !== -1) return 'PostNL';
        if (code === 'DE' || countryName.indexOf('germany') !== -1 || countryName.indexOf('deutschland') !== -1) return 'Deutsche Post';
        if (code === 'ES' || countryName.indexOf('spain') !== -1 || countryName.indexOf('espa') !== -1) return 'Correos';
        if (code === 'FR' || countryName.indexOf('france') !== -1) return 'La Poste';
        if (code === 'GB' || code === 'UK' || code === 'IE' ||
            countryName.indexOf('united kingdom') !== -1 || countryName.indexOf('ireland') !== -1) return 'Royal Mail';
        return apiCarrier;
    }

    // ── FAQ accordion animation (exposed globally) ─────────────────────────
    function animateFaqOpen(answer) {
        if (!answer) return;
        answer.style.overflow = 'hidden';
        answer.style.height = '0px';
        answer.style.paddingBottom = '0';
        void answer.offsetHeight;
        var target = answer.scrollHeight;
        answer.style.transition = 'height 300ms cubic-bezier(0.4,0,0.2,1), padding-bottom 300ms cubic-bezier(0.4,0,0.2,1)';
        answer.style.height = target + 'px';
        answer.style.paddingBottom = '1.5rem';
        answer.addEventListener('transitionend', function h() {
            answer.style.height = 'auto';
            answer.style.overflow = '';
            answer.style.transition = '';
            answer.removeEventListener('transitionend', h);
        });
        var item = answer.closest('.faq-item');
        if (item) {
            item.style.boxShadow = 'inset 3px 0 0 var(--primary-orange), var(--shadow-sm)';
            setTimeout(function () { item.style.boxShadow = ''; }, 600);
        }
    }
    window.animateFaqOpen = animateFaqOpen;

    function animateFaqClose(answer) {
        if (!answer) return;
        var current = answer.getBoundingClientRect().height;
        if (current === 0) return;
        answer.style.overflow = 'hidden';
        answer.style.height = current + 'px';
        void answer.offsetHeight;
        answer.style.transition = 'height 300ms cubic-bezier(0.4,0,0.2,1), padding-bottom 300ms cubic-bezier(0.4,0,0.2,1)';
        answer.style.height = '0px';
        answer.style.paddingBottom = '0';
        answer.addEventListener('transitionend', function h() {
            answer.style.height = '';
            answer.style.overflow = '';
            answer.style.transition = '';
            answer.style.paddingBottom = '';
            answer.removeEventListener('transitionend', h);
        });
    }
    window.animateFaqClose = animateFaqClose;

    // ── Skeleton loader (exposed globally) ─────────────────────────────────
    window.showOrderSkeleton = function () {
        var el = document.getElementById('orderResults');
        if (!el) return;
        el.style.display = 'block';
        el.innerHTML = '<div class="order-skeleton">' +
            '<div class="skeleton-header">' +
            '<div class="skeleton-block" style="width:40%;height:28px;border-radius:8px"></div>' +
            '<div class="skeleton-block" style="width:25%;height:32px;border-radius:999px"></div>' +
            '</div>' +
            '<div class="skeleton-details">' +
            '<div class="skeleton-block" style="width:100%;height:14px;border-radius:6px"></div>' +
            '<div class="skeleton-block" style="width:75%;height:14px;border-radius:6px"></div>' +
            '<div class="skeleton-block" style="width:55%;height:14px;border-radius:6px"></div>' +
            '</div>' +
            '<div class="skeleton-items">' +
            '<div class="skeleton-block" style="width:100%;height:52px;border-radius:12px"></div>' +
            '<div class="skeleton-block" style="width:100%;height:52px;border-radius:12px"></div>' +
            '</div></div>';
    };

    window.hideOrderSkeleton = function () {
        var el = document.getElementById('orderResults');
        if (!el) return;
        var sk = el.querySelector('.order-skeleton');
        if (sk) sk.remove();
    };

    // ── Order lookup handler (exposed globally) ────────────────────────────
    window.handleOrderLookup = function () {
        var btn = document.getElementById('lookupBtn');
        if (!btn || btn.disabled) return;
        var searchingLabel = t('searching') || 'Searching...';
        var foundLabel = t('orderFound') || 'Order found';
        var findLabel = t('findMyOrder') || 'Find my order';
        btn.disabled = true;
        btn.innerHTML = '<svg class="cs-btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
            '<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> ' + searchingLabel;
        window.showOrderSkeleton();
        var promise = window.lookupOrder();
        if (!promise || typeof promise.then !== 'function') {
            window.hideOrderSkeleton();
            btn.disabled = false;
            return;
        }
        promise.then(function () {
            window.hideOrderSkeleton();
            // Always restore the button to its normal, enabled state — a clean
            // search button the customer can reuse to look up another order
            // (a disabled "Order found" pill read as broken / mis-sized).
            btn.disabled = false;
            btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg> ' + findLabel;
        }).catch(function () {
            window.hideOrderSkeleton();
            btn.disabled = false;
            btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg> ' + findLabel;
        });
    };

    // ── Invoice download (exposed globally) ────────────────────────────────
    window.downloadInvoice = function (orderNumber, orderDate, locale) {
        var orderCard = document.querySelector('.order-card');
        if (!orderCard) return;

        // Fire-and-forget analytics.
        if (CS_CONFIG.endpoints.analyticsBase) {
            fetch(CS_CONFIG.endpoints.analyticsBase + '/invoice-download', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store: CS_CONFIG.store })
            }).catch(function () {});
        }

        function txt(el) { return (el && el.textContent) || ''; }
        var orderNumberText = txt(orderCard.querySelector('.order-number')) || orderNumber;
        var statusBadge = txt(orderCard.querySelector('.order-status-badge'));
        var detailValues = orderCard.querySelectorAll('.order-detail-value');
        var paymentStatus = txt(detailValues[0]);
        var carrier = txt(detailValues[1]);
        var shippingTo = txt(detailValues[2]);
        var contactEmail = txt(detailValues[3]);

        var customerName = orderCard.getAttribute('data-customer-name') || '';
        var customerPhone = orderCard.getAttribute('data-phone') || '';

        var subtotalValue = orderCard.getAttribute('data-subtotal') || '0.00';
        var taxValue = orderCard.getAttribute('data-tax') || '0.00';
        var discountsValue = orderCard.getAttribute('data-discounts') || '0.00';
        var shippingCostValue = orderCard.getAttribute('data-shipping-cost') || '0.00';
        var grandTotalValue = orderCard.getAttribute('data-total') || '0.00';
        var currencySymbol = orderCard.getAttribute('data-currency') || CS_CONFIG.currencySymbol || '€';
        var taxesIncluded = orderCard.getAttribute('data-taxes-included') === 'true';

        var taxLines = [];
        try {
            var taxLinesJSON = orderCard.getAttribute('data-tax-lines');
            if (taxLinesJSON) taxLines = JSON.parse(taxLinesJSON);
        } catch (e) { /* ignore */ }

        var shippingAddress = null;
        var billingAddress = null;
        try {
            var shippingJSON = orderCard.getAttribute('data-shipping-address');
            if (shippingJSON) shippingAddress = JSON.parse(shippingJSON.replace(/&quot;/g, '"'));
            var billingJSON = orderCard.getAttribute('data-billing-address');
            if (billingJSON) billingAddress = JSON.parse(billingJSON.replace(/&quot;/g, '"'));
        } catch (e) { /* ignore */ }

        var itemElements = orderCard.querySelectorAll('.order-item');
        var itemsHTML = '';
        itemElements.forEach(function (item) {
            var name = txt(item.querySelector('.order-item-name'));
            var title = txt(item.querySelector('.order-item-title'));
            var itemTitle = title || name;
            var qty = txt(item.querySelector('.order-item-qty'));
            var price = txt(item.querySelector('.order-item-price'));
            itemsHTML += '<tr>' +
                '<td>' + itemTitle + '</td>' +
                '<td style="text-align: center;">' + qty.replace(/^.*?:\s*/, '') + '</td>' +
                '<td style="text-align: right; font-weight: 600;">' + price + '</td>' +
                '</tr>';
        });

        var invoiceDate = new Date().toLocaleDateString(locale || CS_CONFIG.locale);

        var SWS_LOGO = 'https://cdn.shopify.com/s/files/1/0879/7120/1300/files/sws_logo_horizontal_small_rgb.png?v=1729582729';
        var SWB_LOGO = 'https://cdn.shopify.com/s/files/1/0879/7120/1300/files/swb_logo_horizontal_small_rgb.png?v=1725620750';
        var SWA_LOGO = 'https://cdn.shopify.com/s/files/1/0879/7120/1300/files/SWA_logo-01.png?v=1747825132';
        var BSW_LOGO = 'https://cdn.shopify.com/s/files/1/0879/7120/1300/files/bsw_logo_horizontal_small_rgb.png?v=1729582729';
        var countryLogos = {
            'UK': SWS_LOGO, 'GB': SWS_LOGO,
            'NL': SWB_LOGO, 'BE': SWB_LOGO,
            'DE': SWA_LOGO, 'AT': SWA_LOGO, 'CH': SWA_LOGO,
            'FR': BSW_LOGO,
            'ES': SWS_LOGO
        };

        function normalizeCountry(countryStr) {
            if (!countryStr) return '';
            var normalized = countryStr.toUpperCase().trim();
            var countryNameMap = {
                'UNITED KINGDOM': 'GB', 'GREAT BRITAIN': 'GB', 'ENGLAND': 'GB', 'SCOTLAND': 'GB', 'WALES': 'GB',
                'GERMANY': 'DE', 'DEUTSCHLAND': 'DE',
                'NETHERLANDS': 'NL', 'NEDERLAND': 'NL',
                'BELGIUM': 'BE', 'BELGIË': 'BE', 'BELGIQUE': 'BE',
                'SPAIN': 'ES', 'ESPAÑA': 'ES',
                'FRANCE': 'FR'
            };
            if (countryNameMap[normalized]) return countryNameMap[normalized];
            return normalized;
        }

        var rawCountry = (shippingAddress && (shippingAddress.countryCodeV2 || shippingAddress.countryCode || shippingAddress.country_code || shippingAddress.country)) || '';
        var country = normalizeCountry(rawCountry);
        // Logo follows the MARKET/store the order was placed on (BE = part of NL/FR, AT/CH = part of DE),
        // falling back to the shipping-country map only if the market logo is unset.
        var logoURL = CS_CONFIG.invoiceLogo || countryLogos[country] || '';

        function escapeHTML(str) {
            if (!str) return '';
            var div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        }

        function formatAddress(addr) {
            if (!addr) return '';
            var parts = [];
            if (addr.address1) parts.push(escapeHTML(addr.address1));
            if (addr.address2) parts.push(escapeHTML(addr.address2));
            var cityLine = [addr.city, addr.province, addr.zip].filter(Boolean).map(escapeHTML).join(', ');
            if (cityLine) parts.push(cityLine);
            if (addr.country) parts.push(escapeHTML(addr.country));
            return parts.filter(Boolean).join('<br>');
        }

        var companyLabel = t('company') || 'Company';
        var shippingAddressHTML = '';
        if (shippingAddress) {
            var sFullName = [shippingAddress.firstName, shippingAddress.lastName].filter(Boolean).map(escapeHTML).join(' ');
            var sPhone = escapeHTML(shippingAddress.phone || customerPhone);
            var addressFormatted = formatAddress(shippingAddress);
            shippingAddressHTML =
                (shippingAddress.company ? '<p><strong>' + companyLabel + ':</strong> ' + escapeHTML(shippingAddress.company) + '</p>' : '') +
                (sFullName ? '<p><strong>Name:</strong> ' + sFullName + '</p>' : '') +
                (addressFormatted ? '<p><strong>Address:</strong><br>' + addressFormatted + '</p>' : '<p><strong>Ship to:</strong> ' + escapeHTML(shippingTo) + '</p>') +
                (sPhone ? '<p><strong>Phone:</strong> ' + sPhone + '</p>' : '') +
                '<p><strong>Email:</strong> ' + escapeHTML(contactEmail) + '</p>';
        } else {
            shippingAddressHTML =
                (customerName ? '<p><strong>Customer:</strong> ' + escapeHTML(customerName) + '</p>' : '') +
                '<p><strong>' + (t('shippingTo') || 'Shipping To') + ':</strong> ' + escapeHTML(shippingTo) + '</p>' +
                (customerPhone ? '<p><strong>Phone:</strong> ' + escapeHTML(customerPhone) + '</p>' : '') +
                '<p><strong>Email:</strong> ' + escapeHTML(contactEmail) + '</p>';
        }

        var billingAddressHTML = '';
        if (billingAddress) {
            var bFullName = [billingAddress.firstName, billingAddress.lastName].filter(Boolean).map(escapeHTML).join(' ');
            var bPhone = escapeHTML(billingAddress.phone || customerPhone);
            billingAddressHTML =
                '<div class="info-section">' +
                '<h3>' + (t('orderDetails') || 'Billing details') + '</h3>' +
                (billingAddress.company ? '<p><strong>' + companyLabel + ':</strong> ' + escapeHTML(billingAddress.company) + '</p>' : '') +
                (bFullName ? '<p><strong>Name:</strong> ' + bFullName + '</p>' : '') +
                (formatAddress(billingAddress) ? '<p><strong>Address:</strong><br>' + formatAddress(billingAddress) + '</p>' : '') +
                (bPhone ? '<p><strong>Phone:</strong> ' + bPhone + '</p>' : '') +
                '<p><strong>Email:</strong> ' + escapeHTML(contactEmail) + '</p>' +
                '</div>';
        }

        var includedSuffix = ' (' + (t('tax') || 'Tax') + ')';
        var invoiceHTML =
            '<!DOCTYPE html><html><head><meta charset="UTF-8">' +
            '<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">' +
            '<title>' + (t('invoice') || 'Invoice') + ' - ' + orderNumberText + '</title>' +
            '<style>' +
            '* { margin: 0; padding: 0; box-sizing: border-box; }' +
            "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; padding: 2rem; background: #F8FAFC; }" +
            '.invoice-container { max-width: 800px; margin: 0 auto; background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }' +
            '.invoice-disclaimer { background: #FFF9F5; border-left: 4px solid #FE5000; padding: 0.75rem 1rem; margin-bottom: 1.5rem; border-radius: 6px; }' +
            '.invoice-disclaimer p { font-size: 0.8rem; color: #64748B; font-style: italic; }' +
            '.invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; padding-bottom: 1.5rem; border-bottom: 3px solid #FE5000; }' +
            '.company-logo { max-width: 200px; height: auto; margin-bottom: 0.5rem; }' +
            '.company-info h1 { font-size: 1.75rem; color: #FE5000; margin-bottom: 0.25rem; }' +
            '.company-info p { color: #64748B; line-height: 1.4; font-size: 0.875rem; }' +
            '.invoice-details { text-align: right; }' +
            '.invoice-details h2 { font-size: 1.5rem; color: #0F172A; margin-bottom: 0.5rem; }' +
            '.invoice-details p { color: #64748B; line-height: 1.8; }' +
            '.order-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem; padding: 1rem; background: #F8FAFC; border-radius: 8px; }' +
            '.info-section h3 { font-size: 0.75rem; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem; }' +
            '.info-section p { color: #0F172A; line-height: 1.5; font-size: 0.875rem; margin-bottom: 0.25rem; }' +
            '.items-table { width: 100%; margin-bottom: 1rem; border-collapse: collapse; }' +
            '.items-table thead { background: linear-gradient(135deg, #FE5000, #FF6B2B); color: white; }' +
            '.items-table th { padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; }' +
            '.items-table th:nth-child(2) { text-align: center; }' +
            '.items-table th:nth-child(3) { text-align: right; }' +
            '.items-table td { padding: 0.5rem 0.75rem; font-size: 0.875rem; }' +
            '.invoice-totals { margin-top: 1.5rem; padding: 1rem; background: #F8FAFC; border-radius: 8px; }' +
            '.totals-table { width: 100%; max-width: 350px; margin-left: auto; border-collapse: collapse; }' +
            '.totals-table td { padding: 0.5rem 0; font-size: 0.95rem; color: #475569; }' +
            '.totals-table td.amount { text-align: right; font-weight: 600; color: #0F172A; }' +
            '.totals-table .discount-row td { color: #16a34a; }' +
            '.totals-table .grand-total-row td { padding-top: 0.75rem; border-top: 2px solid #E2E8F0; font-size: 1.1rem; font-weight: 700; color: #0F172A; }' +
            '.totals-table .grand-total-row td.amount { color: #FE5000; font-size: 1.25rem; }' +
            '.invoice-footer { margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #E2E8F0; text-align: center; color: #64748B; font-size: 0.8rem; }' +
            '@media screen and (max-width: 768px) {' +
            'body { padding: 0.5rem; } .invoice-container { padding: 1rem; border-radius: 8px; }' +
            '.invoice-header { flex-direction: column; gap: 1rem; margin-bottom: 1rem; padding-bottom: 1rem; }' +
            '.company-logo { max-width: 150px; } .invoice-details { text-align: left; }' +
            '.order-info { grid-template-columns: 1fr !important; gap: 1rem; padding: 0.75rem; margin-bottom: 1rem; }' +
            '.totals-table { max-width: 100%; } }' +
            '@media print {' +
            '@page { size: A4; margin: 14mm; }' +
            'html, body { background: #fff !important; padding: 0; margin: 0; }' +
            '.invoice-container { box-shadow: none; border-radius: 0; padding: 0; max-width: none; }' +
            /* Keep the brand colours when printing; browsers strip backgrounds by default. */
            '.items-table thead, .invoice-disclaimer, .order-info, .invoice-totals {' +
            '-webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
            /* A flat fill reproduces far better than a gradient on paper. */
            '.items-table thead { background: #FE5000 !important; }' +
            /* Repeat the column headers on every page and never split a row. */
            '.items-table thead { display: table-header-group; }' +
            '.items-table tr, .totals-table tr { break-inside: avoid; page-break-inside: avoid; }' +
            '.invoice-header { break-after: avoid; page-break-after: avoid; }' +
            '.invoice-totals { break-before: avoid; page-break-before: avoid; }' +
            '.invoice-footer { break-before: avoid; page-break-before: avoid; }' +
            '}' +
            '</style></head><body>' +
            '<div class="invoice-container">' +
            '<div class="invoice-disclaimer"><p>' + (t('invoiceDisclaimer') || 'This is a system-generated copy for your own reference. Your official invoice has been sent to your email address.') + '</p></div>' +
            '<div class="invoice-header"><div class="company-info">' +
            (logoURL ? '<img src="' + logoURL + '" alt="' + (CS_CONFIG.invoiceBrand || CS_CONFIG.store || '') + '" class="company-logo">' : '<h1>' + (CS_CONFIG.invoiceBrand || CS_CONFIG.store || '') + '</h1>') +
            '</div><div class="invoice-details">' +
            '<h2>' + (t('invoice') || 'Invoice') + '</h2>' +
            '<p><strong>' + (t('date') || 'Date') + ':</strong> ' + invoiceDate + '</p>' +
            '<p><strong>' + (t('orderNumber') || 'Order') + ':</strong> ' + orderNumber + '</p>' +
            '</div></div>' +
            '<div class="order-info">' +
            '<div class="info-section">' +
            '<h3>' + (t('orderDetails') || 'Order Details') + '</h3>' +
            '<p><strong>' + (t('placedOn') || 'Placed on') + ':</strong> ' + orderDate + '</p>' +
            '<p><strong>' + (t('status') || 'Status') + ':</strong> ' + statusBadge + '</p>' +
            '<p><strong>' + (t('paymentStatus') || 'Payment Status') + ':</strong> ' + paymentStatus + '</p>' +
            '<p><strong>' + (t('carrier') || 'Carrier') + ':</strong> ' + carrier + '</p>' +
            '</div>' +
            '<div class="info-section">' +
            '<h3>' + (t('shippingDetails') || 'Shipping Details') + '</h3>' +
            shippingAddressHTML +
            '</div>' +
            billingAddressHTML +
            '</div>' +
            '<table class="items-table"><thead><tr>' +
            '<th>' + (t('item') || 'Item') + '</th>' +
            '<th>' + (t('quantity') || 'Quantity') + '</th>' +
            '<th>' + (t('price') || 'Price') + '</th>' +
            '</tr></thead><tbody>' + itemsHTML + '</tbody></table>' +
            '<div class="invoice-totals"><table class="totals-table"><tbody>' +
            '<tr><td>' + (t('subtotal') || 'Subtotal') + '</td><td class="amount">' + currencySymbol + parseFloat(subtotalValue).toFixed(2) + '</td></tr>' +
            '<tr><td>' + (t('shipping') || 'Shipping') + '</td><td class="amount">' + currencySymbol + parseFloat(shippingCostValue).toFixed(2) + '</td></tr>' +
            (parseFloat(discountsValue) > 0 ?
                '<tr class="discount-row"><td>' + (t('discount') || 'Discount') + '</td><td class="amount">-' + currencySymbol + parseFloat(discountsValue).toFixed(2) + '</td></tr>' : '') +
            (taxLines.length > 0 ? taxLines.map(function (line) {
                return '<tr><td>' + line.title + (line.ratePercentage ? ' (' + line.ratePercentage + '%)' : '') + (taxesIncluded ? includedSuffix : '') + '</td>' +
                    '<td class="amount">' + currencySymbol + parseFloat(line.price).toFixed(2) + '</td></tr>';
            }).join('') :
                '<tr><td>' + (t('tax') || 'Tax') + (taxesIncluded ? includedSuffix : '') + '</td><td class="amount">' + currencySymbol + parseFloat(taxValue).toFixed(2) + '</td></tr>') +
            '<tr class="grand-total-row"><td>' + (t('grandTotal') || 'Total') + '</td><td class="amount">' + currencySymbol + parseFloat(grandTotalValue).toFixed(2) + '</td></tr>' +
            '</tbody></table></div>' +
            '<div class="invoice-footer"><p>' + (t('thankYou') || '') + '</p>' +
            '<p style="margin-top: 1rem;">' + (CS_CONFIG.store || '') + ' © ' + new Date().getFullYear() + '</p></div>' +
            '</div></body></html>';

        printInvoiceDocument(invoiceHTML);
    };

    /* Render the invoice into an OFF-SCREEN IFRAME and print that, instead of
       opening a second tab. A popup gets blocked (the old code had to warn
       about exactly that), reads as "a print app opening", and on mobile
       throws the customer out of the page. Same-origin iframe printing is not
       blocked, keeps the customer on the page, and the browser's own
       "Save as PDF" destination produces a real PDF with no library shipped. */
    function printInvoiceDocument(html) {
        var existing = document.getElementById('cs-invoice-frame');
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

        var frame = document.createElement('iframe');
        frame.id = 'cs-invoice-frame';
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.title = t('invoice') || 'Invoice';
        // Off-screen rather than display:none, which some browsers refuse to print.
        frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;opacity:0;pointer-events:none;';
        document.body.appendChild(frame);

        var win = frame.contentWindow;
        if (!win) { fallbackToWindow(html); return; }
        try {
            win.document.open();
            win.document.write(html);
            win.document.close();
        } catch (writeErr) {
            if (frame.parentNode) frame.parentNode.removeChild(frame);
            fallbackToWindow(html);
            return;
        }

        var printed = false;
        function doPrint() {
            if (printed) return;
            printed = true;
            try {
                win.focus();
                win.print();
            } catch (printErr) {
                fallbackToWindow(html);
            }
            // Tear down well after the dialog is done. The frame is invisible
            // and empty-weight, so a generous delay costs nothing and removing
            // it too early cancels the print job in some browsers.
            window.setTimeout(function () {
                var f = document.getElementById('cs-invoice-frame');
                if (f && f.parentNode) f.parentNode.removeChild(f);
            }, 60000);
        }

        /* A document.write'd iframe does not reliably fire load, and printing
           before the logo has loaded would drop it from the PDF. So wait for
           the images, with a hard cap, and race that against onload. */
        function whenImagesReady(cb) {
            var imgs;
            try { imgs = win.document.images; } catch (e) { cb(); return; }
            if (!imgs || !imgs.length) { cb(); return; }
            var remaining = imgs.length, settled = false;
            function tick() { if (--remaining <= 0 && !settled) { settled = true; cb(); } }
            for (var i = 0; i < imgs.length; i++) {
                if (imgs[i].complete) { tick(); }
                else {
                    imgs[i].addEventListener('load', tick);
                    imgs[i].addEventListener('error', tick);
                }
            }
            window.setTimeout(function () { if (!settled) { settled = true; cb(); } }, 2500);
        }

        frame.onload = function () { whenImagesReady(doPrint); };
        window.setTimeout(function () { whenImagesReady(doPrint); }, 400);
    }

    /* Last resort only: the old second-tab route. */
    function fallbackToWindow(html) {
        var w = window.open('', '_blank');
        if (!w) {
            if (window.csToast) window.csToast(t('popupBlocked') || 'Please allow popups to download the invoice');
            else alert(t('popupBlocked') || 'Please allow popups to download the invoice');
            return;
        }
        w.document.write(html);
        w.document.close();
        window.setTimeout(function () { try { w.print(); } catch (e) {} }, 600);
    }

    // ── Order lookup fetch + render (exposed globally) ─────────────────────
    window.lookupOrder = function () {
        var numEl = document.getElementById('orderNumber');
        var emailEl = document.getElementById('orderEmail');
        var resultsDiv = document.getElementById('orderResults');
        var lookupBtn = document.getElementById('lookupBtn');
        if (!numEl || !emailEl || !resultsDiv) return Promise.resolve();

        var orderNumber = numEl.value.trim();
        var orderEmail = emailEl.value.trim();
        if (!orderNumber || !orderEmail) return Promise.resolve();

        var apiUrl = CS_CONFIG.endpoints.trackOrder || 'https://actium.automagically.nl/webhook/track-order';
        var locale = CS_CONFIG.locale || 'en-GB';
        var findLabel = t('findMyOrder') || 'Find my order';

        if (lookupBtn) {
            lookupBtn.disabled = true;
            lookupBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite;"><circle cx="12" cy="12" r="10" opacity="0.25"></circle><path d="M4 12a8 8 0 0 1 8-8" opacity="0.75"></path></svg> ' + (t('searching') || 'Searching...');
        }

        function resetBtn(label) {
            if (!lookupBtn) return;
            lookupBtn.disabled = false;
            lookupBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg> ' + label;
        }

        return fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderNumber: orderNumber, email: orderEmail, country: CS_CONFIG.country })
        }).then(function (response) {
            return response.json().then(function (data) {
                return { response: response, data: data };
            });
        }).then(function (bundle) {
            var response = bundle.response;
            var data = bundle.data;
            // Locale stays config-driven; the API response no longer swaps dictionaries.

            if (!response.ok || data.error) {
                /* Anything unrecognised falls back to "order not found", which
                   is right for a genuine miss but wrong for a throttled
                   request: it tells a customer their details are wrong when
                   they only need to wait. The proxy already sends a localised
                   sentence for that case, so use it. */
                var errorText;
                if (data.errorType === 'rate_limited') {
                    errorText = (typeof data.error === 'string' && data.error) ||
                        (t('rateLimited') || 'Too many lookups. Please wait a few minutes and try again.');
                } else if (data.errorType === 'connection') {
                    errorText = t('connectionError') || 'Connection error';
                } else {
                    errorText = t('orderNotFound') || 'Order not found';
                }
                resultsDiv.innerHTML =
                    '<div class="order-error">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>' +
                    '<p>' + errorText + '</p>' +
                    (CS_CONFIG.chatEnabled ? '<button type="button" class="cs-search-empty__chat" onclick="if(window.csOpenChat)window.csOpenChat();">' + (t('chatButtonLabel') || 'Chat with us') + '</button>' : '') +
                    '</div>';
                resultsDiv.style.display = 'block';
                resetBtn(findLabel);
                return;
            }

            // Successful search analytics (fire-and-forget).
            if (CS_CONFIG.endpoints.analyticsBase) {
                fetch(CS_CONFIG.endpoints.analyticsBase + '/order-search', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ store: CS_CONFIG.store })
                }).catch(function () {});
            }

            var currencySymbol = data.currencySymbol || CS_CONFIG.currencySymbol || '€';

            var displayCarrier = getLocalCarrierName(
                data.carrier,
                data.shippingAddress && data.shippingAddress.countryCode,
                data.shippingAddress && data.shippingAddress.country
            );

            var statusClass = data.status === 'Shipped' ? 'status-shipped' :
                data.status === 'Processing' ? 'status-processing' : 'status-pending';

            var statusMap = {
                'Shipped': t('shipped'),
                'Processing': t('processing'),
                'Pending Payment': t('pending')
            };
            var translatedStatus = statusMap[data.status] || data.status;

            var financialStatusMap = {
                'Paid': t('paid'),
                'Unpaid': t('unpaid'),
                'Refunded': t('refunded'),
                'Pending': t('pending')
            };
            var translatedFinancialStatus = financialStatusMap[data.financialStatus] || data.financialStatus;

            function hasValidTracking(trackingNum) {
                if (!trackingNum) return false;
                var normalized = String(trackingNum).trim().toLowerCase();
                var invalidSentinels = ['not yet shipped', 'not available', 'no tracking provided', 'pending', 'n/a', 'none', ''];
                return invalidSentinels.indexOf(normalized) === -1;
            }

            /* Refunded and cancelled orders get no tracking button. The
               customer is not waiting for the parcel, and pushing them to a
               carrier page is noise at best. The live status module skips
               these orders for the same reason, which is why this outbound
               button was the only tracking left on such a card. */
            var isClosedOrder = data.financialStatus === 'Refunded' ||
                data.financialStatus === 'partially_refunded' ||
                data.status === 'Cancelled';

            var trackingButtonHTML = '';
            if (isClosedOrder) {
                /* Nothing at all in this slot. Gating only the button pushed
                   the else branch into view instead, which told a refunded
                   customer "your order is on its way" - the same mistake as
                   the delay message, just moved. The refund box above already
                   states where the order stands. */
                trackingButtonHTML = '';
            } else if (hasValidTracking(data.trackingNumber)) {
                var trackingUrl = resolveTrackingUrl(data, displayCarrier);
                trackingButtonHTML = '<a href="' + trackingUrl + '" target="_blank" rel="noopener noreferrer" class="lookup-btn" style="padding: 0.625rem 1.25rem; font-size: 0.9375rem; white-space: nowrap;"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 18px; height: 18px;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>' + (t('trackPackage') || 'Track Package') + '</a>';
            } else {
                var messageText = (data.status === 'Shipped')
                    ? (t('untrackedDelivery') || 'Your order has been dispatched and is on its way to you.')
                    : (t('trackingAvailableSoon') || 'Tracking will be available soon');
                trackingButtonHTML = '<div class="tracking-status-message" style="padding: 0.5rem 0.875rem; font-size: 0.8125rem; color: #FE5000; background: rgba(255, 255, 255, 0.95); font-weight: 600; border-radius: 12px; max-width: 280px; overflow-wrap: break-word; word-wrap: break-word; hyphens: auto; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); line-height: 1.35;">' + messageText + '</div>';
            }

            var itemsHTML = (data.lineItems || []).map(function (item) {
                return '<div class="order-item"><div>' +
                    '<div class="order-item-name">' + (item.translatedName || item.title || item.name) + '</div>' +
                    '<span class="order-item-qty">' + (t('qty') || 'Qty') + ': ' + item.quantity + '</span>' +
                    '</div><div class="order-item-price">' + currencySymbol + parseFloat(item.price).toFixed(2) + '</div></div>';
            }).join('');

            var shippingHTML = '';
            if (data.shippingAddress) {
                shippingHTML = '<div class="order-detail"><div class="order-detail-label">' + (t('shippingTo') || 'Shipping To') + '</div>' +
                    '<div class="order-detail-value">' + data.shippingAddress.city + ', ' + data.shippingAddress.country + '</div></div>';
            }

            var customerFullName = (data.shippingAddress && data.shippingAddress.firstName && data.shippingAddress.lastName)
                ? (data.shippingAddress.firstName + ' ' + data.shippingAddress.lastName) : '';
            var escapedCustomerName = customerFullName.replace(/"/g, '&quot;');

            /* Invoice availability, gated by CS_CONFIG.invoiceCountries.

               This used to substring-match the country NAME against the
               country CODES, which is not a comparison that means anything:
               it hid the invoice for Germany, Spain, Austria and Switzerland
               (no code appears inside "germany", "spain", "austria",
               "switzerland") while "working" for France only because
               "france" contains "fr". It also opened the invoice to
               countries NOT on the list whose names happen to contain a
               configured code: Chile and Czechia contain "ch", Estonia
               contains "es".

               The order lookup now returns a real ISO countryCode, so match
               on that. The name map below is a fallback for the countries we
               actually ship to, in case a response arrives without a code. */
            var shippingCountry = (data.shippingAddress && data.shippingAddress.country || '').toLowerCase().trim();
            var shippingCountryCode = (data.shippingAddress && data.shippingAddress.countryCode || '').toUpperCase().trim();
            var invoiceCountriesUpper = CS_CONFIG.invoiceCountries.map(function (c) { return String(c).toUpperCase().trim(); });

            var COUNTRY_NAME_TO_CODE = {
                'netherlands': 'NL', 'nederland': 'NL', 'holland': 'NL',
                'belgium': 'BE', 'belgie': 'BE', 'belgië': 'BE', 'belgique': 'BE', 'belgien': 'BE',
                'germany': 'DE', 'deutschland': 'DE', 'duitsland': 'DE', 'allemagne': 'DE', 'alemania': 'DE',
                'austria': 'AT', 'osterreich': 'AT', 'österreich': 'AT', 'oostenrijk': 'AT', 'autriche': 'AT',
                'switzerland': 'CH', 'schweiz': 'CH', 'suisse': 'CH', 'zwitserland': 'CH', 'suiza': 'CH',
                'france': 'FR', 'frankrijk': 'FR', 'frankreich': 'FR', 'francia': 'FR',
                'spain': 'ES', 'spanje': 'ES', 'spanien': 'ES', 'espagne': 'ES', 'españa': 'ES', 'espana': 'ES',
                'united kingdom': 'GB', 'great britain': 'GB', 'england': 'GB', 'scotland': 'GB',
                'wales': 'GB', 'northern ireland': 'GB', 'verenigd koninkrijk': 'GB',
                'luxembourg': 'LU', 'luxemburg': 'LU', 'letzebuerg': 'LU'
            };

            var resolvedCode = shippingCountryCode || COUNTRY_NAME_TO_CODE[shippingCountry] || '';
            // GB and UK are the same place; accept either spelling in the list.
            var isInvoiceAvailable = resolvedCode
                ? (invoiceCountriesUpper.indexOf(resolvedCode) !== -1 ||
                   (resolvedCode === 'GB' && invoiceCountriesUpper.indexOf('UK') !== -1) ||
                   (resolvedCode === 'UK' && invoiceCountriesUpper.indexOf('GB') !== -1))
                : false;

            function addWorkingDays(date, days) {
                var result = new Date(date);
                var added = 0;
                while (added < days) {
                    result.setDate(result.getDate() + 1);
                    if (result.getDay() !== 0 && result.getDay() !== 6) added++;
                }
                result.setHours(0, 0, 0, 0);
                return result;
            }

            // Market-aware delivery windows + delay thresholds (working days added
            // to shipDate). notify = yellow "slight delay" threshold, delayed = red
            // "significantly delayed" threshold. skipMonday => use the PostNL
            // Sun+Mon-skip calc (addNLDeliveryDays) instead of addWorkingDays.
            function getDeliveryConfig(market, destCode) {
                var dc = String(destCode || '').toUpperCase();
                switch (market) {
                    case 'nl':
                        if (dc === 'NL') return { min: 1, max: 2, notify: 4, delayed: 8, skipMonday: true };
                        if (dc === 'BE') return { min: 2, max: 4, notify: 5, delayed: 14, skipMonday: false };
                        return { min: 2, max: 4, notify: 6, delayed: 10, skipMonday: false };
                    case 'de':
                        if (dc === 'DE') return { min: 1, max: 2, notify: 6, delayed: 10, skipMonday: false };
                        return { min: 2, max: 4, notify: 6, delayed: 10, skipMonday: false };
                    case 'fr':
                        return { min: 3, max: 7, notify: 8, delayed: 12, skipMonday: false };
                    case 'es':
                        return { min: 4, max: 6, notify: 7, delayed: 11, skipMonday: false };
                    case 'uk':
                        return { min: 3, max: 7, notify: 8, delayed: 12, skipMonday: false };
                    default:
                        return { min: 2, max: 4, notify: 6, delayed: 10, skipMonday: false };
                }
            }

            var deliveryPredictionHTML = '';
            var isNetherlandsOrder = shippingCountry.indexOf('netherlands') !== -1 || shippingCountry.indexOf('nederland') !== -1 || shippingCountryCode === 'NL';
            var isBelgiumOrder = shippingCountry.indexOf('belgium') !== -1 || shippingCountry.indexOf('belgi') !== -1 || shippingCountryCode === 'BE';

            if (data.shipDate) {
                var shipDate = new Date(data.shipDate);
                var today = new Date();
                today.setHours(0, 0, 0, 0);

                function addNLDeliveryDays(date, days) {
                    var result = new Date(date);
                    var added = 0;
                    while (added < days) {
                        result.setDate(result.getDate() + 1);
                        if (result.getDay() !== 0 && result.getDay() !== 1) added++;
                    }
                    result.setHours(0, 0, 0, 0);
                    return result;
                }

                var market = (CS_CONFIG.market || '').toLowerCase();
                var destForConfig = shippingCountryCode || (isNetherlandsOrder ? 'NL' : (isBelgiumOrder ? 'BE' : ''));
                var deliveryConfig = getDeliveryConfig(market, destForConfig);
                var addDeliveryDays = deliveryConfig.skipMonday ? addNLDeliveryDays : addWorkingDays;

                var earliestDate = addDeliveryDays(shipDate, deliveryConfig.min);
                var latestDate = addDeliveryDays(shipDate, deliveryConfig.max);
                var delayedNotificationDate = addDeliveryDays(shipDate, deliveryConfig.notify);
                var delayedThreshold = addDeliveryDays(shipDate, deliveryConfig.delayed);

                var hasArchivedTag = data.tags && data.tags.indexOf('Gearchiveerd') !== -1;
                var isDelivered = data.shipmentStatus === 'delivered' || hasArchivedTag;

                function formatDateWithDay(date) {
                    // Localise the month + weekday names to the market locale so
                    // DE/FR/ES orders no longer show English dates.
                    try {
                        var loc = locale || CS_CONFIG.localeCode || 'en-GB';
                        var month = date.toLocaleDateString(loc, { month: 'long' });
                        var day = date.toLocaleDateString(loc, { weekday: 'long' });
                        function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
                        return date.getDate() + ' ' + cap(month) + ' ' + cap(day);
                    } catch (e) {
                        var monthsEN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                        var daysEN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        return date.getDate() + ' ' + monthsEN[date.getMonth()] + ' ' + daysEN[date.getDay()];
                    }
                }

                var earliestFormatted = formatDateWithDay(earliestDate);
                var latestFormatted = formatDateWithDay(latestDate);

                function isBusyShoppingSeason() {
                    var m = new Date().getMonth();
                    return m === 10 || m === 11 || m === 0;
                }

                var seasonalMessage = isBusyShoppingSeason() ?
                    '<div class="delivery-prediction__seasonal"><div class="delivery-prediction__seasonal-header">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>' +
                    '<p class="delivery-prediction__seasonal-title">' + (t('deliveryDelayed') || 'Seasonal notice') + '</p></div>' +
                    '<p class="delivery-prediction__seasonal-body">' + (t('deliveryDelayedMessage') || 'Some deliveries may be slightly delayed due to seasonal demand. Thank you for your patience.') + '</p></div>' : '';

                if (isDelivered) {
                    deliveryPredictionHTML = '';
                } else if (data.financialStatus === 'Refunded' || data.financialStatus === 'partially_refunded' || data.status === 'Cancelled') {
                    deliveryPredictionHTML = '';
                } else if (today > delayedThreshold || (deliveryConfig.skipMonday && today > delayedNotificationDate)) {
                    var assumedTrackingUrl = resolveTrackingUrl(data, displayCarrier);
                    deliveryPredictionHTML =
                        '<div class="delivery-prediction delivery-prediction--success-assumed" style="background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%); border: 2px solid rgba(16, 185, 129, 0.2);">' +
                        '<div class="delivery-prediction__header">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>' +
                        '<h4 class="delivery-prediction__title" style="color: #065F46; font-size: 1.125rem;">' + (t('yourOrderShouldArrive') || 'Has your order arrived?') + '</h4></div>' +
                        '<p class="delivery-prediction__body" style="color: #047857; margin-bottom: 1rem; font-size: 0.9375rem;">' +
                        (t('estimatedDelivery') || 'According to our schedule, your order should have been delivered by') + ' <strong>' + displayCarrier + '</strong>.</p>' +
                        (hasValidTracking(data.trackingNumber) ?
                            '<div style="margin-bottom: 1.25rem;"><a href="' + assumedTrackingUrl + '" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 0.5rem; background: white; color: #065F46; padding: 0.625rem 1rem; border-radius: 0.5rem; text-decoration: none; font-weight: 600; font-size: 0.875rem; border: 1px solid rgba(16, 185, 129, 0.3); box-shadow: 0 2px 4px rgba(0,0,0,0.05);">' +
                            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 16px; height: 16px;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>' +
                            (t('trackPackage') || 'Track Package') + '</a></div>' : '') +
                        '<p class="delivery-prediction__cta" style="border-top: 1px solid rgba(16, 185, 129, 0.2); padding-top: 0.875rem; color: #065F46; font-size: 0.875rem; margin: 0;">' +
                        '<strong>' + (t('contactUsNow') || 'Not received anything yet?') + '</strong> ' +
                        '<a href="#contact" onclick="if(window.showSection){window.showSection(\'contact\');} return true;" style="color: #047857; text-decoration: underline;">' + (t('contactUsMessage') || 'let us know') + '</a></p>' +
                        '</div>';
                } else if (today > delayedNotificationDate) {
                    var shipDateFormatted = formatDateWithDay(shipDate);
                    var dynamicEarliest = earliestDate;
                    var dynamicLatest = addWorkingDays(latestDate, 2);
                    if (today >= dynamicLatest) {
                        dynamicEarliest = addWorkingDays(today, 1);
                        dynamicLatest = addWorkingDays(today, 3);
                    } else if (today >= dynamicEarliest) {
                        dynamicEarliest = addWorkingDays(today, 1);
                        if (dynamicLatest <= dynamicEarliest) dynamicLatest = addWorkingDays(dynamicEarliest, 2);
                    }
                    var extendedEarliestFormatted = formatDateWithDay(dynamicEarliest);
                    var extendedLatestFormatted = formatDateWithDay(dynamicLatest);
                    deliveryPredictionHTML = seasonalMessage +
                        '<div class="delivery-prediction delivery-prediction--delayed">' +
                        '<div class="delivery-prediction__header">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>' +
                        '<h4 class="delivery-prediction__title">' + (t('deliveryDelayed') || 'Slight Delay') + '</h4></div>' +
                        '<p class="delivery-prediction__body">' + (t('deliveryDelayedMessage') || 'Your order is taking a little longer than planned. It usually still arrives within a few days.') + '</p>' +
                        '<div class="delivery-prediction__tags">' +
                        '<span class="delivery-prediction__tag delivery-prediction__tag--muted">' + (t('orderFulfilled') || 'Shipped') + ': ' + shipDateFormatted + '</span>' +
                        '<span class="delivery-prediction__tag delivery-prediction__tag--accent">' + (t('estimatedArrival') || 'New estimate') + ': ' + extendedEarliestFormatted + ' - ' + extendedLatestFormatted + '</span>' +
                        '</div></div>';
                } else {
                    var shipDateFormatted2 = formatDateWithDay(shipDate);
                    var deliveryMessage = t('yourOrderShouldArrive');
                    var deliveryNote = '';
                    var arrivalLabel = t('estimatedArrival');
                    var arrivalText = earliestFormatted + ' - ' + latestFormatted;

                    if (isNetherlandsOrder) {
                        deliveryMessage = t('yourOrderShouldArriveNL') || t('yourOrderShouldArrive');
                        deliveryNote = t('nlDeliveryNote') || '';
                        arrivalLabel = t('nlExpectedDelivery') || t('estimatedArrival');
                        arrivalText = earliestFormatted;
                    } else if (isBelgiumOrder) {
                        deliveryMessage = t('yourOrderShouldArriveBE') || t('yourOrderShouldArrive');
                        deliveryNote = t('beDeliveryNote') || '';
                        arrivalLabel = t('beExpectedDelivery') || t('estimatedArrival');
                    }

                    deliveryPredictionHTML = seasonalMessage +
                        '<div class="delivery-prediction delivery-prediction--normal">' +
                        '<div class="delivery-prediction__header">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>' +
                        '<h4 class="delivery-prediction__title">' + (t('estimatedDelivery') || 'Estimated Delivery') + '</h4></div>' +
                        '<p class="delivery-prediction__body">' + deliveryMessage + '</p>' +
                        (deliveryNote ? '<p class="delivery-prediction__note">' + deliveryNote + '</p>' : '') +
                        '<div class="delivery-prediction__timeline" style="gap: 0.375rem;">' +
                        '<div class="delivery-prediction__row">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="var(--primary-orange)" stroke-width="2" style="width:14px;height:14px;"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
                        '<span class="delivery-prediction__label">' + arrivalLabel + '</span>' +
                        '<span class="delivery-prediction__value delivery-prediction__value--highlight">' + arrivalText + '</span></div>' +
                        '<div class="delivery-prediction__row" style="opacity: 0.7; font-size: 0.8125rem;">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2" style="width:14px;height:14px;"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
                        '<span class="delivery-prediction__label">' + (t('orderFulfilled') || 'Order fulfilled:') + '</span>' +
                        '<span class="delivery-prediction__value">' + shipDateFormatted2 + '</span></div>' +
                        '</div></div>';
                }
            }

            // Refunded / cancelled override.
            if (data.financialStatus === 'Refunded' || data.financialStatus === 'partially_refunded' || data.status === 'Cancelled') {
                var isRefund = data.financialStatus === 'Refunded' || data.financialStatus === 'partially_refunded';
                var titleText = isRefund
                    ? (t('refunded') || 'Order refunded')
                    : (t('cancelled') || 'Order cancelled');
                var bodyText = isRefund
                    ? (t('refundedMessage') || 'This order has been refunded.')
                    : (t('cancelledMessage') || 'This order has been cancelled.');
                deliveryPredictionHTML =
                    '<div class="delivery-prediction" style="background: linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%); border: 2px solid rgba(156, 163, 175, 0.3);">' +
                    '<div class="delivery-prediction__header">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="#4B5563" stroke-width="2.5" style="width: 22px; height: 22px;"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>' +
                    '<h4 class="delivery-prediction__title" style="color: #374151; font-size: 1.125rem;">' + titleText + '</h4></div>' +
                    '<p class="delivery-prediction__body" style="color: #4B5563; margin-bottom: 0; font-size: 0.9375rem;">' + bodyText + '</p></div>';
            }

            // ── Status stepper (Order placed -> Processing -> Shipped -> Delivered) ──
            // Built in-render for ALL markets. Hidden for refunded / cancelled orders
            // (the refund/cancel prediction box is shown instead).
            var stepperHTML = '';
            var isRefundedOrCancelled = data.financialStatus === 'Refunded' || data.financialStatus === 'partially_refunded' || data.status === 'Cancelled';
            if (!isRefundedOrCancelled) {
                var stepperHasArchivedTag = data.tags && data.tags.indexOf('Gearchiveerd') !== -1;
                var stepperIsDelivered = data.shipmentStatus === 'delivered' || stepperHasArchivedTag;
                var isAssumedDelivered = deliveryPredictionHTML.indexOf('delivery-prediction--success-assumed') !== -1;
                var isStepDelayed = deliveryPredictionHTML.indexOf('delivery-prediction--delayed') !== -1;

                var currentStep;
                if (isAssumedDelivered || stepperIsDelivered) {
                    currentStep = 4;
                } else if (data.status === 'Shipped') {
                    currentStep = 3;
                } else if (data.status === 'Processing') {
                    currentStep = 2;
                } else {
                    currentStep = 1;
                }

                var deliveredLabel = t('delivered');
                if (!deliveredLabel) {
                    var deliveredFallbacks = {
                        'nl-NL': 'Afgeleverd',
                        'de-DE': 'Zugestellt',
                        'fr-FR': 'Livré',
                        'es-ES': 'Entregado'
                    };
                    deliveredLabel = deliveredFallbacks[locale] || 'Delivered';
                }

                var stepDefs = [
                    { label: (t('orderPlaced') || 'Order placed'), icon: '<path d="M9 11l3 3L22 4"></path><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>' },
                    { label: (t('processing') || 'Processing'), icon: '<circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>' },
                    { label: (t('shipped') || 'Shipped'), icon: '<rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle>' },
                    { label: deliveredLabel, icon: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>' }
                ];

                var stepperSteps = stepDefs.map(function (def, idx) {
                    var num = idx + 1;
                    var stateCls = num < currentStep ? 'is-done'
                        : (num === currentStep ? (isStepDelayed ? 'is-delayed' : 'is-current') : 'is-upcoming');
                    return '<li class="cs-stepper__step ' + stateCls + '">' +
                        '<span class="cs-stepper__line" aria-hidden="true"></span>' +
                        '<span class="cs-stepper__dot">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' + def.icon + '</svg>' +
                        '</span>' +
                        '<span class="cs-stepper__label">' + def.label + '</span>' +
                        '</li>';
                }).join('');

                stepperHTML = '<ol class="cs-stepper" data-current-step="' + currentStep + '">' + stepperSteps + '</ol>';
            }

            var shippingAddressJSON = data.shippingAddress ? JSON.stringify(data.shippingAddress).replace(/"/g, '&quot;') : '';
            var billingAddressJSON = data.billingAddress ? JSON.stringify(data.billingAddress).replace(/"/g, '&quot;') : '';
            var customerPhone = (data.phone || '').replace(/"/g, '&quot;');

            var displayOrderDate = data.orderDate;
            try {
                if (data.orderDate) {
                    var d = new Date(data.orderDate);
                    if (!isNaN(d.getTime())) {
                        /* Format in the page's own language. This used to translate
                           the month for nl-NL only, so DE/FR/ES customers read
                           "10 October 2026" on their order card. */
                        try {
                            displayOrderDate = new Intl.DateTimeFormat(locale || 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
                        } catch (fmtErr) {
                            displayOrderDate = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                        }
                    }
                }
            } catch (e) { /* ignore */ }

            var includedSuffix2 = ' (' + (t('tax') || 'Tax') + ')';

            resultsDiv.innerHTML =
                '<div class="order-card order-card--v2"' +
                ' data-financial-status="' + (data.financialStatus || '') + '"' +
                ' data-order-status="' + (data.status || '') + '"' +
                ' data-customer-name="' + escapedCustomerName + '"' +
                ' data-shipping-address="' + shippingAddressJSON + '"' +
                ' data-billing-address="' + billingAddressJSON + '"' +
                ' data-phone="' + customerPhone + '"' +
                ' data-subtotal="' + (data.subtotalPrice || '0.00') + '"' +
                ' data-tax="' + (data.totalTax || '0.00') + '"' +
                ' data-discounts="' + (data.totalDiscounts || '0.00') + '"' +
                ' data-shipping-cost="' + (data.totalShippingPrice || '0.00') + '"' +
                ' data-total="' + (data.totalPrice || '0.00') + '"' +
                ' data-currency="' + currencySymbol + '"' +
                " data-tax-lines='" + JSON.stringify(data.taxLines || []) + "'" +
                ' data-taxes-included="' + (data.taxesIncluded || false) + '">' +
                '<div class="order-card-header-v2"><div class="order-card-header-info">' +
                '<div class="order-number">' + (t('orderNumber') || 'Order') + ' ' + data.orderNumber + '</div>' +
                '<div class="order-date">' + (t('placedOn') || 'Placed on') + ': ' + displayOrderDate + '</div>' +
                '</div><div class="order-card-header-actions">' +
                '<span class="order-status-badge order-status-badge--v2 ' + statusClass + '">' + translatedStatus + '</span>' +
                trackingButtonHTML +
                '</div></div>' +
                '<div class="order-card-body-v2">' +
                stepperHTML +
                deliveryPredictionHTML +
                '<div class="order-details order-details--v2">' +
                '<div class="order-detail"><div class="order-detail-label">' + (t('paymentStatus') || 'Payment Status') + '</div>' +
                '<div class="order-detail-value">' + translatedFinancialStatus + '</div></div>' +
                '<div class="order-detail"><div class="order-detail-label">' + (t('carrier') || 'Carrier') + '</div>' +
                '<div class="order-detail-value">' + displayCarrier + '</div></div>' +
                shippingHTML +
                '<div class="order-detail"><div class="order-detail-label">' + (t('contactEmail') || 'Contact Email') + '</div>' +
                '<div class="order-detail-value" style="word-break: break-all; font-size: 0.875rem;">' + data.email + '</div></div>' +
                '</div>' +
                // Only when there is a button to hold, otherwise a closed order
                // leaves an empty container still carrying its spacing.
                (trackingButtonHTML ? '<div class="order-mobile-tracking">' + trackingButtonHTML + '</div>' : '') +
                '<div class="order-items"><h4>' + (t('orderItems') || 'Order Items') + ' (' + data.itemCount + ')</h4>' +
                itemsHTML +
                '<div class="order-totals-breakdown">' +
                '<div class="breakdown-row"><span class="breakdown-label">' + (t('subtotal') || 'Subtotal') + '</span>' +
                '<span class="breakdown-value">' + currencySymbol + parseFloat(data.subtotalPrice || data.totalPrice).toFixed(2) + '</span></div>' +
                '<div class="breakdown-row"><span class="breakdown-label">' + (t('shipping') || 'Shipping') + '</span>' +
                '<span class="breakdown-value">' + currencySymbol + parseFloat(data.totalShippingPrice || '0.00').toFixed(2) + '</span></div>' +
                (parseFloat(data.totalDiscounts || 0) > 0 ?
                    '<div class="breakdown-row discount-row"><span class="breakdown-label">' + (t('discount') || 'Discount') + '</span>' +
                    '<span class="breakdown-value">-' + currencySymbol + parseFloat(data.totalDiscounts).toFixed(2) + '</span></div>' : '') +
                ((data.taxLines && data.taxLines.length > 0) ? data.taxLines.map(function (line) {
                    return '<div class="breakdown-row"><span class="breakdown-label">' + line.title + (line.ratePercentage ? ' (' + line.ratePercentage + '%)' : '') + (data.taxesIncluded ? includedSuffix2 : '') + '</span>' +
                        '<span class="breakdown-value">' + currencySymbol + parseFloat(line.price).toFixed(2) + '</span></div>';
                }).join('') :
                    '<div class="breakdown-row"><span class="breakdown-label">' + (t('tax') || 'Tax') + (data.taxesIncluded ? includedSuffix2 : '') + '</span>' +
                    '<span class="breakdown-value">' + currencySymbol + parseFloat(data.totalTax || '0.00').toFixed(2) + '</span></div>') +
                '<div class="breakdown-row grand-total-row"><span class="breakdown-label">' + (t('grandTotal') || 'Total') + '</span>' +
                '<span class="breakdown-value order-total-value">' + currencySymbol + parseFloat(data.totalPrice).toFixed(2) + '</span></div>' +
                '</div></div>' +
                (isInvoiceAvailable ?
                    '<div class="invoice-download-section invoice-download-section--v2">' +
                    '<button type="button" onclick="downloadInvoice(\'' + data.orderNumber + '\', \'' + data.orderDate + '\', \'' + locale + '\')" class="invoice-download-btn invoice-download-btn--v2">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>' +
                    '<span>' + (t('downloadInvoice') || 'Download Invoice') + '</span></button></div>' : '') +
                '<p class="order-thankyou">' + (t('thankYou') || '') + '</p>' +
                '</div></div>';

            resultsDiv.style.display = 'block';
            resultsDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            /* PORTED FROM customer-service-nl.js: live PostNL status inside the order
               card we just rendered. NL + PostNL only. The module self-gates to NL +
               PostNL so it no-ops elsewhere, but we also guard on the market so
               DE / FR / ES / UK do zero work at runtime. Wrapped so a throw in here
               can never take the order card down. */
            try {
                if (window.SwbPnlTracking && typeof window.SwbPnlTracking.attach === 'function') {
                    window.SwbPnlTracking.attach(resultsDiv.querySelector('.order-card'), data, {
                        carrier: displayCarrier,
                        hasTracking: hasValidTracking(data.trackingNumber)
                    });
                }
            } catch (liveTrackingError) {
                console.log('Live tracking skipped:', liveTrackingError);
            }

            // Copy-to-clipboard on the order number (one tap -> clipboard + toast).
            (function () {
                var numEl = resultsDiv.querySelector('.order-number');
                if (!numEl || numEl.querySelector('.cs-copy-btn')) return;
                var orderText = (data.orderNumber != null ? String(data.orderNumber) : numEl.textContent).trim();
                var copyBtn = document.createElement('button');
                copyBtn.type = 'button';
                copyBtn.className = 'cs-copy-btn';
                copyBtn.setAttribute('aria-label', t('copy') || 'Copy');
                copyBtn.innerHTML =
                    '<svg class="cs-copy-btn__ico cs-copy-btn__ico--copy" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>' +
                    '<svg class="cs-copy-btn__ico cs-copy-btn__ico--ok" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
                    '<span class="cs-copy-btn__txt">' + (t('copy') || 'Copy') + '</span>';
                function done() {
                    copyBtn.classList.add('is-copied');
                    var txtEl = copyBtn.querySelector('.cs-copy-btn__txt');
                    if (txtEl) txtEl.textContent = t('copied') || 'Copied';
                    if (window.csToast) window.csToast(t('copied') || 'Copied', 'success');
                    setTimeout(function () {
                        copyBtn.classList.remove('is-copied');
                        if (txtEl) txtEl.textContent = t('copy') || 'Copy';
                    }, 1800);
                }
                function fallbackCopy(text) {
                    try {
                        var ta = document.createElement('textarea');
                        ta.value = text; ta.setAttribute('readonly', '');
                        ta.style.position = 'absolute'; ta.style.left = '-9999px';
                        document.body.appendChild(ta); ta.select();
                        document.execCommand('copy'); document.body.removeChild(ta); done();
                    } catch (e) {}
                }
                copyBtn.addEventListener('click', function () {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(orderText).then(done).catch(function () { fallbackCopy(orderText); });
                    } else { fallbackCopy(orderText); }
                });
                numEl.appendChild(copyBtn);
            })();

            resetBtn(findLabel);
        }).catch(function () {
            resultsDiv.innerHTML =
                '<div class="order-error">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>' +
                '<p>' + (t('connectionError') || 'Connection error') + '</p>' +
                (CS_CONFIG.chatEnabled ? '<button type="button" class="cs-search-empty__chat" onclick="if(window.csOpenChat)window.csOpenChat();">' + (t('chatButtonLabel') || 'Chat with us') + '</button>' : '') +
                '</div>';
            resultsDiv.style.display = 'block';
            resetBtn(findLabel);
        });
    };

    // ── Order status timeline ─ NEUTRALIZED ───────────────────────
    // The legacy NL-only MutationObserver that injected .cs-order-timeline has
    // been removed. The status stepper is now rendered in-render (.cs-stepper)
    // inside window.lookupOrder for all markets, so no observer is needed.

    // ── Delivery ESTIMATOR (ported from inline Liquid IIFE) ────────────────
    (function () {
        var daySelectEl = document.getElementById('est-day-orders');
        var resultsEl = document.getElementById('est-results-orders');
        var btnNL = document.getElementById('btn-NL-orders');
        var btnBE = document.getElementById('btn-BE-orders');
        if (!daySelectEl || !resultsEl) return; // only initialize when present

        var currentEstCountry = 'NL';
        var daysNL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
        var monthsNL = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

        window.setEstimatorCountryOrders = function (countryCode) {
            currentEstCountry = countryCode;
            if (btnNL) btnNL.classList.toggle('active', countryCode === 'NL');
            if (btnBE) btnBE.classList.toggle('active', countryCode === 'BE');
            window.updateEstimatorOrders();
        };

        function getNextDateForDay(dayName) {
            var cleanDayName = dayName.split(' ')[0];
            var today = new Date();
            var currentDayIdx = today.getDay();
            var targetDayIdx = daysNL.indexOf(cleanDayName);
            var daysToAdd = targetDayIdx - currentDayIdx;
            if (daysToAdd < 0) daysToAdd += 7;
            var orderDate = new Date(today);
            orderDate.setDate(today.getDate() + daysToAdd);
            return orderDate;
        }

        function formatExactDate(date) {
            return daysNL[date.getDay()].toLowerCase() + ' ' + date.getDate() + ' ' + monthsNL[date.getMonth()];
        }

        function addWorkDays(startDate, days) {
            var date = new Date(startDate);
            var added = 0;
            while (added < days) {
                date.setDate(date.getDate() + 1);
                if (date.getDay() !== 0 && date.getDay() !== 6) added++;
            }
            return date;
        }

        window.updateEstimatorOrders = function () {
            var daySelect = document.getElementById('est-day-orders');
            var resultsContainer = document.getElementById('est-results-orders');
            if (!daySelect || !resultsContainer) return;

            var orderDate = getNextDateForDay(daySelect.value);
            var cleanDayName = daySelect.value.split(' ')[0];

            if (currentEstCountry === 'NL') {
                var offsetsNL = { 'Maandag': 1, 'Dinsdag': 1, 'Woensdag': 1, 'Donderdag': 1, 'Vrijdag': 1, 'Zaterdag': 3, 'Zondag': 2 };
                var nlDate = new Date(orderDate);
                nlDate.setDate(orderDate.getDate() + offsetsNL[cleanDayName]);
                resultsContainer.innerHTML =
                    '<div class="del-est__card">' +
                    '<div class="del-est__card-top">' +
                    '<span class="del-est__carrier">Standaard Bezorging (PostNL)</span>' +
                    '<span class="del-est__price del-est__price--free">GRATIS</span>' +
                    '</div>' +
                    '<div class="del-est__card-bottom">' +
                    '<span class="del-est__arrival-label">Bezorgd op:</span>' +
                    '<span class="del-est__date">' + formatExactDate(nlDate) + '</span>' +
                    '</div></div>';
            } else {
                var offsetsBE_Premium = { 'Maandag': 1, 'Dinsdag': 1, 'Woensdag': 1, 'Donderdag': 1, 'Vrijdag': 3, 'Zaterdag': 3, 'Zondag': 2 };
                var shipOffsets = { 'Maandag': 0, 'Dinsdag': 0, 'Woensdag': 0, 'Donderdag': 0, 'Vrijdag': 0, 'Zaterdag': 2, 'Zondag': 1 };
                var bePremiumDate = new Date(orderDate);
                bePremiumDate.setDate(orderDate.getDate() + offsetsBE_Premium[cleanDayName]);
                var shipDate = new Date(orderDate);
                shipDate.setDate(orderDate.getDate() + shipOffsets[cleanDayName]);
                var beMin = addWorkDays(shipDate, 2);
                var beMax = addWorkDays(shipDate, 3);
                resultsContainer.innerHTML =
                    '<div class="del-est__card">' +
                    '<div class="del-est__card-top">' +
                    '<span class="del-est__carrier">Bpost Premium+ (1 werkdag)</span>' +
                    '<span class="del-est__price">€ 1,99</span>' +
                    '</div>' +
                    '<div class="del-est__card-bottom">' +
                    '<span class="del-est__arrival-label">Bezorgd op:</span>' +
                    '<span class="del-est__date">' + formatExactDate(bePremiumDate) + '</span>' +
                    '</div></div>' +
                    '<div class="del-est__card">' +
                    '<div class="del-est__card-top">' +
                    '<span class="del-est__carrier">Bpost standaard (2-3 werkdagen)</span>' +
                    '<span class="del-est__price del-est__price--free">GRATIS</span>' +
                    '</div>' +
                    '<div class="del-est__card-bottom">' +
                    '<span class="del-est__arrival-label">Verwacht tussen:</span>' +
                    '<span class="del-est__date">' + formatExactDate(beMin) + ' en ' + formatExactDate(beMax) + '</span>' +
                    '</div></div>';
            }
        };

        var todayIdx = new Date().getDay();
        for (var i = 0; i < daySelectEl.options.length; i++) {
            if (daySelectEl.options[i].value.indexOf(daysNL[todayIdx]) === 0) {
                daySelectEl.selectedIndex = i;
                break;
            }
        }
        window.updateEstimatorOrders();
        setTimeout(function () {
            if (resultsEl && resultsEl.innerHTML.trim() === '') window.updateEstimatorOrders();
        }, 150);
    })();

    // ── FAQ accordion + contact form + Dixa (main DOMContentLoaded) ────────
    function mainInit() {
        if (!document.querySelector('.cs-section')) return;

        var faqItems = document.querySelectorAll('.faq-item');
        var contactForm = document.getElementById('contactForm');

        // Single-open accordion.
        faqItems.forEach(function (item) {
            var question = item.querySelector('.faq-question');
            if (!question) return;
            question.addEventListener('click', function (e) {
                e.preventDefault();
                var isActive = item.classList.contains('active');
                faqItems.forEach(function (faq) {
                    if (faq !== item && faq.classList.contains('active')) {
                        faq.classList.remove('active');
                        animateFaqClose(faq.querySelector('.faq-answer'));
                    }
                });
                if (!isActive) {
                    item.classList.add('active');
                    animateFaqOpen(item.querySelector('.faq-answer'));
                } else {
                    item.classList.remove('active');
                    animateFaqClose(item.querySelector('.faq-answer'));
                }
            });
        });

        // Return cost box tap ripple (mobile).
        document.querySelectorAll('.return-cost-box').forEach(function (box) {
            box.addEventListener('touchstart', function () {
                box.classList.remove('cs-rippling');
                void box.offsetWidth;
                box.classList.add('cs-rippling');
                box.addEventListener('animationend', function () {
                    box.classList.remove('cs-rippling');
                }, { once: true });
            }, { passive: true });
        });

        // Custom subject visibility.
        var subjectDropdown = document.getElementById('subject');
        var customSubjectGroup = document.getElementById('customSubjectGroup');
        var customSubjectInput = document.getElementById('customSubject');
        var subjectTrigger = CS_CONFIG.contact.customSubjectTrigger || 'Anders';

        function updateCustomSubjectVisibility() {
            if (subjectDropdown && customSubjectGroup && customSubjectInput) {
                if (subjectDropdown.value === subjectTrigger) {
                    customSubjectGroup.hidden = false;
                    customSubjectGroup.style.display = 'block';
                    customSubjectInput.setAttribute('required', 'required');
                } else {
                    customSubjectGroup.hidden = true;
                    customSubjectGroup.style.display = 'none';
                    customSubjectInput.removeAttribute('required');
                    customSubjectInput.value = '';
                }
            }
        }
        if (subjectDropdown && customSubjectGroup && customSubjectInput) {
            updateCustomSubjectVisibility();
            subjectDropdown.addEventListener('change', updateCustomSubjectVisibility);
        }

        // File upload UI.
        var fileInput = document.getElementById('attachments');
        var fileList = document.getElementById('file-list');
        if (fileInput && fileList) {
            // Strings for the attachment list, by the page's language. Kept
            // local to this function on purpose, see the IIFE note above.
            var FILE_STR = {
                nl: { one: 'bestand', many: 'bestanden', remove: 'Verwijderen', limit: 'Je kunt maximaal 3 foto\u2019s meesturen. Alleen de eerste 3 worden verzonden.' },
                de: { one: 'Datei', many: 'Dateien', remove: 'Entfernen', limit: 'Du kannst maximal 3 Fotos mitsenden. Nur die ersten 3 werden gesendet.' },
                fr: { one: 'fichier', many: 'fichiers', remove: 'Supprimer', limit: 'Vous pouvez joindre 3 photos maximum. Seules les 3 premi\u00e8res seront envoy\u00e9es.' },
                es: { one: 'archivo', many: 'archivos', remove: 'Eliminar', limit: 'Puedes adjuntar 3 fotos como m\u00e1ximo. Solo se enviar\u00e1n las 3 primeras.' },
                en: { one: 'file', many: 'files', remove: 'Remove', limit: 'You can attach up to 3 photos. Only the first 3 will be sent.' }
            };
            var fileLang = String((window.CS_CONFIG && window.CS_CONFIG.locale) || 'en').slice(0, 2).toLowerCase();
            var FS = FILE_STR[fileLang] || FILE_STR.en;
            fileInput.addEventListener('change', function () {
                var files = Array.prototype.slice.call(this.files, 0, 3);
                if (files.length === 0) { fileList.innerHTML = ''; fileList.style.display = 'none'; return; }
                var countLabel = files.length + ' ' + (files.length === 1 ? FS.one : FS.many);
                var html = '<div style="font-weight:700;margin-bottom:0.375rem;color:#1F2937;">' + countLabel + '</div>';
                files.forEach(function (f, idx) {
                    var mb = (f.size / 1048576).toFixed(1);
                    html += '<div class="cs-attachment-row" style="display:flex;align-items:center;gap:0.5rem;padding:0.2rem 0;width:100%;min-width:0;box-sizing:border-box;">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:14px;height:14px;flex-shrink:0;color:var(--primary-orange);"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' +
                        '<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + f.name + '</span>' +
                        '<span style="color:#6B7280;font-size:0.75rem;flex-shrink:0;white-space:nowrap;">' + mb + ' MB</span>' +
                        '<button type="button" class="cs-attachment-remove" data-idx="' + idx + '" aria-label="' + FS.remove + '" title="' + FS.remove + '">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:14px;height:14px;"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
                        '</button></div>';
                });
                if (this.files.length > 3) {
                    html += '<div style="color:#b45309;margin-top:0.375rem;font-style:italic;">' + FS.limit + '</div>';
                }
                fileList.innerHTML = html;
                fileList.style.display = 'block';
            });

            fileList.addEventListener('click', function (e) {
                var btn = e.target.closest('.cs-attachment-remove');
                if (!btn) return;
                e.preventDefault();
                var idx = parseInt(btn.getAttribute('data-idx'), 10);
                if (isNaN(idx) || !fileInput.files) return;
                var dt = new DataTransfer();
                Array.prototype.forEach.call(fileInput.files, function (f, i) {
                    if (i !== idx) dt.items.add(f);
                });
                fileInput.files = dt.files;
                fileInput.dispatchEvent(new Event('change'));
            });

            var dropZone = document.querySelector('.cs-file-drop');
            if (dropZone) {
                ['dragenter', 'dragover'].forEach(function (ev) {
                    dropZone.addEventListener(ev, function (e) {
                        e.preventDefault(); e.stopPropagation();
                        dropZone.classList.add('is-drag');
                    });
                });
                ['dragleave', 'dragend'].forEach(function (ev) {
                    dropZone.addEventListener(ev, function (e) {
                        e.preventDefault(); e.stopPropagation();
                        dropZone.classList.remove('is-drag');
                    });
                });
                dropZone.addEventListener('drop', function (e) {
                    e.preventDefault(); e.stopPropagation();
                    dropZone.classList.remove('is-drag');
                    var dt = e.dataTransfer;
                    if (!dt || !dt.files || !dt.files.length) return;
                    try {
                        var out = new DataTransfer();
                        var added = 0;
                        for (var i = 0; i < dt.files.length && added < 3; i++) {
                            if (/^image\//i.test(dt.files[i].type)) { out.items.add(dt.files[i]); added++; }
                        }
                        if (out.files.length) {
                            fileInput.files = out.files;
                            fileInput.dispatchEvent(new Event('change'));
                        }
                    } catch (err) {
                        fileInput.files = dt.files;
                        fileInput.dispatchEvent(new Event('change'));
                    }
                });
            }
        }

        // Contact form submit (webhook OR mailto, per CS_CONFIG.contact.mode).
        if (contactForm) {
            contactForm.addEventListener('submit', function (e) {
                e.preventDefault();
                if (!contactForm.checkValidity()) { contactForm.reportValidity(); return; }

                var subjectVal = (document.getElementById('subject') || {}).value || '';
                var customSubjectVal = (document.getElementById('customSubject') || {}).value || '';
                var subject = (subjectVal === subjectTrigger && customSubjectVal) ? customSubjectVal : subjectVal;

                var submitBtn = contactForm.querySelector('.cs-submit-btn');
                var statusDiv = document.getElementById('contactStatus');
                var originalText = submitBtn ? submitBtn.textContent : '';

                var mode = CS_CONFIG.contact.mode || 'webhook';

                if (mode === 'mailto') {
                    var name = (document.getElementById('name') || {}).value || '';
                    var email = (document.getElementById('email') || {}).value || '';
                    var order = (document.getElementById('order') || {}).value || '';
                    var message = (document.getElementById('message') || {}).value || '';
                    var target = CS_CONFIG.contact.mailtoTarget || '';
                    var emailSubject = 'Customer Service: ' + subject;
                    var emailBody = 'Name: ' + name + '\n' +
                        'Email: ' + email + '\n' +
                        'Order Number: ' + order + '\n' +
                        'Subject: ' + subject + '\n\n' +
                        'Message:\n' + message + '\n\n' +
                        '---\nThis message was sent from the customer service portal.';
                    var mailtoLink = 'mailto:' + target + '?subject=' + encodeURIComponent(emailSubject) + '&body=' + encodeURIComponent(emailBody);
                    if (submitBtn) submitBtn.textContent = t('contactSending') || 'Opening Email...';
                    // Show a clear confirmation + direct-email + chat fallback so the
                    // user is never left wondering whether anything happened (e.g.
                    // when no mail client is configured).
                    if (statusDiv) {
                        var emailLink = target
                            ? '<a href="mailto:' + target + '" style="color:#047857;text-decoration:underline;">' + target + '</a>'
                            : target;
                        var openedMsg = (t('contactMailtoOpened') || "We've opened your email app with your message ready - just press Send. No email app? Email us directly at {email}, or use the chat button below.").replace('{email}', emailLink);
                        statusDiv.innerHTML = '<div class="cs-status-anim" style="display:flex;gap:0.875rem;align-items:flex-start;padding:1.25rem;background:linear-gradient(135deg,#ECFDF5 0%,#D1FAE5 100%);border:1px solid #10B981;border-radius:12px;">' +
                            '<div style="background:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 4px rgba(5,150,105,0.1);">' +
                            '<svg viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2.5" style="width:16px;height:16px;"><polyline points="20 6 9 17 4 12"></polyline></svg></div>' +
                            '<div><p style="margin:0;color:#047857;font-size:0.9375rem;font-weight:600;line-height:1.5;">' + openedMsg + '</p>' +
                            (CS_CONFIG.chatEnabled ? '<button type="button" class="cs-search-empty__chat" onclick="if(window.csOpenChat)window.csOpenChat();">' + (t('chatButtonLabel') || 'Chat with us') + '</button>' : '') + '</div></div>';
                        statusDiv.style.display = 'block';
                    }
                    window.location.href = mailtoLink;
                    setTimeout(function () {
                        contactForm.reset();
                        if (submitBtn) submitBtn.textContent = originalText;
                        updateCustomSubjectVisibility();
                    }, 2000);
                    return;
                }

                // Webhook mode (multipart FormData).
                var formData = new FormData(contactForm);
                if (submitBtn) {
                    submitBtn.textContent = t('contactSending') || 'Sending...';
                    submitBtn.disabled = true;
                }
                if (statusDiv) statusDiv.style.display = 'none';

                formData.set('subject', subject);
                formData.append('customSubject', customSubjectVal);
                formData.append('source', CS_CONFIG.contact.source || '');
                formData.append('market', CS_CONFIG.market || '');
                formData.append('country', CS_CONFIG.country || '');

                var fileInputNode = document.getElementById('attachments');
                if (fileInputNode && fileInputNode.files.length > 0) {
                    formData.delete('attachments');
                    formData.delete('attachments[]');
                    formData.delete('attachment');

                    var MAX_FILES = 3;
                    var MAX_SIZE_MB = 5;
                    var MAX_TOTAL_MB = 15;
                    var sendFiles = Array.prototype.slice.call(fileInputNode.files, 0, MAX_FILES);
                    var total = 0;
                    var tooBig = null;
                    for (var i = 0; i < sendFiles.length; i++) {
                        total += sendFiles[i].size;
                        if (sendFiles[i].size > MAX_SIZE_MB * 1024 * 1024) { tooBig = sendFiles[i]; break; }
                    }
                    if (tooBig) {
                        if (submitBtn) { submitBtn.textContent = originalText; submitBtn.disabled = false; }
                        if (statusDiv) {
                            statusDiv.innerHTML = '<div class="cs-status-anim" style="padding:1rem 1.25rem;background:linear-gradient(135deg,#FEE2E2 0%,#FECACA 100%);border:2px solid rgba(220,38,38,0.3);color:#991B1B;border-radius:12px;font-weight:600;font-size:0.9rem;">' + (t('fileTooLarge') || 'Image too large (max. 5 MB):') + ' ' + tooBig.name + '</div>';
                            statusDiv.style.display = 'block';
                        }
                        return;
                    }
                    if (total > MAX_TOTAL_MB * 1024 * 1024) {
                        var totalMb = (total / 1048576).toFixed(1);
                        if (submitBtn) { submitBtn.textContent = originalText; submitBtn.disabled = false; }
                        if (statusDiv) {
                            statusDiv.innerHTML = '<div class="cs-status-anim" style="padding:1rem 1.25rem;background:linear-gradient(135deg,#FEE2E2 0%,#FECACA 100%);border:2px solid rgba(220,38,38,0.3);color:#991B1B;border-radius:12px;font-weight:600;font-size:0.9rem;">' + (t('filesTooLarge') || 'Images too large together') + ' (' + totalMb + ' MB).</div>';
                            statusDiv.style.display = 'block';
                        }
                        return;
                    }
                    sendFiles.forEach(function (file, index) {
                        formData.append('attachment_' + index, file);
                    });
                }

                var webhookUrl = CS_CONFIG.contact.webhookUrl || '';
                fetch(webhookUrl, { method: 'POST', body: formData })
                    .then(function (response) {
                        if (!response.ok) throw new Error('Server response was not ok.');
                        if (statusDiv) {
                            statusDiv.innerHTML = '<div class="cs-status-anim" style="display: flex; gap: 0.875rem; align-items: flex-start; padding: 1.25rem; background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%); border: 1px solid #10B981; border-radius: 12px;">' +
                                '<div style="background: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(5, 150, 105, 0.1);">' +
                                '<svg viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2.5" style="width: 16px; height: 16px;"><polyline points="20 6 9 17 4 12"></polyline></svg></div>' +
                                '<div><p style="margin: 0; color: #047857; font-size: 0.9375rem; font-weight: 600; line-height: 1.5;">' + (t('contactSuccess') || 'Message sent! We will get back to you as soon as possible.') + '</p></div></div>';
                            statusDiv.style.display = 'block';
                        }
                        contactForm.reset();
                        updateCustomSubjectVisibility();
                        var fl = document.getElementById('file-list');
                        if (fl) fl.innerHTML = '';
                    })
                    .catch(function () {
                        if (statusDiv) {
                            statusDiv.innerHTML = '<div class="cs-status-anim" style="display: flex; gap: 0.875rem; align-items: flex-start; padding: 1.25rem; background: linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%); border: 1px solid #EF4444; border-radius: 12px;">' +
                                '<div style="background: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(185, 28, 28, 0.1);">' +
                                '<svg viewBox="0 0 24 24" fill="none" stroke="#B91C1C" stroke-width="2.5" style="width: 16px; height: 16px;"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></div>' +
                                '<div><p style="margin: 0; color: #7F1D1D; font-size: 0.9375rem; font-weight: 600; line-height: 1.5;">' + (t('contactError') || 'Something went wrong. Please try again later.') + '</p></div></div>';
                            statusDiv.style.display = 'block';
                        }
                    })
                    .then(function () {
                        if (submitBtn) { submitBtn.textContent = originalText; submitBtn.disabled = false; }
                    });
            });
        }

        // Dixa open trigger (no init, no token).
        var dixaButton = document.getElementById('csDixaButton');
        if (dixaButton) {
            dixaButton.addEventListener('click', function (e) {
                e.preventDefault();
                if (window.csOpenChat) window.csOpenChat();
            });
        }

        // Order tooltip toggle (#orderTooltip <-> .is-visible).
        document.addEventListener('click', function (e) {
            var tooltip = document.getElementById('orderTooltip');
            if (!tooltip) return;
            var infoBtn = e.target.closest('.lookup-info-btn');
            if (infoBtn) { tooltip.classList.toggle('is-visible'); return; }
            if (tooltip.classList.contains('is-visible') && !tooltip.contains(e.target)) {
                tooltip.classList.remove('is-visible');
            }
        });

        // ── URL prefill: auto-fill & submit lookup from query params ──
        (function () {
            var urlParams = new URLSearchParams(window.location.search);
            var urlOrderNumber = urlParams.get('orderNumber');
            var urlOrderEmail = urlParams.get('orderEmail');
            if (urlOrderNumber && urlOrderEmail) {
                var numInput = document.getElementById('orderNumber');
                var emailInput = document.getElementById('orderEmail');
                if (numInput && emailInput) {
                    numInput.value = urlOrderNumber;
                    emailInput.value = urlOrderEmail;
                    setTimeout(function () {
                        if (typeof window.handleOrderLookup === 'function') window.handleOrderLookup();
                        else if (typeof window.lookupOrder === 'function') window.lookupOrder();
                    }, 600);
                }
            }
        })();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mainInit);
    } else {
        mainInit();
    }

    // ── Deep-links: #faq-x, #orders.faq-x (dot), #orders#faq-x ─────────────
    (function () {
        function extractFaqId(hash) {
            if (!hash) return null;
            var raw = hash;
            var dotIdx = raw.indexOf('.');
            if (dotIdx !== -1) raw = raw.substring(dotIdx + 1);
            var hashIdx = raw.indexOf('#');
            if (hashIdx !== -1) raw = raw.substring(hashIdx + 1);
            if (raw.indexOf('faq-') === 0) return raw;
            return null;
        }

        function openFAQ(faqId, retries) {
            retries = retries || 0;
            var faqItem = document.getElementById(faqId);
            if (!faqItem) {
                if (retries < 15) setTimeout(function () { openFAQ(faqId, retries + 1); }, 200);
                return;
            }
            document.querySelectorAll('.faq-item.active').forEach(function (item) {
                item.classList.remove('active');
                animateFaqClose(item.querySelector('.faq-answer'));
            });
            faqItem.classList.add('active');
            animateFaqOpen(faqItem.querySelector('.faq-answer'));
            requestAnimationFrame(function () {
                setTimeout(function () {
                    faqItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 50);
            });
        }

        function handleHash(hash) {
            var faqId = extractFaqId(hash);
            if (faqId) {
                openFAQ(faqId, 0);
                return true;
            }
            // Bare #category hashes are a no-op now (each category is its own page).
            return false;
        }

        function init() {
            var hash = window.location.hash.substring(1);
            if (hash) handleHash(hash);
        }

        window.addEventListener('hashchange', function () {
            handleHash(window.location.hash.substring(1));
        });
        window.addEventListener('popstate', function () {
            handleHash(window.location.hash.substring(1));
        });
        window.addEventListener('load', function () {
            if (window.location.hash) handleHash(window.location.hash.substring(1));
        });

        // Intercept in-page hash links (capture phase).
        document.addEventListener('click', function (e) {
            var link = e.target.closest('a[href^="#"]');
            if (!link) return;
            var href = link.getAttribute('href');
            var hash = href.substring(1);
            var faqId = extractFaqId(hash);
            if (faqId) {
                e.preventDefault();
                e.stopPropagation();
                if (window.history && window.history.pushState) {
                    window.history.pushState({}, '', '#' + hash);
                }
                handleHash(hash);
            }
        }, true);

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    })();

    // ── Smart FAQ search — live suggestions ACROSS all category pages (current market) ─
    (function () {
        function escapeHtml(s) {
            return String(s).replace(/[&<>"']/g, function (c) {
                return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
            });
        }

        function highlight(text, q) {
            var idx = text.toLowerCase().indexOf(q);
            if (idx === -1) return escapeHtml(text);
            return escapeHtml(text.slice(0, idx)) + '<mark>' + escapeHtml(text.slice(idx, idx + q.length)) + '</mark>' + escapeHtml(text.slice(idx + q.length));
        }

        function openFaq(item) {
            if (!item) return;
            document.querySelectorAll('.faq-item.active').forEach(function (other) {
                if (other !== item) {
                    other.classList.remove('active');
                    if (window.animateFaqClose) window.animateFaqClose(other.querySelector('.faq-answer'));
                }
            });
            if (!item.classList.contains('active')) {
                item.classList.add('active');
                if (window.animateFaqOpen) window.animateFaqOpen(item.querySelector('.faq-answer'));
            }
            requestAnimationFrame(function () {
                setTimeout(function () { item.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 60);
            });
        }

        var previewMatch = (location.search || '').match(/[?&]preview_theme_id=(\d+)/);
        var previewId = previewMatch ? previewMatch[1] : '';
        function withPreview(url) {
            if (!previewId) return url;
            return url + (url.indexOf('?') === -1 ? '?' : '&') + 'preview_theme_id=' + previewId;
        }
        function anchorOf(id) { return (id && id.indexOf('faq-') === 0) ? id.slice(4) : (id || ''); }

        function collectNavTargets() {
            var map = {};
            document.querySelectorAll('.cs-nav-item[data-section], .cs-tab-item[data-section]').forEach(function (a) {
                var cat = a.getAttribute('data-section');
                var href = a.getAttribute('href');
                if (!cat || !href || map[cat]) return;
                var labelEl = a.querySelector('.cs-nav-item__label, .cs-tab-item__label');
                map[cat] = { category: cat, url: href, label: labelEl ? labelEl.textContent.trim() : cat };
            });
            return map;
        }

        function parseFaqs(root) {
            var out = [];
            root.querySelectorAll('.faq-item').forEach(function (it) {
                var qEl = it.querySelector('.faq-question span');
                var aEl = it.querySelector('.faq-answer');
                out.push({
                    question: qEl ? qEl.textContent.trim() : '',
                    qLower: (qEl ? qEl.textContent : '').toLowerCase(),
                    aLower: (aEl ? aEl.textContent : '').toLowerCase(),
                    anchor: anchorOf(it.id)
                });
            });
            return out;
        }

        function fetchFaqs(url) {
            var cacheKey = 'cs_faq_' + url;
            try { var cached = sessionStorage.getItem(cacheKey); if (cached) return Promise.resolve(JSON.parse(cached)); } catch (e) {}
            return fetch(withPreview(url), { credentials: 'same-origin' })
                .then(function (r) { return r.ok ? r.text() : ''; })
                .then(function (html) {
                    if (!html) return [];
                    var doc = new DOMParser().parseFromString(html, 'text/html');
                    var faqs = parseFaqs(doc);
                    try { sessionStorage.setItem(cacheKey, JSON.stringify(faqs)); } catch (e) {}
                    return faqs;
                }).catch(function () { return []; });
        }

        function init() {
            var inputs = document.querySelectorAll('.cs-search-input');
            if (!inputs.length) return;

            var currentCategory = (window.CS_CONFIG && CS_CONFIG.category) || '';
            var currentPath = location.pathname;
            var navTargets = collectNavTargets();
            var allEntries = [];

            // Current page FAQs (live DOM references — open instantly).
            var curLabel = (navTargets[currentCategory] && navTargets[currentCategory].label) || currentCategory;
            Array.prototype.slice.call(document.querySelectorAll('.faq-item')).forEach(function (item) {
                var qEl = item.querySelector('.faq-question span');
                var aEl = item.querySelector('.faq-answer');
                allEntries.push({
                    question: qEl ? qEl.textContent.trim() : '',
                    qLower: (qEl ? qEl.textContent : '').toLowerCase(),
                    aLower: (aEl ? aEl.textContent : '').toLowerCase(),
                    category: currentCategory, label: curLabel, url: currentPath, anchor: anchorOf(item.id), item: item
                });
            });

            // Other category pages (fetched once, cached per session, deep-linked on click).
            Object.keys(navTargets).forEach(function (cat) {
                if (cat === currentCategory) return;
                var tgt = navTargets[cat];
                fetchFaqs(tgt.url).then(function (faqs) {
                    faqs.forEach(function (f) {
                        allEntries.push({
                            question: f.question, qLower: f.qLower, aLower: f.aLower,
                            category: cat, label: tgt.label, url: tgt.url, anchor: f.anchor, item: null
                        });
                    });
                });
            });

            inputs.forEach(function (input) {
                var container = input.closest('.cs-search-container');
                if (!container) return;
                var results = container.querySelector('.cs-search-results');
                var clearBtn = container.querySelector('.cs-search-clear');
                var timer, activeIndex = -1, current = [];

                // Log searches that return nothing (deduped, >=3 chars) to the BWJ
                // data dashboard so "what customers search but can't find" sits next
                // to the helpful scores. Same token + sendBeacon (text/plain) pattern.
                var missTimer, loggedMisses = {};
                function missLs(k, v) {
                    try { return arguments.length > 1 ? window.localStorage.setItem(k, v) : window.localStorage.getItem(k); }
                    catch (e) { return null; }
                }
                function missUuid() {
                    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
                    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                        var r = Math.floor(Math.random() * 16);
                        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                    });
                }
                function logSearchMiss(q) {
                    q = (q || '').trim();
                    if (q.length < 3 || loggedMisses[q]) return;
                    var url = CS_CONFIG.searchMissUrl;
                    if (!url) return;
                    if (missTimer) clearTimeout(missTimer);
                    missTimer = setTimeout(function () {
                        loggedMisses[q] = true;
                        var vid = missLs('cs_visitor_id');
                        if (!vid) { vid = missUuid(); missLs('cs_visitor_id', vid); }
                        var payload = {
                            token: CS_CONFIG.helpfulToken || '',
                            query: q,
                            store: CS_CONFIG.brand || 'bwj',
                            market: CS_CONFIG.country ? String(CS_CONFIG.country).toUpperCase() : undefined,
                            locale: String(CS_CONFIG.locale || '').split('-')[0].toLowerCase() || undefined,
                            category: CS_CONFIG.category,
                            path: window.location.pathname,
                            session_id: vid,
                            event_id: missUuid()
                        };
                        var body = JSON.stringify(payload);
                        try {
                            if (navigator.sendBeacon) {
                                var blob = new Blob([body], { type: 'text/plain' });
                                if (navigator.sendBeacon(url, blob)) return;
                            }
                        } catch (e) {}
                        try {
                            fetch(url, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'no-cors' }).catch(function () {});
                        } catch (e2) {}
                    }, 1200);
                }

                function hide() { if (results) { results.hidden = true; results.innerHTML = ''; results.style.maxHeight = ''; } activeIndex = -1; current = []; }

                /* Keep the suggestion list inside the space it can actually use.

                   The stylesheet caps it at min(60vh, 26rem), but on a phone the
                   keyboard is open while you are typing and vh knows nothing
                   about it. A list shorter than that cap still ran off the bottom
                   of the screen: there was no overflow inside the panel, so there
                   was nothing to scroll, and dragging moved the page instead,
                   which carried the results out of view. visualViewport reports
                   the area actually left above the keyboard, so the panel is
                   capped to the gap under the input and anything longer scrolls
                   within itself. Floored so it always stays a usable target. */
                function fitResults() {
                    if (!results || results.hidden) return;
                    var vv = window.visualViewport;
                    var vh = (vv && vv.height) || window.innerHeight || 0;
                    if (!vh) return;
                    var offsetTop = (vv && vv.offsetTop) || 0;
                    var box = input.getBoundingClientRect();
                    // 0.75rem sits between input and panel; leave the same below it.
                    var available = (offsetTop + vh) - box.bottom - 24;
                    results.style.maxHeight = Math.max(140, Math.round(available)) + 'px';
                }
                function scheduleFit() {
                    if (typeof window.requestAnimationFrame === 'function') {
                        window.requestAnimationFrame(fitResults);
                    } else {
                        window.setTimeout(fitResults, 0);
                    }
                }
                if (window.visualViewport) {
                    window.visualViewport.addEventListener('resize', fitResults);
                    window.visualViewport.addEventListener('scroll', fitResults);
                }
                window.addEventListener('resize', fitResults, { passive: true });
                window.addEventListener('scroll', fitResults, { passive: true });

                function highlightTokens(text, toks) {
                    var safe = escapeHtml(text);
                    var lower = safe.toLowerCase();
                    var marks = [];
                    toks.forEach(function (tk) {
                        var t = tk.toLowerCase(), from = 0, idx;
                        while ((idx = lower.indexOf(t, from)) !== -1) { marks.push([idx, idx + t.length]); from = idx + t.length; }
                    });
                    if (!marks.length) return safe;
                    marks.sort(function (a, b) { return a[0] - b[0]; });
                    var merged = [marks[0]];
                    for (var i = 1; i < marks.length; i++) {
                        var last = merged[merged.length - 1];
                        if (marks[i][0] <= last[1]) { last[1] = Math.max(last[1], marks[i][1]); }
                        else { merged.push(marks[i]); }
                    }
                    var res = '', pos = 0;
                    merged.forEach(function (m) { res += safe.slice(pos, m[0]) + '<mark>' + safe.slice(m[0], m[1]) + '</mark>'; pos = m[1]; });
                    res += safe.slice(pos);
                    return res;
                }
                function render(q) {
                    // Tokenized, forgiving search: split the query into words and match
                    // entries that contain the words anywhere (question or answer), in any
                    // order — so people don't need to type the exact question.
                    var tokens = q.split(/[\s,.;:!?]+/).filter(function (tk) { return tk.length >= 2; });
                    if (!tokens.length) tokens = [q];
                    function score(e, requireAll) {
                        var qHits = 0, aHits = 0;
                        for (var i = 0; i < tokens.length; i++) {
                            var inQ = e.qLower.indexOf(tokens[i]) !== -1;
                            var inA = !inQ && e.aLower.indexOf(tokens[i]) !== -1;
                            if (inQ) qHits++; else if (inA) aHits++;
                            else if (requireAll) return -1;
                        }
                        return qHits * 3 + aHits;
                    }
                    var matches = [];
                    allEntries.forEach(function (e) {
                        var sc = score(e, true);
                        if (sc >= 0) matches.push({ e: e, score: sc, exact: e.qLower.indexOf(q) !== -1 });
                    });
                    if (!matches.length) {
                        // Fallback: match ANY word (catches partial / loosely-phrased queries).
                        allEntries.forEach(function (e) {
                            var sc = score(e, false);
                            if (sc > 0) matches.push({ e: e, score: sc, exact: false });
                        });
                    }
                    matches.sort(function (a, b) { return (b.exact - a.exact) || (b.score - a.score); });
                    current = matches.slice(0, 8);
                    if (!results) return;
                    results.hidden = false;
                    scheduleFit();
                    if (!current.length) {
                        results.innerHTML = '<div class="cs-search-empty">' + escapeHtml(t('noResults') || 'No results found.') +
                            (CS_CONFIG.chatEnabled ? '<button type="button" class="cs-search-empty__chat" onclick="if(window.csOpenChat)window.csOpenChat();">' + escapeHtml(t('chatButtonLabel') || 'Chat with us') + '</button>' : '') +
                            '</div>';
                        logSearchMiss(q);
                        return;
                    }
                    results.innerHTML = current.map(function (m, i) {
                        return '<button type="button" class="cs-search-result" data-index="' + i + '">' +
                            '<span class="cs-search-result__q">' + highlightTokens(m.e.question, tokens) + '</span>' +
                            '<span class="cs-search-result__cat">' + escapeHtml(m.e.label) + '</span></button>';
                    }).join('');
                    activeIndex = -1;
                }

                function choose(i) {
                    var entry = current[i] && current[i].e;
                    if (!entry) return;
                    if (entry.item) {
                        openFaq(entry.item); hide(); input.blur();
                    } else {
                        if (window.csShowPageLoader) window.csShowPageLoader();
                        window.location.href = withPreview(entry.url) + '#faq-' + entry.anchor;
                    }
                }

                input.addEventListener('input', function () {
                    clearTimeout(timer);
                    var q = input.value.trim().toLowerCase();
                    if (clearBtn) clearBtn.hidden = q.length === 0;
                    if (q.length < 2) { hide(); return; }
                    timer = setTimeout(function () { render(q); }, 120);
                });

                input.addEventListener('keydown', function (e) {
                    if (!results || results.hidden || !current.length) return;
                    var btns = results.querySelectorAll('.cs-search-result');
                    if (e.key === 'ArrowDown') { e.preventDefault(); activeIndex = Math.min(activeIndex + 1, current.length - 1); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); activeIndex = Math.max(activeIndex - 1, 0); }
                    else if (e.key === 'Enter') { e.preventDefault(); choose(activeIndex < 0 ? 0 : activeIndex); return; }
                    else if (e.key === 'Escape') { hide(); return; }
                    else return;
                    btns.forEach(function (b, i) { b.classList.toggle('is-active', i === activeIndex); });
                    if (btns[activeIndex]) btns[activeIndex].scrollIntoView({ block: 'nearest' });
                });

                if (results) {
                    results.addEventListener('mousedown', function (e) {
                        var btn = e.target.closest('.cs-search-result');
                        if (btn) { e.preventDefault(); choose(parseInt(btn.getAttribute('data-index'), 10)); }
                    });
                }

                input.addEventListener('blur', function () { setTimeout(hide, 150); });
                input.addEventListener('focus', function () {
                    var q = input.value.trim().toLowerCase();
                    if (q.length >= 2) render(q);
                });

                if (clearBtn) {
                    clearBtn.addEventListener('click', function () {
                        input.value = '';
                        clearBtn.hidden = true;
                        hide();
                        input.focus();
                    });
                }
            });
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    })();

    // ── Delivery estimator (modern, per-market arrival window) ─────────────
    (function () {
        function init() {
            var widgets = document.querySelectorAll('.cs-estimator');
            if (!widgets.length) return;

            widgets.forEach(function (el) {
                var select = el.querySelector('.cs-estimator__select');
                var result = el.querySelector('.cs-estimator__result');
                if (!select || !result) return;

                var min = parseInt(el.getAttribute('data-min'), 10); if (isNaN(min)) min = 2;
                var max = parseInt(el.getAttribute('data-max'), 10); if (isNaN(max)) max = 4;
                var cutoff = parseInt(el.getAttribute('data-cutoff'), 10); if (isNaN(cutoff)) cutoff = 22;
                var skipMon = el.getAttribute('data-skip-monday') === '1';
                var locale = el.getAttribute('data-locale') || CS_CONFIG.locale || 'en-GB';
                var carrier = el.getAttribute('data-carrier') || '';
                var free = el.getAttribute('data-free') === '1';

                /* Optional destination branches.

                   A market's storefront serves more than one country and the
                   delivery promise differs per country, so quoting the home
                   country's dates to everyone misleads the rest. The old
                   per-market pages had exactly this: NL offered Nederland /
                   Belgie, DE offered Deutschland / Osterreich / Schweiz, FR
                   offered France / Belgique / Luxembourg.

                   One line per destination, pipe separated:
                     CODE|Label|minDays|maxDays|carrier|free
                   `free` is the literal word "free"; anything else (or empty)
                   means paid. The first line is the default. Fewer than two
                   valid lines leaves the widget exactly as it was, so markets
                   that ship to one country only are untouched. */
                var destinations = (function () {
                    var raw = el.getAttribute('data-destinations') || '';
                    var out = [];
                    raw.split('\n').forEach(function (line) {
                        var p = String(line).split('|');
                        if (p.length < 4) return;
                        var code = p[0].trim(), label = p[1].trim();
                        var lo = parseInt(p[2], 10), hi = parseInt(p[3], 10);
                        if (!code || !label || isNaN(lo) || isNaN(hi)) return;
                        out.push({
                            code: code,
                            label: label,
                            min: lo,
                            max: hi,
                            carrier: (p[4] || '').trim(),
                            free: String(p[5] || '').trim().toLowerCase() === 'free'
                        });
                    });
                    return out;
                })();

                function startOfDay(d) { var x = new Date(d.getTime()); x.setHours(12, 0, 0, 0); return x; }
                function isWeekend(d) { var w = d.getDay(); return w === 0 || w === 6; }
                function addBusinessDays(date, n) {
                    var d = new Date(date.getTime()), added = 0;
                    while (added < n) {
                        d.setDate(d.getDate() + 1);
                        var w = d.getDay();
                        if (skipMon) { if (w !== 0 && w !== 1) added++; }
                        else { if (w !== 0 && w !== 6) added++; }
                    }
                    return d;
                }
                function nextShipDay(d) { var x = new Date(d.getTime()); while (isWeekend(x)) x.setDate(x.getDate() + 1); return x; }
                function fmt(d) { try { return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return d.toLocaleDateString(); } }

                var now = new Date();
                var base = startOfDay(now);
                // Options run from a week back to a week ahead so the widget answers
                // BOTH questions: "if I order on X, when does it arrive?" and
                // "I ordered last Friday, when should it arrive?". Every option is a
                // concrete date, so there is never any last-Friday/next-Friday doubt.
                var PAST_DAYS = 7, FUTURE_DAYS = 6;
                for (var i = -PAST_DAYS; i <= FUTURE_DAYS; i++) {
                    var dt = new Date(base.getTime()); dt.setDate(base.getDate() + i);
                    var opt = document.createElement('option');
                    opt.value = String(i);
                    if (i === 0) opt.textContent = t('estimatorToday') || 'Today';
                    else if (i === 1) opt.textContent = t('estimatorTomorrow') || 'Tomorrow';
                    else if (i === -1) opt.textContent = t('estimatorYesterday') || 'Yesterday';
                    else opt.textContent = fmt(dt);
                    select.appendChild(opt);
                }
                select.value = '0'; // default to today, not the oldest option

                /* Render the destination chips above the day picker and let a
                   choice swap the delivery promise. Built in JS rather than
                   Liquid so a market with one destination renders no extra
                   markup at all. */
                if (destinations.length > 1) {
                    var row = document.createElement('div');
                    row.className = 'cs-estimator__dest';
                    row.setAttribute('role', 'group');
                    row.setAttribute('aria-label', t('estimatorDestination') || 'Delivery country');

                    destinations.forEach(function (d, idx) {
                        var b = document.createElement('button');
                        b.type = 'button';
                        b.className = 'cs-estimator__dest-btn' + (idx === 0 ? ' is-active' : '');
                        b.textContent = d.label;
                        b.setAttribute('aria-pressed', idx === 0 ? 'true' : 'false');
                        b.addEventListener('click', function () {
                            min = d.min; max = d.max; carrier = d.carrier; free = d.free;
                            var all = row.querySelectorAll('.cs-estimator__dest-btn');
                            for (var i = 0; i < all.length; i++) {
                                var on = all[i] === b;
                                all[i].classList.toggle('is-active', on);
                                all[i].setAttribute('aria-pressed', on ? 'true' : 'false');
                            }
                            compute();
                        });
                        row.appendChild(b);
                    });

                    var control = el.querySelector('.cs-estimator__control');
                    if (control && control.parentNode) control.parentNode.insertBefore(row, control);
                    else el.appendChild(row);

                    // The first destination is the default, so start from it.
                    min = destinations[0].min;
                    max = destinations[0].max;
                    carrier = destinations[0].carrier;
                    free = destinations[0].free;
                }

                function compute() {
                    var offset = parseInt(select.value, 10) || 0;
                    var order = new Date(base.getTime()); order.setDate(base.getDate() + offset);
                    var ship;
                    if (isWeekend(order)) ship = nextShipDay(order);
                    else if (offset === 0 && now.getHours() >= cutoff) ship = nextShipDay(new Date(order.getTime() + 86400000));
                    else ship = order;
                    var earliest = addBusinessDays(ship, min);
                    var latest = addBusinessDays(ship, max);
                    var dateText = (min === max || earliest.getTime() === latest.getTime()) ? fmt(earliest) : (fmt(earliest) + ' ' + (({nl:'tot',de:'bis',fr:'à',es:'a'})[(CS_CONFIG.market||'').toLowerCase()] || 'to') + ' ' + fmt(latest));
                    var meta = [];
                    if (carrier) meta.push(carrier);
                    if (free) meta.push(t('estimatorFreeShipping') || 'Free shipping');
                    result.innerHTML =
                        '<div class="cs-estimator__date">' +
                        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>' +
                        '<span class="cs-estimator__date-body"><span class="cs-estimator__date-label">' + (t('estimatorArrives') || 'Estimated delivery') + '</span>' +
                        '<strong class="cs-estimator__date-value">' + dateText + '</strong></span></div>' +
                        (meta.length ? '<div class="cs-estimator__meta">' + meta.map(function (m) { return '<span>' + m + '</span>'; }).join('') + '</div>' : '');
                }

                select.addEventListener('change', compute);
                compute();
            });
        }
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
        else init();
    })();

    // ── Page-transition loader (cut-scene between CS pages) ─────────────────
    (function () {
        var loader = document.getElementById('csPageLoader');
        var csRoot = document.querySelector('.cs');
        // Also flag the page root so the outgoing content can settle back while
        // the next page loads (see .cs.is-navigating). Deliberately NOT a
        // full-screen blur overlay -- that read as a heavy cut-scene.
        window.csShowPageLoader = function () {
            if (loader) loader.classList.add('is-active');
            if (csRoot) csRoot.classList.add('is-navigating');
        };
        window.csHidePageLoader = function () {
            if (loader) loader.classList.remove('is-active');
            if (csRoot) csRoot.classList.remove('is-navigating');
        };
        if (!loader) return;

        document.addEventListener('click', function (e) {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            var link = e.target.closest('.cs-nav-item, .cs-tab-item');
            if (!link) return;
            var href = link.getAttribute('href');
            if (!href || href.charAt(0) === '#') return;
            if (link.target && link.target !== '_self') return;
            if (link.classList.contains('active')) return; // same page
            window.csShowPageLoader();
        }, true);

        // Hide on arrival / back-forward cache restore.
        window.addEventListener('pageshow', function () { window.csHidePageLoader(); });
    })();

    // ── Interactive timeline / aux-timeline reveal observer ────────────────
    (function () {
        function init() {
            // `.aux-timeline-item` is the legacy markup; `.cs-timeline__item` is
            // the current about-us timeline, which had no reveal at all.
            var items = document.querySelectorAll('.aux-timeline-item, .cs-timeline__item');
            if (!items.length) return;

            // No IntersectionObserver (or reduced motion): show everything at
            // once so the content is never left invisible.
            var reduced = false;
            try {
                reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            } catch (e) {}
            if (!('IntersectionObserver' in window) || reduced) {
                items.forEach(function (item) { item.classList.add('is-visible'); });
                return;
            }

            var observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (!entry.isIntersecting) return;
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target); // reveal once, then stop
                });
            }, { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
            items.forEach(function (item) { observer.observe(item); });

            // Stagger each row so the line reads as drawing downwards, and only
            // NOW opt the timeline into the hidden start state (the CSS keys off
            // .cs-reveal-ready). Doing it here means a JS failure, a blocked
            // script or an old browser can never leave the copy invisible.
            document.querySelectorAll('.cs-timeline').forEach(function (tl) {
                var rows = tl.querySelectorAll('.cs-timeline__item');
                for (var i = 0; i < rows.length; i++) {
                    rows[i].style.setProperty('--cs-tl-delay', (i * 90) + 'ms');
                }
                tl.classList.add('cs-reveal-ready');

                // Failsafe: if the timeline is on screen but the observer has
                // not revealed anything shortly after init, assume it is not
                // firing (background/prerendered tab, exotic browser) and just
                // show the rows. Only triggers when in view, so a timeline far
                // below the fold keeps its scroll animation.
                window.setTimeout(function () {
                    var r = tl.getBoundingClientRect();
                    var inView = r.top < (window.innerHeight || 0) && r.bottom > 0;
                    if (inView && !tl.querySelector('.cs-timeline__item.is-visible')) {
                        for (var j = 0; j < rows.length; j++) rows[j].classList.add('is-visible');
                    }
                }, 1500);
            });
        }
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    })();

    // ── Live support hours badge ───────────────────────────────────────────
    (function () {
        var badge = document.getElementById('csSupportBadge');
        if (!badge) return;
        function update() {
            var now = new Date();
            var day = now.getDay();
            var hr = now.getHours() + now.getMinutes() / 60;
            var isOpen = day >= 1 && day <= 5 && hr >= 8 && hr < 16;
            badge.className = 'cs-support-badge cs-support-badge--' + (isOpen ? 'open' : 'closed');
            badge.innerHTML = '<span class="cs-support-badge-dot"></span> ' + (isOpen ? 'We are open' : 'Currently closed');
        }
        update();
        setInterval(update, 60000);
    })();

    // ── Floating quick-action bar (mobile) ─────────────────────────────────
    (function () {
        if (window.innerWidth > 767) return;
        var bar = document.getElementById('csQuickActions');
        if (!bar) return;
        if (sessionStorage.getItem('csQADismissed')) return;

        var shown = false;
        var lookupVisible = false;
        var lookupForm = document.getElementById('orderLookupForm');

        if (lookupForm && window.IntersectionObserver) {
            var io = new IntersectionObserver(function (entries) {
                lookupVisible = entries[0].isIntersecting;
                if (shown) bar.style.display = lookupVisible ? 'none' : '';
            }, { threshold: 0.5 });
            io.observe(lookupForm);
        }

        window.addEventListener('scroll', function () {
            if (!shown && window.scrollY > 300) {
                shown = true;
                if (!lookupVisible) bar.style.display = '';
            }
        }, { passive: true });

        var qaTrack = document.getElementById('qaTrackOrder');
        if (qaTrack) {
            qaTrack.addEventListener('click', function () {
                var input = document.getElementById('orderNumber');
                if (input) {
                    input.focus();
                    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        }

        var qaChat = document.getElementById('qaChat');
        if (qaChat) {
            qaChat.addEventListener('click', function () {
                var dixaBtn = document.getElementById('csDixaButton');
                if (dixaBtn) dixaBtn.click();
            });
        }

        var closeBtn = document.getElementById('csQuickActionsClose');
        if (closeBtn) {
            closeBtn.addEventListener('click', function () {
                bar.style.display = 'none';
                sessionStorage.setItem('csQADismissed', '1');
            });
        }
    })();

})();

/* ============================================================================
 * STRAP FINDER (inline brand -> model type-ahead picker)
 * ----------------------------------------------------------------------------
 * Self-contained IIFE for the Customer Service "Product Information" page.
 * Renders a brand grid + cross-brand type-ahead search; selecting a model
 * shows an inline result card whose "Shop straps" button is the ONLY outbound
 * navigation (a store-relative /collections/<handle> URL). Everything else
 * stays inline. The #cs-finder snippet only exists on the product-info page,
 * so every lookup is null-guarded; the IIFE no-ops when absent.
 * ==========================================================================*/
(function () {
    'use strict';

    var root = document.getElementById('csFinder');
    var dataEl = document.getElementById('cs-finder-data');
    if (!root || !dataEl) return;

    var BRANDS = [];
    try {
        BRANDS = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
        BRANDS = [];
    }
    if (!Array.isArray(BRANDS) || !BRANDS.length) return;

    // Strings from #cs-config (read like the main engine; tolerate its absence).
    var STR = {};
    try {
        if (window.CS_CONFIG && window.CS_CONFIG.strings) {
            STR = window.CS_CONFIG.strings;
        } else {
            var cfgEl = document.getElementById('cs-config');
            if (cfgEl) STR = (JSON.parse(cfgEl.textContent || '{}') || {}).strings || {};
        }
    } catch (e2) {
        STR = {};
    }
    function t(key, fallback) {
        var v = (STR && STR[key]) || fallback || '';
        // Same entity-decode as the main helper above (see note there).
        if (v && v.indexOf('&') !== -1) {
            try { var ta = document.createElement('textarea'); ta.innerHTML = v; v = ta.value; } catch (e) {}
        }
        return v;
    }

    // Current market (nl/de/fr/es/uk) -> pick the matching translated brand
    // image (the guide images contain text, so they differ per market).
    var MARKET = '';
    var CHAT_ENABLED = false;
    try {
        var cfgObj = (window.CS_CONFIG && window.CS_CONFIG.market) ? window.CS_CONFIG : null;
        if (!cfgObj) {
            var cfgEl3 = document.getElementById('cs-config');
            cfgObj = cfgEl3 ? (JSON.parse(cfgEl3.textContent || '{}') || {}) : {};
        }
        MARKET = cfgObj.market || '';
        CHAT_ENABLED = !!cfgObj.chatEnabled;
    } catch (e3) {
        MARKET = '';
    }
    MARKET = String(MARKET || '').toLowerCase();
    function brandImage(b) {
        if (b && b.images && b.images[MARKET]) return b.images[MARKET];
        return (b && b.image) || '';
    }

    // ── DOM refs ───────────────────────────────────────────────────────────
    var searchInput = document.getElementById('csFinderSearch');
    var suggestEl = document.getElementById('csFinderSuggest');
    var brandsStep = document.getElementById('csFinderBrands');
    var brandGrid = document.getElementById('csFinderBrandGrid');
    var modelsStep = document.getElementById('csFinderModels');
    var modelsLabel = document.getElementById('csFinderModelsLabel');
    var modelList = document.getElementById('csFinderModelList');
    var guidePanel = document.getElementById('csFinderGuide');
    var guideImg = document.getElementById('csFinderGuideImg');
    var guideTitle = document.getElementById('csFinderGuideTitle');
    var guideLink = document.getElementById('csFinderGuideLink');
    var resultStep = document.getElementById('csFinderResult');
    var resultTitle = document.getElementById('csFinderResultTitle');
    var shopBtn = document.getElementById('csFinderShop');
    var emptyEl = document.getElementById('csFinderEmpty');
    var backBtn = document.getElementById('csFinderBack');
    var resultBack = document.getElementById('csFinderResultBack');
    var guideZoom = document.getElementById('csFinderGuideZoom');
    var lightbox = document.getElementById('csFinderLightbox');
    var lightboxImg = document.getElementById('csFinderLightboxImg');
    var lightboxClose = document.getElementById('csFinderLightboxClose');

    if (!brandGrid) return;

    // ── Flat model index for cross-brand search (built once) ─────────────────
    // Each entry: { brand, name, url, hay } where hay is the normalized
    // searchable string (brand + model name).
    function normalize(s) {
        return String(s == null ? '' : s)
            .toLowerCase()
            .replace(/[-_/]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
    var INDEX = [];
    var seenUrl = {};
    BRANDS.forEach(function (b) {
        var brandName = b && b.name ? b.name : '';
        (b && Array.isArray(b.models) ? b.models : []).forEach(function (m) {
            if (!m || !m.name) return;
            // Skip duplicate collection URLs (e.g. the shared lug-width straps
            // that appear under both "Other" and "Universal") so cross-brand
            // search doesn't surface the same collection twice.
            var u = m.url || '';
            if (u && seenUrl[u]) return;
            if (u) seenUrl[u] = true;
            INDEX.push({
                brand: brandName,
                name: m.name,
                url: u,
                hay: normalize(brandName + ' ' + m.name)
            });
        });
    });

    // ── HTML escaping ────────────────────────────────────────────────────────
    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    // ── Step visibility ──────────────────────────────────────────────────────
    function showStep(which) {
        if (brandsStep) brandsStep.hidden = which !== 'brand';
        if (modelsStep) modelsStep.hidden = which !== 'model';
        if (resultStep) resultStep.hidden = which !== 'result';
        // After advancing a step, bring the finder back into view so the new
        // panel (model list / result) isn't off-screen on mobile and the tap
        // feels like it did something.
        if (root && which !== 'brand') {
            try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
        }
    }

    // ── Brand grid ───────────────────────────────────────────────────────────
    function renderBrands() {
        brandGrid.innerHTML = BRANDS.map(function (b, i) {
            var src = brandImage(b);
            var img = src
                ? '<img class="cs-finder__tile-img" src="' + esc(src) + '" alt="" loading="lazy">'
                : '';
            return '<button type="button" class="cs-finder__tile" role="listitem" data-brand-idx="' + i + '">' +
                img +
                '<span class="cs-finder__tile-name">' + esc(b && b.name) + '</span>' +
                '</button>';
        }).join('');
    }

    // ── Model list for a chosen brand ────────────────────────────────────────
    function renderModels(brand) {
        if (!brand) return;
        var brandName = brand.name || '';
        var label = (t('finderModelsFor', 'Models for {brand}')).replace('{brand}', brandName);
        if (modelsLabel) modelsLabel.textContent = label;

        // "How to identify your model" panel: guide image + tips + full-guide link.
        if (guidePanel) {
            var guideSrc = brandImage(brand);
            var hasImage = !!guideSrc;
            if (guideImg) {
                if (hasImage) {
                    guideImg.setAttribute('src', guideSrc);
                    guideImg.setAttribute('alt', (t('finderModelsFor', 'Models for {brand}')).replace('{brand}', brandName));
                } else {
                    guideImg.removeAttribute('src');
                    guideImg.setAttribute('alt', '');
                }
            }
            if (guideTitle) {
                guideTitle.textContent = (t('finderModelsFor', 'Models for {brand}')).replace('{brand}', brandName);
            }
            if (guideLink) {
                if (brand.guideUrl) {
                    // Keep guide links same-origin so they resolve on the
                    // current market's domain (Shopify Markets localizes the
                    // handle). Strip any absolute domain that slips into data.
                    var _gu = brand.guideUrl;
                    try {
                        if (/^https?:\/\//i.test(_gu)) {
                            var _u = new URL(_gu);
                            _gu = _u.pathname + _u.search + _u.hash;
                        }
                    } catch (e) {}
                    guideLink.setAttribute('href', _gu);
                    guideLink.textContent = (t('finderFullGuide', 'View the full {brand} model guide')).replace('{brand}', brandName);
                    guideLink.hidden = false;
                } else {
                    guideLink.setAttribute('href', '#');
                    guideLink.textContent = '';
                    guideLink.hidden = true;
                }
            }
            guidePanel.hidden = false;
        }

        // Build ordered product-line groups, then show a COMPACT GRID of lines
        // (Brand -> line -> variant). Big brands (Garmin = 121) become ~17 tidy
        // tiles instead of a long scroll; single-model lines jump straight to
        // the result. The type-ahead search still covers everything flat.
        var models = Array.isArray(brand.models) ? brand.models : [];
        var order = [];
        var byGroup = {};
        models.forEach(function (m, i) {
            var g = (m && m.group) ? m.group : '';
            if (!byGroup[g]) { byGroup[g] = []; order.push(g); }
            byGroup[g].push({ m: m, i: i });
        });
        modelList._brand = brand;
        modelList._order = order;
        modelList._byGroup = byGroup;
        renderLineGrid();
        showStep('model');
    }

    // Strip a leading line/brand prefix for a clean tile label.
    function tileLabel(full, prefix, brandName) {
        if (prefix && full.indexOf(prefix) === 0) {
            var r = full.slice(prefix.length).replace(/^[\s\-]+/, '');
            if (r.length >= 2) return r;
        }
        if (brandName && full.indexOf(brandName + ' ') === 0) {
            var r2 = full.slice(brandName.length + 1).trim();
            if (r2.length >= 2) return r2;
        }
        return full;
    }

    function modelTile(m, i, prefix, brandName) {
        return '<button type="button" class="cs-finder__model" role="listitem" data-model-idx="' + i + '">' +
            '<span class="cs-finder__model-name">' + esc(tileLabel((m && m.name) || '', prefix, brandName)) + '</span>' +
            '<span class="cs-finder__model-arrow" aria-hidden="true">&#8594;</span>' +
            '</button>';
    }

    // Level 1: grid of product lines (+ single-model lines as direct tiles).
    function renderLineGrid() {
        var order = modelList._order || [];
        var byGroup = modelList._byGroup || {};
        var brandName = (modelList._brand && modelList._brand.name) || '';
        // Multi-model lines first (the expandable tiles), then single/direct
        // models after, so the groupings lead.
        var lineTiles = '';
        var singleTiles = '';
        order.forEach(function (g) {
            var entries = byGroup[g];
            if (g && entries.length > 1) {
                lineTiles += '<button type="button" class="cs-finder__line" data-line="' + esc(g) + '">' +
                    '<span class="cs-finder__line-name">' + esc(g) + '</span>' +
                    '<span class="cs-finder__line-count">' + entries.length + '</span>' +
                    '</button>';
            } else {
                entries.forEach(function (e) { singleTiles += modelTile(e.m, e.i, g, brandName); });
            }
        });
        modelList.innerHTML = '<div class="cs-finder__tiles">' + lineTiles + singleTiles + '</div>';
        modelList._view = 'lines';
    }

    // Level 2: the chosen line's models.
    function renderLineModels(lineKey) {
        var byGroup = modelList._byGroup || {};
        var brandName = (modelList._brand && modelList._brand.name) || '';
        var entries = byGroup[lineKey] || [];
        var back = '<button type="button" class="cs-finder__lines-back" data-lines-back>' +
            '<span aria-hidden="true">&#8592;</span><span>' + esc(t('finderBack', 'Back')) + '</span></button>';
        var heading = '<p class="cs-finder__tiles-heading">' + esc(lineKey) + '</p>';
        var tiles = entries.map(function (e) { return modelTile(e.m, e.i, lineKey, brandName); }).join('');
        modelList.innerHTML = back + heading + '<div class="cs-finder__tiles">' + tiles + '</div>';
        modelList._view = 'models';
    }

    // ── Inline result card (no navigation until Shop straps is clicked) ──────
    function showResult(name, url) {
        var title = (t('finderResultTitle', 'Straps for {model}')).replace('{model}', name || '');
        if (resultTitle) resultTitle.textContent = title;
        if (shopBtn) {
            if (url) {
                shopBtn.setAttribute('href', url);
                shopBtn.removeAttribute('aria-disabled');
            } else {
                shopBtn.setAttribute('href', '#');
                shopBtn.setAttribute('aria-disabled', 'true');
            }
        }
        showStep('result');
        if (shopBtn && typeof shopBtn.focus === 'function') {
            try { shopBtn.focus(); } catch (e) {}
        }
    }

    // ── Search (type-ahead across all brands) ────────────────────────────────
    var activeIdx = -1;
    var matches = [];

    function closeSuggest() {
        if (!suggestEl) return;
        suggestEl.hidden = true;
        suggestEl.innerHTML = '';
        activeIdx = -1;
        matches = [];
        if (searchInput) searchInput.setAttribute('aria-expanded', 'false');
    }

    function runSearch(q) {
        var query = normalize(q);
        if (query.length < 2) {
            closeSuggest();
            return;
        }
        var tokens = query.split(' ').filter(Boolean);
        matches = INDEX.filter(function (entry) {
            return tokens.every(function (tok) {
                return entry.hay.indexOf(tok) !== -1;
            });
        }).slice(0, 8);

        if (!matches.length) {
            var cantFind = t('finderCantFind', '');
            suggestEl.innerHTML = '<li class="cs-finder__suggest-empty" role="option" aria-disabled="true">' +
                esc(t('finderNoResults', 'No results found.')) +
                (cantFind ? '<span class="cs-finder__suggest-help">' + esc(cantFind) + '</span>' : '') +
                (CHAT_ENABLED ? '<button type="button" class="cs-finder__suggest-chat" onclick="if(window.csOpenChat)window.csOpenChat();">' + esc(t('chatButtonLabel', 'Chat with us')) + '</button>' : '') +
                '</li>';
            suggestEl.hidden = false;
            activeIdx = -1;
            if (searchInput) searchInput.setAttribute('aria-expanded', 'true');
            return;
        }

        suggestEl.innerHTML = matches.map(function (m, i) {
            return '<li class="cs-finder__suggest-item" role="option" id="csFinderOpt' + i + '" data-idx="' + i + '">' +
                '<span class="cs-finder__suggest-name">' + esc(m.name) + '</span>' +
                '<span class="cs-finder__suggest-brand">' + esc(m.brand) + '</span>' +
                '</li>';
        }).join('');
        suggestEl.hidden = false;
        activeIdx = -1;
        if (searchInput) searchInput.setAttribute('aria-expanded', 'true');
    }

    function highlight(idx) {
        var items = suggestEl ? suggestEl.querySelectorAll('.cs-finder__suggest-item') : [];
        for (var i = 0; i < items.length; i++) {
            var on = i === idx;
            items[i].classList.toggle('is-active', on);
            if (on) {
                items[i].setAttribute('aria-selected', 'true');
                if (searchInput) searchInput.setAttribute('aria-activedescendant', items[i].id);
            } else {
                items[i].removeAttribute('aria-selected');
            }
        }
        if (idx < 0 && searchInput) searchInput.removeAttribute('aria-activedescendant');
    }

    function pickMatch(idx) {
        var m = matches[idx];
        if (!m) return;
        closeSuggest();
        if (searchInput) searchInput.value = m.name;
        showResult(m.name, m.url);
    }

    // Debounced input
    var debounceTimer = null;
    if (searchInput) {
        searchInput.addEventListener('input', function () {
            var val = searchInput.value;
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(function () { runSearch(val); }, 150);
        });

        searchInput.addEventListener('keydown', function (e) {
            if (suggestEl.hidden || !matches.length) {
                if (e.key === 'Enter') e.preventDefault();
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                activeIdx = (activeIdx + 1) % matches.length;
                highlight(activeIdx);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                activeIdx = (activeIdx - 1 + matches.length) % matches.length;
                highlight(activeIdx);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                pickMatch(activeIdx >= 0 ? activeIdx : 0);
            } else if (e.key === 'Escape') {
                closeSuggest();
            }
        });
    }

    if (suggestEl) {
        suggestEl.addEventListener('mousedown', function (e) {
            // mousedown (not click) so it fires before the input blur
            var li = e.target.closest('.cs-finder__suggest-item');
            if (!li) return;
            e.preventDefault();
            pickMatch(Number(li.getAttribute('data-idx')));
        });
    }

    document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) closeSuggest();
    });

    // ── Brand / model delegation ─────────────────────────────────────────────
    brandGrid.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-brand-idx]');
        if (!btn) return;
        var b = BRANDS[Number(btn.getAttribute('data-brand-idx'))];
        if (b) {
            closeSuggest();
            renderModels(b);
        }
    });

    modelList.addEventListener('click', function (e) {
        // Drill into a product line.
        var line = e.target.closest('[data-line]');
        if (line) { renderLineModels(line.getAttribute('data-line')); return; }
        // Back from a line's models to the line grid.
        if (e.target.closest('[data-lines-back]')) { renderLineGrid(); return; }
        // Pick a model.
        var btn = e.target.closest('[data-model-idx]');
        if (!btn) return;
        var brand = modelList._brand;
        if (!brand) return;
        var m = (brand.models || [])[Number(btn.getAttribute('data-model-idx'))];
        if (m) showResult(m.name, m.url);
    });

    if (backBtn) {
        backBtn.addEventListener('click', function () {
            showStep('brand');
        });
    }
    if (resultBack) {
        resultBack.addEventListener('click', function () {
            // Return to whichever step makes sense: model list if a brand is set,
            // otherwise the brand grid.
            if (modelList && modelList._brand) {
                showStep('model');
            } else {
                showStep('brand');
            }
        });
    }

    // ── Guide-image zoom (FLIP grow from the thumbnail, restore on click-out) ─
    // Portal the overlay to <body>: its `.cs__main` ancestor carries an
    // animated transform (cs-page-in), which would otherwise be the containing
    // block for this position:fixed overlay and stop it covering the viewport.
    if (lightbox && document.body && lightbox.parentNode !== document.body) {
        document.body.appendChild(lightbox);
    }
    // Visibility is driven by the .is-open class (so it can transition); drop
    // the hidden attribute which would force display:none.
    if (lightbox) lightbox.removeAttribute('hidden');

    var lbScrollY = 0;
    var lbCloseTimer = null;
    var LB_MS = 340;
    function lockBody() {
        lbScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        var b = document.body;
        b.style.position = 'fixed';
        b.style.top = '-' + lbScrollY + 'px';
        b.style.left = '0';
        b.style.right = '0';
        b.style.width = '100%';
    }
    function unlockBody() {
        var b = document.body;
        b.style.position = '';
        b.style.top = '';
        b.style.left = '';
        b.style.right = '';
        b.style.width = '';
        window.scrollTo(0, lbScrollY);
    }
    function isOpen() {
        return lightbox && lightbox.classList.contains('is-open');
    }
    // Transform that maps the big image back onto the small thumbnail's box.
    function thumbTransform() {
        if (!lightboxImg || !guideImg) return '';
        var thumb = guideImg.getBoundingClientRect();
        var full = lightboxImg.getBoundingClientRect();
        if (!full.width || !thumb.width) return '';
        var dx = thumb.left - full.left;
        var dy = thumb.top - full.top;
        var s = thumb.width / full.width;
        return 'translate(' + dx + 'px,' + dy + 'px) scale(' + s + ')';
    }
    function openLightbox() {
        if (!lightbox || !lightboxImg || !guideImg) return;
        var src = guideImg.getAttribute('src');
        if (!src) return;
        if (lbCloseTimer) { clearTimeout(lbCloseTimer); lbCloseTimer = null; }
        lightboxImg.setAttribute('src', src);
        lightboxImg.setAttribute('alt', guideImg.getAttribute('alt') || '');
        lockBody();
        lightbox.classList.add('is-open');
        lightbox.scrollTop = 0;
        // FLIP: start the image at the thumbnail's size/position, then release
        // to its natural size so it visibly grows out of the thumbnail.
        var start = thumbTransform();
        if (start) {
            lightboxImg.style.transition = 'none';
            lightboxImg.style.transform = start;
            void lightboxImg.offsetWidth; // force reflow
            lightboxImg.style.transition = '';
            lightboxImg.style.transform = '';
        }
        if (lightboxClose && typeof lightboxClose.focus === 'function') {
            try { lightboxClose.focus(); } catch (e) {}
        }
    }
    function closeLightbox() {
        if (!isOpen()) return;
        // Shrink back toward the thumbnail, then tear down after the transition.
        var back = thumbTransform();
        if (back && lightboxImg) lightboxImg.style.transform = back;
        lightbox.classList.remove('is-open');
        if (lbCloseTimer) clearTimeout(lbCloseTimer);
        lbCloseTimer = setTimeout(function () {
            if (lightboxImg) {
                lightboxImg.style.transition = 'none';
                lightboxImg.style.transform = '';
                lightboxImg.removeAttribute('src');
                void lightboxImg.offsetWidth;
                lightboxImg.style.transition = '';
            }
            unlockBody();
            lbCloseTimer = null;
        }, LB_MS);
        if (guideZoom && typeof guideZoom.focus === 'function') {
            try { guideZoom.focus(); } catch (e) {}
        }
    }
    if (guideZoom) guideZoom.addEventListener('click', openLightbox);
    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox) {
        lightbox.addEventListener('click', function (e) {
            // Click anywhere except the image itself restores it.
            if (e.target !== lightboxImg) closeLightbox();
        });
    }
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) closeLightbox();
    });

    // ── Init ─────────────────────────────────────────────────────────────────
    if (emptyEl) emptyEl.hidden = true;
    renderBrands();
    showStep('brand');
})();

/* ============================================================================
 * MOBILE TAB BAR — condense on scroll (Revolut-style floating dock).
 * At the top of the page the labels show; once scrolled past a small
 * threshold the bar shrinks to an icons-only pill. No-ops if the bar is absent.
 * ==========================================================================*/
(function () {
    'use strict';
    var bar = document.getElementById('csTabBar');
    if (!bar) return;
    /* Condense LATER than the old flat 140px so the labels stay readable long
       enough to take in every option before the dock shrinks. Viewport-relative
       so a taller phone gives proportionally more reading room, with a floor for
       short screens, plus hysteresis so it cannot flicker on the boundary. */
    function condenseAt() {
        return Math.max(260, Math.round((window.innerHeight || 700) * 0.45));
    }
    var ticking = false;
    var pinTimer = null;

    /* Timing comes from the stylesheet so the guard below and the CSS
       transition can never disagree about how long a state change takes. */
    function dockDuration() {
        var raw = '';
        try {
            raw = window.getComputedStyle(document.documentElement)
                .getPropertyValue('--cs-dock-dur');
        } catch (e) { raw = ''; }
        var n = parseFloat(raw);
        if (!isFinite(n) || n <= 0) return 200;
        return /ms/.test(raw) ? n : n * 1000;
    }
    var DUR = dockDuration();

    /* The dock's height CHANGES with its state (labels wrap to two lines when
       open, ~86px; slim icons-only pill when condensed, ~67px). Measure BOTH
       once, then just swap the published value on state change -- no layout
       read per scroll frame. Transitions are suppressed during the measurement
       so toggling the class cannot flash. */
    var DOCK = { open: 0, slim: 0, offset: 0, ready: false };

    function measureDock() {
        var cs = window.getComputedStyle(bar);
        if (cs.display === 'none') {
            document.documentElement.style.removeProperty('--cs-dock-clearance');
            DOCK.ready = false;
            return;
        }
        var wasCondensed = bar.classList.contains('is-condensed');
        bar.classList.add('is-measuring');
        bar.classList.remove('is-condensed');
        DOCK.open = bar.getBoundingClientRect().height;
        bar.classList.add('is-condensed');
        DOCK.slim = bar.getBoundingClientRect().height;
        if (!wasCondensed) { bar.classList.remove('is-condensed'); }
        bar.classList.remove('is-measuring');
        DOCK.offset = parseFloat(cs.bottom) || 0;
        DOCK.ready = true;
    }

    /* Sit the floaters directly on top of the dock in its CURRENT state, so
       when it minimises they glide down and hug the slim pill. 10px breathing
       room. Both the chat launcher (right) and back-to-top (left) read the same
       value, so they always line up with each other. */
    function applyClearance() {
        if (!DOCK.ready) return;
        var h = bar.classList.contains('is-condensed') ? DOCK.slim : DOCK.open;
        var px = Math.round(h + DOCK.offset + 10);
        document.documentElement.style.setProperty('--cs-dock-clearance', px + 'px');

        /* The Dixa launcher ships its own injected !important styles that beat a
           stylesheet rule, so it never picked up the custom property. Set it on
           the element instead (also !important) and re-assert shortly after, in
           case the widget re-renders. Cheap: only runs when the dock actually
           changes state, not per scroll frame. */
        /* The dock is a <=900px feature and its stylesheet rule is scoped that
           way, but this inline !important was not, so on desktop it kept
           lifting the launcher to dock height and Dixa floated far above the
           loyalty button in the opposite corner.

           Below the breakpoint: lift clear of the dock, as before.
           Above it: sit level with the loyalty FAB, which is bottom:24px in
           assets/loyalty-rework.css (16px only under 480px, well inside the
           docked range). Removing the property is not enough here, because
           Dixa's own injected !important bottom does not match that. */
        var DESKTOP_BOTTOM = 24;
        var docked = false;
        try { docked = window.matchMedia('(max-width: 900px)').matches; }
        catch (e) { docked = (window.innerWidth || 0) <= 900; }
        var target = docked ? px : DESKTOP_BOTTOM;

        var pin = function () {
            var nodes = document.querySelectorAll(
                '[id^="dixa-messenger-toggler-wrapper"],[id^="dixa-messenger-frame-wrapper"]'
            );
            for (var i = 0; i < nodes.length; i++) {
                try { nodes[i].style.setProperty('bottom', target + 'px', 'important'); } catch (e) {}
            }
        };
        pin();
        /* ONE pending re-assert. Every flip used to queue another 400ms timer
           that was never cleared, so a flick across the threshold left several
           in flight and the launcher kept being repositioned well after the
           scroll had stopped. */
        if (pinTimer !== null) { window.clearTimeout(pinTimer); }
        pinTimer = window.setTimeout(function () { pinTimer = null; pin(); }, 400);
    }

    /* Which state the CURRENT position calls for.

       Condensing and re-expanding are deliberately NOT the same threshold.
       The labels are worth showing at the top of the page, where someone is
       still deciding where to go; once they are reading, the slim pill is
       right for the whole rest of the page. So the dock condenses at 45vh but
       only expands again near the very top.

       The old rule expanded 80px back above the condense point, which put
       both edges in the middle of the page: scrolling up and down there
       crossed them over and over, and every crossing restarted the
       animation. With the re-expand pinned near the top, flicking anywhere
       below it does not change state at all, which is what actually stops the
       churn. */
    var EXPAND_AT = 140;

    function wantsCondensed(y) {
        return bar.classList.contains('is-condensed') ? (y > EXPAND_AT) : (y > condenseAt());
    }

    /* A state change is never interrupted.

       Each flip starts a transition. Flipping again inside that window left
       the previous one half finished, and several of those overlapping is
       what made a fast scroll look broken. So a flip may only start once the
       last one has landed. A crossing during the wait is not thrown away: it
       is re-checked the moment the dock settles, so the dock still ends in
       the state the scroll position calls for, just never mid-animation. */
    var DWELL = DUR + 40;
    var lastFlip = -1e9;
    var settleTimer = null;

    function nowMs() {
        try { if (window.performance && performance.now) return performance.now(); }
        catch (e) {}
        return Date.now();
    }

    function update() {
        ticking = false;
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        var want = wantsCondensed(y);
        if (want === bar.classList.contains('is-condensed')) return;

        var waited = nowMs() - lastFlip;
        if (waited < DWELL) {
            if (settleTimer === null) {
                settleTimer = window.setTimeout(function () {
                    settleTimer = null;
                    update();
                }, DWELL - waited);
            }
            return;
        }

        lastFlip = nowMs();
        if (want) { bar.classList.add('is-condensed'); }
        else { bar.classList.remove('is-condensed'); }
        applyClearance();
    }

    window.addEventListener('scroll', function () {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });

    measureDock();
    applyClearance();
    window.addEventListener('resize', function () { measureDock(); applyClearance(); update(); }, { passive: true });
    window.addEventListener('orientationchange', function () {
        window.setTimeout(function () { measureDock(); applyClearance(); }, 250);
    });
    update();
})();

/* ============================================================================
 * SITUATION HIGHLIGHT — light the option cards when the banner button is used.
 *
 * This is what drives the effect on a click, not the :target rule in the CSS.
 * theme.js intercepts same-page hash links, calls preventDefault and scrolls the
 * target itself, so the hash is never set and :target never matches from a
 * click. :target still earns its place for someone opening a shared
 * ...#cs-situations link, where it lights the cards on load.
 *
 * Nothing here may preventDefault: theme.js owns the scroll and this only adds
 * the ring on top.
 * ==========================================================================*/
(function () {
    'use strict';
    document.addEventListener('click', function (e) {
        var link = e.target && e.target.closest ? e.target.closest('a[href$="#cs-situations"]') : null;
        if (!link) return;
        var grid = document.getElementById('cs-situations');
        if (!grid) return;
        /* Clicking again with the hash already set does not re-fire :target, so
           swap a class and force a reflow between the two writes, otherwise the
           browser coalesces them and the animation never restarts. */
        grid.classList.remove('is-highlighted');
        void grid.offsetWidth;
        grid.classList.add('is-highlighted');
    });
})();

/* ============================================================================
 * TOAST primitive — polite auto-dismiss snackbar (window.csToast).
 * ==========================================================================*/
(function () {
    'use strict';
    window.csToast = function (message, kind) {
        if (!message) return;
        var region = document.getElementById('cs-toast-region');
        if (!region) {
            region = document.createElement('div');
            region.id = 'cs-toast-region';
            region.className = 'cs-toast-region';
            region.setAttribute('role', 'status');
            region.setAttribute('aria-live', 'polite');
            document.body.appendChild(region);
        }
        var toast = document.createElement('div');
        toast.className = 'cs-toast ' + (kind === 'error' ? 'cs-toast--error' : 'cs-toast--success');
        var ok = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        var err = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>';
        toast.innerHTML = '<span class="cs-toast__ico">' + (kind === 'error' ? err : ok) + '</span><span class="cs-toast__msg"></span>';
        toast.querySelector('.cs-toast__msg').textContent = message;
        region.appendChild(toast);
        window.requestAnimationFrame(function () { toast.classList.add('is-in'); });
        var ttl = setTimeout(remove, 4000);
        function remove() {
            clearTimeout(ttl);
            toast.classList.remove('is-in');
            setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 400);
        }
        toast.addEventListener('click', remove);
    };
})();

/* ============================================================================
 * BACK TO TOP — appears after scrolling; smooth-scrolls up. No-op if absent.
 * ==========================================================================*/
(function () {
    'use strict';
    var btn = document.getElementById('csToTop');
    if (!btn) return;
    var SHOW_AT = 600;
    var ticking = false;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function update() {
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (y > SHOW_AT) { btn.hidden = false; btn.classList.add('is-visible'); }
        else { btn.classList.remove('is-visible'); }
        ticking = false;
    }
    window.addEventListener('scroll', function () {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    btn.addEventListener('click', function () {
        try { window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); }
        catch (e) { window.scrollTo(0, 0); }
    });
    update();
})();

/* ============================================================================
 * SCROLL PROGRESS — fill a slim top bar as the page is read (scaleX only).
 * ==========================================================================*/
(function () {
    'use strict';
    var el = document.getElementById('csProgress');
    if (!el) return;
    var bar = el.querySelector('.cs-progress__bar');
    if (!bar) return;
    var ticking = false;
    function update() {
        var doc = document.documentElement;
        var max = (doc.scrollHeight - doc.clientHeight) || 0;
        var y = window.pageYOffset || doc.scrollTop || 0;
        var p = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
        bar.style.transform = 'scaleX(' + p + ')';
        ticking = false;
    }
    window.addEventListener('scroll', function () {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
})();

/* ============================================================================
 * FAQ "Did this question help you?" voting -> BWJ data dashboard.
 * One vote per visitor per question (localStorage guard, persists across
 * reloads). Fire-and-forget via sendBeacon (survives navigation) with a
 * text/plain JSON body to avoid a CORS preflight; falls back to fetch+keepalive.
 * Endpoint URL + token come from #cs-config (theme setting, not committed code).
 * ==========================================================================*/
(function () {
    'use strict';
    var cfg = {};
    try {
        var el = document.getElementById('cs-config');
        if (el) cfg = JSON.parse(el.textContent || '{}') || {};
    } catch (e) { cfg = {}; }
    var STR = (cfg && cfg.strings) || {};
    var URL_ = cfg.helpfulUrl || '';
    var TOKEN = cfg.helpfulToken || '';

    var VOTE_PREFIX = 'cs_helpful_';   // + question_id -> 'yes' | 'no'
    var VID_KEY = 'cs_visitor_id';

    function ls(get, key, val) {
        try { return get ? window.localStorage.getItem(key) : window.localStorage.setItem(key, val); }
        catch (e) { return null; }
    }
    function uuid() {
        try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (window.crypto && crypto.getRandomValues)
                ? crypto.getRandomValues(new Uint8Array(1))[0] % 16
                : Math.floor(((new Date()).getTime() + performance.now()) % 16);
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }
    function visitorId() {
        var v = ls(true, VID_KEY);
        if (!v) { v = uuid(); ls(false, VID_KEY, v); }
        return v;
    }
    function langOf() {
        return String(cfg.locale || '').split('-')[0].toLowerCase() || undefined;
    }
    function marketOf() {
        return cfg.country ? String(cfg.country).toUpperCase() : undefined;
    }

    // Mark a feedback box as already voted (quiet "thanks" state).
    function markVoted(box) {
        if (!box) return;
        box.classList.add('is-voted');
        var q = box.querySelector('.faq-feedback__q');
        if (q) q.textContent = STR.faqThanks || 'Thanks for your feedback!';
    }

    // On load, restore the voted state for questions this visitor already rated.
    function restore() {
        var boxes = document.querySelectorAll('.faq-feedback');
        for (var i = 0; i < boxes.length; i++) {
            var item = boxes[i].closest('.faq-item');
            var qid = item ? item.getAttribute('data-faq-id') : '';
            if (qid && ls(true, VOTE_PREFIX + qid)) markVoted(boxes[i]);
        }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', restore);
    } else { restore(); }

    function send(payload) {
        if (!URL_) return;
        var body = JSON.stringify(payload);
        try {
            if (navigator.sendBeacon) {
                var blob = new Blob([body], { type: 'text/plain' });
                if (navigator.sendBeacon(URL_, blob)) return;
            }
        } catch (e) {}
        try {
            fetch(URL_, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'no-cors' }).catch(function () {});
        } catch (e2) {}
    }

    document.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('.faq-feedback__btn') : null;
        if (!btn) return;
        var box = btn.closest('.faq-feedback');
        var item = btn.closest('.faq-item');
        if (!box || box.classList.contains('is-voted')) return;
        var qid = item ? item.getAttribute('data-faq-id') : '';
        // One vote per visitor per question.
        if (qid && ls(true, VOTE_PREFIX + qid)) { markVoted(box); return; }

        var helpful = btn.getAttribute('data-vote') === 'yes';
        markVoted(box);
        if (qid) ls(false, VOTE_PREFIX + qid, helpful ? 'yes' : 'no');

        send({
            token: TOKEN,
            helpful: helpful,
            question_id: qid,
            question_text: item ? item.getAttribute('data-faq-q') : '',
            store: cfg.brand || 'bwj',
            market: marketOf(),
            locale: langOf(),
            path: window.location.pathname,
            session_id: visitorId(),
            event_id: uuid()
        });
    });
})();

/* ============================================================
   PORTED FROM customer-service-nl.js (verbatim modules)
   PostNL live tracking + recommendations (NL only) and the
   n8n health probe that reveals the maintenance banner.
   Self-contained, window.SwbPnl* namespaced, self-gated to
   NL + PostNL, so no-ops on DE / FR / ES / UK.
   ============================================================ */
/* ============================================================
   LIVE POSTNL STATUS INSIDE THE ORDER CARD (SWB / smartwatchbanden.nl)

   The customer never sees a second form: they look their order up with
   ordernummer + e-mail exactly as before, and this block takes the
   trackingNumber and the shipping zip straight off that response and
   asks the PostNL proxy for the live status.

   Scope rules that this block never breaks:
     - PostNL + Netherlands only. Every other market keeps the carrier
       link it has today and never sees this block.
     - The order card is already on screen when we run. A failure here
       may never blank it, so every path ends in either a rendered
       status or the small inline failsafe form.
     - No links to the carrier's own site. The customer stays here.
     - Namespaced: every class and id starts with swb-pnl / swbPnl, and
       nothing in here uses document.getElementById.
     - No em dashes and no en dashes in customer facing copy.

   Ported from the standalone tracking page that this replaces:
   status headline + pill, per phase reassurance copy, letterbox aware
   delivered copy, shipment type line, expected delivery banner,
   4 step stepper and the collapsible scan timeline. The timeline
   toggle is a real button driving a class, never a flex summary,
   because a flex summary does not toggle on iOS Safari.
   ============================================================ */
(function () {
    'use strict';

    var ENDPOINT = 'https://actium.automagically.nl/webhook/postnl-status';
    var FETCH_TIMEOUT_MS = 10000;
    var WATCHDOG_MS = 11500;
    var BARCODE_RE = /^[A-Z0-9]{8,30}$/;

    /* Is this parcel actually in PostNL's network?

       BARCODE_RE only checks the general shape, so other carriers' codes sail
       straight through it and we end up asking PostNL about a parcel it has
       never heard of. The customer then reads "check your details" about a
       tracking code that is perfectly correct, which is worse than showing no
       live status at all.

       Real shapes in this store's orders:
         PostNL domestic (NL)          3SHDUT553185070      -> yes
         PostNL GlobalPack (UK/FR/ES)  LA314113575NL        -> yes
         Deutsche Post Warenpost (DE)  00340434515056818023 -> no
         LandMark Global (BE)          UD904856084BE        -> no

       An S10 code carries the ISSUING country in its last two letters, so
       PostNL's international parcels end in NL no matter where they are going.
       Verified: the BE codes above return nothing from PostNL under either
       BE or NL. Those markets keep their own estimate and the Track Package
       link to the right carrier until each has an API of its own. */
    function isPostNlBarcode(value) {
        var s = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (/^3S/.test(s)) return true;              // domestic
        if (/^[A-Z]{2}[0-9]{9}NL$/.test(s)) return true;  // S10 issued by NL
        return false;
    }

    /* LandMark Global, the Belgian carrier. Their own S10 range ends BE, and
       the proxy reads status straight from their tracking page. */
    /* The CARRIER decides, not the barcode format.

       Belgium ships two Bpost service levels through LandMark and they use
       different formats: "Bpost standaard" as UD.........BE, "Bpost Premium+"
       as a 24-digit 3232... code. Matching only the first silently treated
       every paid-express parcel as untrackable, even though LandMark tracks it
       fine. Shopify's tracking_company agrees with the barcode on all 247
       shipments checked, so it is the safer key; the known shapes stay as a
       fallback for a card that has no carrier value. */
    function isLandmarkBarcode(value, carrier) {
        if (/landmark/i.test(String(carrier || ''))) return true;
        var s = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (/^UD[0-9]{9}BE$/.test(s)) return true;
        if (/^3232[0-9]{20}$/.test(s)) return true;
        return false;
    }

    /* Can the proxy return a live status for this parcel at all?
       Deutsche Post Warenpost (DE, 00340…) deliberately answers no: dhl.de is
       a JS shell whose data endpoint refuses automated requests, so Germany
       needs a real API key and until then keeps its estimate plus the
       Deutsche Post track link. */
    /* Deutsche Post / DHL Germany.

       Warenpost was untrackable for as long as the only sources were PostNL
       and LandMark: the parcels are real but their scans live behind a
       JavaScript-only dhl.de page. The proxy now asks TrackingMore for this
       range instead (their detect endpoint calls it dhl-germany), so the
       German market can finally pass this gate. */
    function isDhlDeBarcode(value, carrier) {
        var digits = String(value || '').replace(/[^0-9]/g, '');
        /* The 20-digit 00340 range is Deutsche Post / DHL Germany and nothing
           else, and it is what both stores actually ship to Germany. A
           carrier label alone is not proof: "DHL" covers DHL Germany, DHL
           Express and DHL eCommerce, which are separate couriers over
           overlapping number lengths, so a number we cannot attribute keeps
           its delivery estimate rather than risking a stranger's parcel.
           This must stay in step with isDhlDe in the proxy's resolver. */
        if (!/^00340[0-9]{15}$/.test(digits)) return false;
        var c = String(carrier || '');
        if (c && !/dhl|deutsche\s*post|post/i.test(c)) return false;
        return true;
    }

    function isTrackableBarcode(value, carrier) {
        return isPostNlBarcode(value) || isLandmarkBarcode(value, carrier) || isDhlDeBarcode(value, carrier);
    }
    /* ------------------------------------------------------------------
       MARKET AWARENESS
       Every market ships through PostNL, so every market gets the live
       status. What differs is the DOORSTEP carrier the customer actually
       sees, the postcode format we validate, and the country code the
       proxy needs. Copy comes from CS_CONFIG.postnl (locale files) with
       {carrier} substituted, so no market is ever shown "PostNL" when a
       local partner delivers it.
       ------------------------------------------------------------------ */
    var MARKET_CFG = {
        nl: { carrier: 'PostNL',        country: 'NL', re: /^[0-9]{4}[A-Z]{2}$/,                      ph: '1234 AB'  },
        de: { carrier: 'Deutsche Post', country: 'DE', re: /^[0-9]{5}$/,                              ph: '12345'    },
        fr: { carrier: 'Colissimo',     country: 'FR', re: /^[0-9]{5}$/,                              ph: '75001'    },
        es: { carrier: 'Correos',       country: 'ES', re: /^[0-9]{5}$/,                              ph: '28001'    },
        uk: { carrier: 'Royal Mail',    country: 'GB', re: /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/, ph: 'SW1A 1AA' }
    };
    var MKT = ((window.CS_CONFIG && window.CS_CONFIG.market) || 'nl').toString().toLowerCase();
    var M = MARKET_CFG[MKT] || MARKET_CFG.nl;
    var CARRIER = M.carrier;
    /* The carrier of the parcel being shown, which is not always the market's.
       attach() sets this from the card's own resolved carrier before rendering;
       until then the market's value is the sensible default. */
    var ACTIVE_CARRIER = CARRIER;

    /* Which markets may call the live PostNL API.

       PostNL authorises the Shipment Status API per BARCODE RANGE. This
       account is cleared for the domestic 3S range only, so every
       international shipment (an S10 barcode like LA...NL, which is what
       UK / DE / FR / ES all ship under) comes back as error 24,
       "The user is not authorized for this operation based on the provided
       BarcodeRange", surfaced by the proxy as reason "not_authorized".

       Those markets therefore skip the API entirely and keep what they had
       before: the order card's own delivery estimate plus its Track Package
       redirect to the local carrier. Nothing is lost, and the customer is
       never shown a lookup that cannot succeed.

       This is a theme setting, not a code constant, so the moment PostNL
       authorises the international range the markets can be switched back on
       from the theme editor. Set Customer service > PostNL live markets to
       e.g. "nl,uk,de,fr,es". */
    var POSTCODE_RE = M.re;

    /* Postcode shapes by DESTINATION country.

       Every storefront serves more than one country, so the market's own
       pattern is the wrong test for an inbound parcel: BE is four digits while
       NL is four digits plus two letters, and Belgian customers use the Dutch
       site. An unknown country falls back to the market pattern, and if that
       also fails we accept anything non-empty rather than refuse a parcel we
       simply have no rule for. */
    var POSTCODE_BY_COUNTRY = {
        NL: /^[0-9]{4}[A-Z]{2}$/,
        BE: /^[0-9]{4}$/,
        LU: /^[0-9]{4}$/,
        DE: /^[0-9]{5}$/,
        FR: /^[0-9]{5}$/,
        ES: /^[0-9]{5}$/,
        IT: /^[0-9]{5}$/,
        AT: /^[0-9]{4}$/,
        CH: /^[0-9]{4}$/,
        GB: /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/,
        IE: /^[A-Z0-9]{3,8}$/
    };

    function postcodeLooksValid(zip, countryCode) {
        var z = String(zip || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (!z) return false;
        var cc = String(countryCode || '').toUpperCase().trim();
        var re = POSTCODE_BY_COUNTRY[cc];
        if (re) return re.test(z);
        if (POSTCODE_RE && POSTCODE_RE.test(z)) return true;
        // No rule for this country: do not block a parcel over it.
        return z.length >= 3;
    }

    /* Our own wording for PostNL's event text.

       Two problems with rendering the API's strings directly. They name
       PostNL and say "abroad" / "border" / "extranjero" / "frontière", which
       tells every non-Dutch customer the parcel ships cross-border from the
       Netherlands. And they describe destination-country stages the four step
       bar cannot show, so the useful detail arrives in wording we would rather
       not publish.

       Mapping the codes fixes both: the customer reads a clear, origin-neutral
       description of the same stage. An unmapped code falls through to the
       carrier's own text UNLESS that text mentions an origin, in which case a
       generic line is used instead: better vague than disclosing. */
    var EVENT_CODE_KEYS = {
        A01: 'ev_announced',
        B01: 'ev_received',
        J01: 'ev_sorted',
        J06: 'ev_sorted',
        J05: 'ev_out_for_delivery',
        X01: 'ev_in_transit',
        X19: 'ev_in_transit',
        X02: 'ev_at_hub',
        X08: 'ev_at_delivery_centre',
        I01: 'ev_delivered',
        I05: 'ev_delivered_letterbox',
        I11: 'ev_delivered_letterbox',
        DELIVERED: 'ev_delivered',

        /* TrackingMore, which is where the German market's scans come from.

           Their checkpoint text cannot be shown to anyone: one German parcel
           came back mixing English ("The shipment has been successfully
           delivered") with German ("Die Sendung wurde im Briefzentrum
           bearbeitet"), so the language follows whatever the carrier fed
           them. Only their documented status enum is mapped, and eventText
           below refuses to print a raw TM_ row even if they add a status we
           have not seen. */
        TM_INFORECEIVED: 'ev_announced',
        TM_PENDING: 'ev_announced',
        TM_TRANSIT: 'ev_in_transit',
        TM_PICKUP: 'ev_out_for_delivery',
        TM_DELIVERED: 'ev_delivered',
        TM_UNDELIVERED: 'ev_generic',
        TM_EXCEPTION: 'ev_generic',
        TM_EXPIRED: 'ev_generic',
        TM_NOTFOUND: 'ev_generic',
        TM_GENERIC: 'ev_generic',

        /* LandMark's rows carry no code of their own, so the proxy derives one
           from their wording and we translate it here like any other. Without
           this a Belgian parcel showed their English text ("Delivered
           (Delivered at Letterbox)") on a Dutch page. They reuse the same
           stages, so no new copy was needed. */
        LM_ANNOUNCED: 'ev_announced',
        LM_RECEIVED: 'ev_received',
        LM_SORTED: 'ev_sorted',
        LM_AT_HUB: 'ev_at_hub',
        LM_IN_TRANSIT: 'ev_in_transit',
        LM_OUT_FOR_DELIVERY: 'ev_out_for_delivery',
        LM_DELIVERED: 'ev_delivered',
        LM_DELIVERED_LETTERBOX: 'ev_delivered_letterbox'
    };

    /* Anything naming the origin, the carrier, or a border crossing. Matched
       across all five languages we serve. */
    var ORIGIN_LEAK_RE = /postnl|netherlands|nederland|holland|abroad|border|extranjero|frontera|frontière|étranger|etranger|ausland|grenze|buitenland/i;

    function eventText(ev) {
        var code = String((ev && ev.code) || '').toUpperCase().trim();
        var key = EVENT_CODE_KEYS[code];
        if (key) {
            var mapped = S(key, '');
            if (mapped) return mapped;
        }
        /* A source whose text we do not control never reaches the card.
           PostNL's own rows are in the reader's language because the proxy
           asks for it; TrackingMore's are not, so an unmapped TM_ code falls
           back to our generic line rather than to their string. */
        if (code.indexOf('TM_') === 0) return S('ev_generic', '');
        var raw = String((ev && ev.description) || '').trim();
        if (!raw) return S('ev_generic', '');
        if (ORIGIN_LEAK_RE.test(raw)) return S('ev_generic', '') || raw;
        return raw;
    }


    /* Localised copy. Shopify's | t filter HTML-escapes, so decode once, then
       swap {carrier}. Falls back to the Dutch original if a key is missing.

       MUST stay above anything that reads it. LIVE_MARKETS below used to sit
       before this line and read PNL_STR inside an immediately-invoked
       function: `var` hoisted the name but not the value, so it threw
       "Cannot read properties of undefined" at module load, the whole IIFE
       aborted, window.SwbPnlTracking was never assigned, and the call site's
       feature check silently skipped live tracking on EVERY market. It failed
       invisibly because the caller wraps attach() in try/catch and the card
       renders fine without it. */
    var PNL_STR = (window.CS_CONFIG && window.CS_CONFIG.postnl) || {};

    var LIVE_MARKETS = (function () {
        var raw = PNL_STR.live_markets;
        if (raw === undefined || raw === null || String(raw).trim() === '') return ['nl'];
        return String(raw).toLowerCase().split(',').map(function (x) { return x.trim(); })
            .filter(function (x) { return x; });
    })();
    function S(key, fallback) {
        var v = PNL_STR[key];
        if (v === undefined || v === null || v === '') v = fallback || '';
        if (v && v.indexOf('&') !== -1) {
            try { var ta = document.createElement('textarea'); ta.innerHTML = v; v = ta.value; } catch (e) {}
        }
        return String(v).replace(/{carrier}/g, ACTIVE_CARRIER || CARRIER);
    }

    var STEP_LABELS = [
        S('step_registered', 'Aangemeld'),
        S('step_sorted', 'Gesorteerd'),
        S('step_on_way', 'Onderweg'),
        S('step_delivered', 'Bezorgd')
    ];

    var PHASE_HELP = {
        1: S('help_1'),
        2: S('help_2'),
        3: S('help_3'),
        delivered: S('help_delivered'),
        deliveredLetterbox: S('help_delivered_letterbox')
    };

    /* Past this many days in transit a parcel is late in every market we
       ship to: the slowest promise is five working days and TrackingMore
       counts calendar days. It is deliberately not a per-market number,
       because the point is only to stop claiming everything is fine. */
    var OVERDUE_DAYS = 7;

    /* Which line the customer reads.

       Phase alone cannot say why a parcel is not moving. TrackingMore reports
       a refusal, a customs hold, a failed attempt and a parcel already on its
       way back to us all as their own statuses, and a parcel can be scanned
       daily for a week without getting closer. Each of those now has its own
       sentence; anything unrecognised still falls back to the phase copy, so
       a status we have never seen can never produce a blank card. */
    function scenarioHelp(data, phase, delivered, letterbox) {
        if (delivered) return letterbox ? PHASE_HELP.deliveredLetterbox : PHASE_HELP.delivered;
        var code = String((data && data.statusCode) || '').toLowerCase();
        var sub = String((data && data.statusSub) || '').toLowerCase();
        if (code === 'exception' || code === 'expired') return S('help_problem', '') || PHASE_HELP[phase];
        if (code === 'undelivered') return S('help_attempt_failed', '') || PHASE_HELP[phase];
        if (sub === 'pickup002' || sub === 'pickup003') return S('help_awaiting_pickup', '') || PHASE_HELP[phase];
        var days = Number(data && data.transitDays);
        if (isFinite(days) && days >= OVERDUE_DAYS) return S('help_overdue', '') || PHASE_HELP[phase];
        return PHASE_HELP[phase];
    }

    /* Every route into the failsafe, with the line the customer reads. */
    var FALLBACK_COPY = {
        no_tracking: { lead: S('fb_no_tracking_lead'), message: S('fb_no_tracking_msg') },
        no_zip:      { lead: S('fb_no_zip_lead'),      message: S('fb_no_zip_msg') },
        not_found:   { lead: S('fb_not_found_lead'),   message: S('fb_not_found_msg') },
        invalid:     { lead: S('fb_invalid_lead'),     message: S('fb_invalid_msg') },
        unavailable: { lead: S('fb_unavailable_lead'), message: S('fb_unavailable_msg') }
    };

    var LABELS = {
        brandPrefix: S('brand_prefix'),
        destTo: S('dest_to'),
        destDone: S('dest_done'),
        shipmentType: S('shipment_type'),
        typeLetterbox: S('type_letterbox'),
        typeParcel: S('type_parcel'),
        deliveredOn: S('delivered_on'),
        deliveredAt: S('delivered_at'),
        etaPrefix: S('eta_prefix'),
        etaBetween: S('eta_between'),
        etaAnd: S('eta_and'),
        progress: S('progress'),
        timelineTitle: S('timeline_title'),
        timelineSource: S('timeline_source'),
        eventFallback: S('event_fallback'),
        eventsEmptyEarly: S('events_empty_early'),
        eventsEmpty: S('events_empty'),
        barcodeLabel: S('barcode_label'),
        loading: S('loading'),
        fieldBarcode: S('field_barcode'),
        fieldPostcode: S('field_postcode'),
        submit: S('submit'),
        submitBusy: S('submit_busy'),
        errBarcode: S('err_barcode'),
        errPostcode: S('err_postcode')
    };

    var ICONS = {
        registered: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" y1="22" x2="12" y2="12"></line></svg>',
        transit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="1" y="3" width="15" height="13" rx="1"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>',
        delivered: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
        clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M12 6v6l4 2"></path></svg>',
        check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>',
        chevron: '<svg class="swb-pnl-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>',
        alert: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>'
    };

    /* Official PostNL logo, pasted from the standalone tracking page that
       this block replaces. PostNL publishes webshop logos for the shops
       that ship with them, and their terms forbid altering the mark, so it
       is never recoloured, stretched, redrawn or given a fill override.
       The only edits against that source file are the gradient id, which
       is namespaced swbPnlGrad because a duplicate SVG gradient id on one
       page silently steals the fill, and the class name so it stays inside
       the swb-pnl namespace.
       Branding only: this is not a link and never points at the carrier. */
    var POSTNL_LOGO = '<svg class="swb-pnl-brand-logo" xmlns="http://www.w3.org/2000/svg" viewBox="2 1.99 56.64 56.02" role="img" aria-label="PostNL" width="24" height="24"><defs><radialGradient id="swbPnlGrad" cx="22.53" cy="23.41" r="38.18" gradientTransform="matrix(.92633 -.3767 .33527 .82444 -6.22 12.52)" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#f1a100"/><stop offset=".23" stop-color="#f09e00"/><stop offset=".41" stop-color="#ee9401"/><stop offset=".57" stop-color="#eb8303"/><stop offset=".73" stop-color="#e66c05"/><stop offset=".87" stop-color="#e04e09"/><stop offset=".93" stop-color="#dd3f0a"/></radialGradient></defs><path d="M14.36 2c6.81 0 17.74 4.32 25 8.33 8 4.41 19.27 13.42 19.28 19.63 0 6.61-11.54 15.36-19.28 19.63C32.35 53.47 21.64 58 14.61 58a9.49 9.49 0 0 1-4.52-.94C4.53 54 2 40.86 2 30a67.93 67.93 0 0 1 1.94-16.19c1.48-5.75 3.61-9.54 6.15-10.94A8.91 8.91 0 0 1 14.36 2z" fill="#bbbbbc"/><path d="M56.78 30c0-4.71-8.87-12.88-18.32-18.09-10.89-6-23.28-9.55-27.37-7.3C6.44 7.14 3.89 20 3.89 30s2.4 22.74 7.2 25.39c4.36 2.4 16.21-1.14 27.37-7.29 9.6-5.33 18.31-13.19 18.32-18.1z" fill="url(#swbPnlGrad)"/><path d="M25.14 31a3.79 3.79 0 0 1-4.08 4A3.79 3.79 0 0 1 17 31a3.76 3.76 0 0 1 4.08-4.08A3.76 3.76 0 0 1 25.14 31zm-2.32 0c0-1.49-.75-2.14-1.76-2.14s-1.77.65-1.77 2.14.76 2.14 1.77 2.14 1.76-.81 1.76-2.14zm12.94-6.05c0-.06 0-.08-.09-.08A6.73 6.73 0 0 0 34 26a1.76 1.76 0 0 0-.44 1.17v5c0 2.18 1.25 2.86 2.6 2.86a3.58 3.58 0 0 0 1.62-.28.26.26 0 0 0 .12-.22v-1.42a.08.08 0 0 0-.1-.11 5.36 5.36 0 0 1-.92.2c-.7 0-1.12-.33-1.12-1.43v-2.86a.07.07 0 0 1 .08-.07h1.88a.08.08 0 0 0 .08-.08v-1.49a.25.25 0 0 0-.25-.24h-1.71a.07.07 0 0 1-.08-.08zM8.47 27.28a.25.25 0 0 1 .25-.28h3c2.93 0 4.44 1.75 4.44 4s-1.69 3.85-4.48 3.85h-.87a.08.08 0 0 0-.08.08v3.68c0 .06 0 .08-.09.08a6.54 6.54 0 0 1-1.69-1.16 1.72 1.72 0 0 1-.45-1.17zm5.45 3.66c0-1-.62-1.87-2.27-1.87h-.87a.09.09 0 0 0-.08.08v3.63a.08.08 0 0 0 .08.07h.91c1.97 0 2.23-1.36 2.23-1.91zm17.51-.32a4.74 4.74 0 0 0-1.57-.55l-.54-.07c-.67-.15-1.19-.26-1.19-.69s.35-.62.91-.62a8 8 0 0 1 2.74.54.1.1 0 0 0 .13-.08v-1.6a.26.26 0 0 0-.19-.24 9 9 0 0 0-2.41-.43 4.18 4.18 0 0 0-2.51.68 2.21 2.21 0 0 0-.92 1.85c0 1.73 1.42 2.06 2.76 2.37l.25.06c.62.14 1.25.28 1.25.78A.5.5 0 0 1 30 33a1.24 1.24 0 0 1-.94.25 13.57 13.57 0 0 1-3-.56.1.1 0 0 0-.1.09v1.55a.26.26 0 0 0 .18.24 10.18 10.18 0 0 0 2.74.47c2.32 0 3.52-.91 3.52-2.57a2.07 2.07 0 0 0-.97-1.85z" fill="#001a73"/><path d="M44.42 34.87h-.05v-4.74c0-1-.35-1.48-1.18-1.48a2.13 2.13 0 0 0-1 .31l-.73.43a.15.15 0 0 0-.06.11v5.32h-2.1v-7.54a.22.22 0 0 1 .22-.22h1.92a.05.05 0 0 1 0 .05v.59a.06.06 0 0 0 .06.06l.06-.05a3.51 3.51 0 0 1 .84-.47 4.52 4.52 0 0 1 1.54-.32c1.71 0 2.61 1 2.61 2.84v5.07zm5.94 0v-9.3a1.72 1.72 0 0 0-.36-1.2 6.57 6.57 0 0 0-1.67-1.15c-.06 0-.07 0-.07.05v11.55h.05zM24.51 25c.19 0 .27-.1.27-.31v-.85a.36.36 0 0 0-.36-.35H17.7a.35.35 0 0 0-.35.35v.85c0 .21.08.31.27.31zm.09-2.37c.12 0 .19 0 .22-.13l.14-.38c.26-.74.71-2 .8-2.31.14-.55.17-1.1-.58-1.38l-.73-.25c-.3-.09-.59-.16-.84-.22h-.14a.46.46 0 0 0-.42.29 13.58 13.58 0 0 0-.66 1.83.18.18 0 0 0 0 .13h.86c.1 0 .15 0 .17-.1a7.23 7.23 0 0 1 .37-1 .14.14 0 0 1 .13-.08H24l.38.13a.3.3 0 0 1 .14.38s-.76 2.12-1 2.82a.19.19 0 0 0 0 .18.18.18 0 0 0 .15.06zm-3.07 0c.17 0 .18-.19.18-.19s.05-4 .06-4.51a.24.24 0 0 0-.07-.18.13.13 0 0 0-.12-.05h-1a.13.13 0 0 0-.11.05.24.24 0 0 0-.07.18c0 .53.06 4.47.06 4.51s0 .19.18.19zm-3.1 0c.07 0 .12 0 .14-.06a.21.21 0 0 0 0-.18c-.22-.7-1-2.8-1-2.82a.31.31 0 0 1 .15-.38l.38-.13h.07a.14.14 0 0 1 .14.08 7.25 7.25 0 0 1 .36 1c0 .07.08.1.18.1h.74a.12.12 0 0 0 .11 0 .14.14 0 0 0 0-.13 12.33 12.33 0 0 0-.66-1.83.43.43 0 0 0-.41-.29h-.15c-.25.06-.54.13-.84.22l-.73.25c-.76.28-.73.83-.58 1.38.09.33.53 1.57.8 2.31.06.16.1.29.14.38a.19.19 0 0 0 .21.13zm2.63-5.74a.2.2 0 0 0 .13-.05A5.32 5.32 0 0 0 22 16a.14.14 0 0 0 0-.2 5.17 5.17 0 0 0-.91-.9h-.16a4.85 4.85 0 0 0-.91.9.14.14 0 0 0 0 .2 5.32 5.32 0 0 0 .85.86.18.18 0 0 0 .13.05z" fill="#fff"/></svg>';

    /* Small provenance chip at the top of the block. Neutral white ground,
       hairline border, PostNL navy for the name. PostNL orange sits about
       7 degrees of hue from our own #FF4F01, so it never appears as a UI
       accent here: the logo is the only place that orange is allowed. */
/* Which doorstep carrier this parcel is actually with.

       The logo slot is deliberately null for everyone but PostNL. The note
       above records that PostNL is the only mark we hold a licence for, and a
       carrier logo taken off a logo-aggregator site is neither licensed nor
       reliably current: DHL, Royal Mail, Correos, La Poste and Bpost all
       require written permission for commercial use and publish their assets
       through partner brand portals. Until a licensed file exists, each carrier
       gets its own accent instead, which localises the chip without borrowing
       anyone's artwork. Drop a licensed SVG into logo and it renders with no
       other change. */
    var CARRIER_BRAND = [
        { re: /postnl/i,               accent: '#EE7203', logo: function () { return POSTNL_LOGO; } },
        { re: /dhl|deutsche\s*post/i,  accent: '#D40511', logo: null },
        { re: /royal\s*mail/i,         accent: '#DA291C', logo: null },
        { re: /colissimo|la\s*poste/i, accent: '#003DA5', logo: null },
        { re: /correos/i,              accent: '#003C7D', logo: null },
        { re: /bpost|landmark/i,       accent: '#E5231B', logo: null }
    ];

    function carrierBrand(name) {
        var n = String(name || '');
        for (var i = 0; i < CARRIER_BRAND.length; i++) {
            if (CARRIER_BRAND[i].re.test(n)) return CARRIER_BRAND[i];
        }
        return null;
    }

    function brandHtml(carrierName) {
        /* The PostNL logo is only correct in the Netherlands. Outside NL the
           parcel is handed to a local doorstep partner, so we show THAT
           carrier's name and no third-party logo (we hold no licensed brand
           asset for them). CARRIER comes from MARKET_CFG. */
        /* The card's own carrier wins when it has one: the market constant is
           only right for a parcel actually carried by that market's carrier.
           The logo appears only for genuine PostNL, since that is the only
           brand asset we hold a licence for. */
        var name = String(carrierName || '') || CARRIER;
        var brand = carrierBrand(name);
        var logo = (brand && brand.logo) ? brand.logo() : '';
        var accent = (brand && !logo) ? brand.accent : '';
        return '<div class="swb-pnl-brandbar"><span class="swb-pnl-brand' +
            (logo ? '' : ' swb-pnl-brand--textonly') + '"' +
            (accent ? ' style="--swb-pnl-carrier:' + accent + '"' : '') + '>' +
            logo +
            '<span class="swb-pnl-brand-text">' + LABELS.brandPrefix +
            '<strong>' + esc(name) + '</strong></span>' +
            '</span></div>';
    }

    var uid = 0;

    /* ---- helpers ------------------------------------------ */

    function esc(value) {
        return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    function safeText(value) {
        if (typeof value === 'string') return value.trim();
        if (typeof value === 'number' && isFinite(value)) return String(value);
        return '';
    }

    function normBarcode(value) {
        return String(value === null || value === undefined ? '' : value).replace(/\s+/g, '').toUpperCase();
    }

    function normPostcode(value) {
        return String(value === null || value === undefined ? '' : value).replace(/\s+/g, '').toUpperCase();
    }

    function displayPostcode(value) {
        var p = normPostcode(value);
        if (MKT !== 'nl') return p;
        return POSTCODE_RE.test(p) ? p.slice(0, 4) + ' ' + p.slice(4) : p;
    }

    function parseDate(iso) {
        if (!iso) return null;
        var d = new Date(iso);
        return isNaN(d.getTime()) ? null : d;
    }

    function fmtParts(iso, options) {
        var d = parseDate(iso);
        if (!d) return '';
        var loc = (function () {
            var short = String((window.CS_CONFIG && window.CS_CONFIG.locale) || 'nl').toLowerCase();
            var map = { nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', en: 'en-GB' };
            return map[short] || short || 'nl-NL';
        })();
        try {
            return new Intl.DateTimeFormat(loc, options).format(d);
        } catch (e) {
            return '';
        }
    }

    function fmtLongDate(iso) {
        return fmtParts(iso, { weekday: 'long', day: 'numeric', month: 'long' });
    }

    function fmtTime(iso) {
        return fmtParts(iso, { hour: '2-digit', minute: '2-digit' });
    }

    function fmtStamp(iso) {
        var datePart = fmtParts(iso, { weekday: 'short', day: 'numeric', month: 'short' });
        if (!datePart) return '';
        var timePart = fmtTime(iso);
        return timePart ? datePart + ', ' + timePart : datePart;
    }

    /* An ok:true body with nothing in it is a failure wearing a success
       hat (the action-less 200 an n8n flow returns when it dies before
       its Respond node). Treat it as unavailable rather than render a
       hollow card that claims "Aangemeld". */
    function hasSubstance(data) {
        if (!data || typeof data !== 'object') return false;
        if (Array.isArray(data.events) && data.events.length) return true;
        if (safeText(data.statusDescription)) return true;
        if (parseDate(data.deliveredAt)) return true;
        var eta = (data.eta && typeof data.eta === 'object') ? data.eta : {};
        if (parseDate(eta.from) || parseDate(eta.to) || parseDate(eta.date)) return true;
        var phase = parseInt(data.phase, 10);
        return !isNaN(phase) && phase >= 1 && phase <= 4;
    }

    /* ---- the NL + PostNL gate ------------------------------ */

    function isPostNl(carrier) {
        return String(carrier === null || carrier === undefined ? '' : carrier).trim().toLowerCase() === 'postnl';
    }

    function isNl(addr) {
        var code = String((addr && addr.countryCode) || '').toUpperCase().trim();
        if (code) return code === 'NL';
        var name = String((addr && addr.country) || '').toLowerCase().trim();
        return name.indexOf('netherlands') !== -1 || name.indexOf('nederland') !== -1;
    }

    /* ---- shipment type (letterbox vs doorstep) ------------- */

    // "Brievenbuspakje+ ptp, gesorteerd" becomes "Brievenbuspakje+"
    function cleanProductDescription(raw) {
        if (typeof raw !== 'string') return '';
        var text = raw.replace(/,\s*(gesorteerd|standaard)\s*$/i, '');
        text = text.replace(/\bptp\b/gi, '');
        text = text.replace(/\s{2,}/g, ' ');
        text = text.replace(/[\s,;.-]+$/, '');
        return text.trim();
    }

    function newestEventText(data) {
        var events = Array.isArray(data.events) ? data.events : [];
        var best = null;
        var bestTime = -Infinity;
        events.forEach(function (ev) {
            if (!ev) return;
            var parsed = ev.time ? new Date(ev.time).getTime() : NaN;
            var value = isNaN(parsed) ? -Infinity : parsed;
            if (best === null || value > bestTime) {
                best = ev;
                bestTime = value;
            }
        });
        return (best && typeof best.description === 'string') ? best.description : '';
    }

    // The productDescription or the newest scan saying "brievenbus" is
    // what decides between the letterbox wording and the neighbours one.
    function isLetterbox(data) {
        var productText = (data && typeof data.productDescription === 'string') ? data.productDescription : '';
        if (/brievenbus/i.test(productText)) return true;
        return /brievenbus/i.test(newestEventText(data || {}));
    }

    function shipmentTypeLabel(data) {
        var cleaned = cleanProductDescription(data && data.productDescription);
        if (!cleaned) return '';
        return /brievenbus/i.test(cleaned) ? LABELS.typeLetterbox : LABELS.typeParcel;
    }

    /* ---- watchdog ------------------------------------------
       The 10 second abort needs AbortController and a fetch that really
       settles. This is the unconditional backstop just past it, so the
       spinner can never be the last thing the customer sees. */

    function clearWatchdog(ctx) {
        if (ctx.watchdog !== null) {
            window.clearTimeout(ctx.watchdog);
            ctx.watchdog = null;
        }
    }

    function armWatchdog(ctx, barcode, postcode) {
        clearWatchdog(ctx);
        ctx.watchdog = window.setTimeout(function () {
            ctx.watchdog = null;
            if (ctx.state === 'loading') renderFallback(ctx, 'unavailable', barcode, postcode);
        }, WATCHDOG_MS);
    }

    /* ---- fetch --------------------------------------------- */

    function fetchStatus(barcode, postcode, carrier, country) {
        if (typeof window.fetch !== 'function') {
            return Promise.reject(new Error('no_fetch'));
        }
        var controller = (typeof window.AbortController === 'function') ? new window.AbortController() : null;
        var timer = null;
        if (controller) {
            timer = window.setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
        }
        function clear() {
            if (timer !== null) {
                window.clearTimeout(timer);
                timer = null;
            }
        }
        return fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ barcode: barcode, postcode: postcode, country: country || M.country, carrier: carrier || '' }),
            signal: controller ? controller.signal : undefined
        }).then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
        }).then(function (data) {
            clear();
            return data;
        }, function (err) {
            clear();
            throw err;
        });
    }

    /* ---- rendering ----------------------------------------- */

    function bannerHtml(data, delivered) {
        if (delivered) {
            var dDate = fmtLongDate(data.deliveredAt);
            if (!dDate) return '';
            var dTime = fmtTime(data.deliveredAt);
            return '<div class="swb-pnl-banner swb-pnl-banner--delivered">' + ICONS.delivered +
                '<span>' + LABELS.deliveredOn + '<strong>' + esc(dDate) + '</strong>' +
                (dTime ? LABELS.deliveredAt + '<strong>' + esc(dTime) + '</strong>' : '') +
                '</span></div>';
        }
        var eta = (data.eta && typeof data.eta === 'object') ? data.eta : {};
        if (eta.from && eta.to) {
            var eDate = fmtLongDate(eta.from);
            var fromTime = fmtTime(eta.from);
            var toTime = fmtTime(eta.to);
            if (eDate && fromTime && toTime) {
                return '<div class="swb-pnl-banner">' + ICONS.clock +
                    '<span>' + LABELS.etaPrefix + '<strong>' + esc(eDate) + '</strong>' + LABELS.etaBetween +
                    '<strong>' + esc(fromTime) + LABELS.etaAnd + esc(toTime) + '</strong></span></div>';
            }
        }
        var oDate = fmtLongDate(eta.date || eta.from || eta.to);
        if (oDate) {
            return '<div class="swb-pnl-banner">' + ICONS.clock +
                '<span>' + LABELS.etaPrefix + '<strong>' + esc(oDate) + '</strong></span></div>';
        }
        return '';
    }

    function stepperHtml(phase, delivered) {
        var items = '';
        for (var i = 1; i <= 4; i++) {
            var cls = 'swb-pnl-step';
            if (delivered || i < phase) cls += ' is-done';
            else if (i === phase) cls += ' is-current';
            var ariaCurrent = (i === phase && !delivered) ? ' aria-current="step"' : '';
            items += '<li class="' + cls + '"' + ariaCurrent + '>' +
                '<span class="swb-pnl-step-dot">' + ICONS.check + '</span>' +
                '<span class="swb-pnl-step-label">' + STEP_LABELS[i - 1] + '</span></li>';
        }
        var listCls = delivered ? 'swb-pnl-stepper swb-pnl-stepper--delivered' : 'swb-pnl-stepper';
        return '<ol class="' + listCls + '" aria-label="' + LABELS.progress + '">' + items + '</ol>';
    }

    /* Provenance and freshness.

       Outside NL the status does not come from the official Shipment Status
       API (this account is not authorised for the international barcode
       range) but from the same track & trace service jouw.postnl.nl reads.
       That is an undocumented endpoint, so the card says plainly where the
       status came from and when it was read. If it ever goes stale or the
       shape drifts, the customer can see the timestamp rather than trusting a
       silent panel, and the proxy falls back to ok:false anyway.

       Renders nothing when the proxy sent no checkedAt, so an older proxy
       response simply shows no footer instead of an empty or wrong one. */
    function freshnessHtml(data) {
        var stamp = data && data.checkedAt;
        if (!stamp) return '';
        var when = new Date(stamp);
        if (isNaN(when.getTime())) return '';

        var time = '';
        try {
            time = when.toLocaleTimeString(CS_CONFIG.locale || 'en-GB',
                { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            time = when.toISOString().slice(11, 16);
        }

        /* Only the Dutch market names PostNL.

           Everywhere else the carrier the customer knows is the local one, and
           the event text is deliberately origin-neutral, so a footer reading
           "via PostNL Track & Trace" on the Spanish page would give away in
           one line what the rest of the card is careful not to say. Outside NL
           the neutral wording is used whichever source answered. */
        var tpl;
        if (MKT === 'nl' && data.source !== 'landmark') {
            tpl = (data.source === 'consumer')
                ? S('freshness_tracked', 'Status van PostNL Track & Trace, gecontroleerd om {time}')
                : S('freshness_live', 'Live status van PostNL, gecontroleerd om {time}');
        } else {
            tpl = S('freshness_carrier', 'Status van de vervoerder, gecontroleerd om {time}');
        }

        /* How old the carrier's newest scan is.

           A parcel can sit at one stage for days: the card showed "sorted"
           for six days on a Spanish order with no hint the information was
           stale, which reads as if the parcel is stuck. Saying plainly when
           it was last scanned is more honest than a status bar that looks
           live. Only shown for an undelivered parcel and only past the
           threshold, so a normal shipment gains no extra noise. */
        var stale = '';
        try {
            var newest = null;
            var evs = (data && Array.isArray(data.events)) ? data.events : [];
            for (var i = 0; i < evs.length; i++) {
                var t = Date.parse(evs[i] && evs[i].time);
                if (isFinite(t) && (newest === null || t > newest)) newest = t;
            }
            if (newest !== null && data && data.isDelivered !== true) {
                var days = Math.floor((Date.now() - newest) / 86400000);
                if (days >= 2) {
                    var st = S('stale_note', '');
                    if (st) stale = '<p class="swb-pnl-stale">' +
                        esc(String(st).replace(/{days}/g, String(days))) + '</p>';
                }
            }
        } catch (e) { stale = ''; }

        return '<p class="swb-pnl-freshness">' +
               esc(String(tpl).replace(/{time}/g, time)) + '</p>' + stale;
    }

    function timelineHtml(ctx, data, phase, delivered) {
        var events = Array.isArray(data.events) ? data.events.slice() : [];
        events.sort(function (a, b) {
            var ta = a && a.time ? new Date(a.time).getTime() : 0;
            var tb = b && b.time ? new Date(b.time).getTime() : 0;
            return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
        });

        var inner;
        if (events.length) {
            inner = '<ol class="swb-pnl-events">' + events.map(function (ev) {
                ev = ev || {};
                var stamp = fmtStamp(ev.time);
                var loc = safeText(ev.location);
                return '<li class="swb-pnl-event"><span class="swb-pnl-event-dot" aria-hidden="true"></span>' +
                    '<div class="swb-pnl-event-body">' +
                    (stamp ? '<span class="swb-pnl-event-time">' + esc(stamp) + '</span>' : '') +
                    '<span class="swb-pnl-event-desc">' + esc(eventText(ev) || LABELS.eventFallback) + '</span>' +
                    (loc ? '<span class="swb-pnl-event-loc">' + esc(loc) + '</span>' : '') +
                    '</div></li>';
            }).join('') + '</ol>';
        } else if (phase === 1 && !delivered) {
            inner = '<p class="swb-pnl-events-empty">' + LABELS.eventsEmptyEarly + '</p>';
        } else {
            inner = '<p class="swb-pnl-events-empty">' + LABELS.eventsEmpty + '</p>';
        }

        var bodyId = ctx.id + '-tl';
        return '<div class="swb-pnl-timeline" data-swb-pnl-timeline>' +
            '<button type="button" class="swb-pnl-timeline-toggle" data-swb-pnl-toggle aria-expanded="false" aria-controls="' + bodyId + '">' +
            '<span class="swb-pnl-timeline-heading">' + LABELS.timelineTitle +
            '<span class="swb-pnl-timeline-source">' + LABELS.timelineSource + '</span></span>' + ICONS.chevron + '</button>' +
            '<div class="swb-pnl-timeline-body" id="' + bodyId + '"><div class="swb-pnl-timeline-inner">' + inner + '</div></div></div>';
    }

    /* The complementary product row is a separate element parked right
       under this block. Any re-render (a retry from the failsafe form)
       goes through renderLoading first, so this is the single place that
       has to take the stale row away. */
    function clearRecos(ctx) {
        if (ctx.recos && ctx.recos.parentNode) ctx.recos.parentNode.removeChild(ctx.recos);
        ctx.recos = null;
    }

    function renderLoading(ctx) {
        clearRecos(ctx);
        ctx.state = 'loading';

        /* Hide, right now, the two things a successful live status ALWAYS
           replaces: the card's own estimate stepper and its outbound tracking
           button. Without this the customer sees the estimate-based card for
           the length of the fetch and then watches it swap, which reads as the
           page loading twice. Only what is certain to be replaced is hidden:
           .delivery-prediction survives many live results, so hiding it here
           would just move the flicker. Removed again by renderFallback, so a
           failed lookup gets the card's own version back. */
        ctx.card.classList.add('swb-pnl-pending');
        ctx.block.className = 'swb-pnl swb-pnl--loading';
        ctx.block.innerHTML =
            brandHtml(ctx && (ctx.carrierLabel || ctx.carrier)) +
            '<div class="swb-pnl-skel" role="status" aria-live="polite">' +
            '<span class="swb-pnl-skel-circle"></span>' +
            '<span class="swb-pnl-skel-lines">' +
            '<span class="swb-pnl-skel-bar swb-pnl-skel-bar--pill"></span>' +
            '<span class="swb-pnl-skel-bar swb-pnl-skel-bar--title"></span>' +
            '<span class="swb-pnl-skel-bar swb-pnl-skel-bar--sub"></span>' +
            '</span></div>' +
            '<p class="swb-pnl-skel-note">' + LABELS.loading + '</p>';
    }

    function renderResult(ctx, data, barcode) {
        clearWatchdog(ctx);

        var phase = parseInt(data.phase, 10);
        if (isNaN(phase)) phase = 1;
        phase = Math.min(4, Math.max(1, phase));
        var delivered = data.isDelivered === true || phase === 4;
        if (delivered) phase = 4;

        var pillLabel = delivered ? STEP_LABELS[3] : STEP_LABELS[phase - 1];
        var statusIcon = delivered ? ICONS.delivered : (phase >= 3 ? ICONS.transit : ICONS.registered);
        /* The headline. statusDescription comes from the carrier, and on the
           LandMark path that is their English wording ("Delivered (Delivered
           at Letterbox)") which read as English on a Dutch page. When the
           newest event carries a code we can translate, prefer our own copy;
           otherwise keep the carrier's own sentence, which is the case for
           PostNL where it is already localised. */
        var statusTitle = safeText(data.statusDescription) || pillLabel;
        var newestEvent = (data.events && data.events.length) ? data.events[0] : null;
        if (newestEvent && EVENT_CODE_KEYS[String(newestEvent.code || '').toUpperCase()]) {
            statusTitle = eventText(newestEvent) || statusTitle;
        }
        var letterbox = isLetterbox(data);
        var helpCopy = scenarioHelp(data, phase, delivered, letterbox);
        var typeLabel = shipmentTypeLabel(data);

        var dest = (data.destination && typeof data.destination === 'object') ? data.destination : {};
        var destCity = safeText(dest.city);
        var destZip = safeText(dest.zip);
        var destLine = '';
        if (destCity || destZip) {
            var zipCity = [displayPostcode(destZip), destCity].filter(Boolean).join(' ');
            destLine = (delivered ? LABELS.destDone : LABELS.destTo) + zipCity;
        }

        var banner = bannerHtml(data, delivered);
        var code = normBarcode(safeText(data.barcode)) || normBarcode(barcode);

        ctx.state = 'result';
        ctx.block.className = 'swb-pnl swb-pnl--result' + (delivered ? ' swb-pnl--delivered' : '');
        ctx.block.innerHTML =
            brandHtml(ctx && (ctx.carrierLabel || ctx.carrier)) +
            '<div class="swb-pnl-hero">' +
            '<span class="swb-pnl-hero-icon">' + statusIcon + '</span>' +
            '<span class="swb-pnl-hero-text">' +
            '<span class="swb-pnl-pill">' + esc(pillLabel) + '</span>' +
            '<span class="swb-pnl-title">' + esc(statusTitle) + '</span>' +
            (destLine ? '<span class="swb-pnl-dest">' + esc(destLine) + '</span>' : '') +
            (typeLabel ? '<span class="swb-pnl-type">' + LABELS.shipmentType + '<strong>' + esc(typeLabel) + '</strong></span>' : '') +
            '</span></div>' +
            (helpCopy ? '<p class="swb-pnl-help">' + esc(helpCopy) + '</p>' : '') +
            banner +
            stepperHtml(phase, delivered) +
            timelineHtml(ctx, data, phase, delivered) +
            (code ? '<p class="swb-pnl-code"><span>' + LABELS.barcodeLabel + '</span><code>' + esc(code) + '</code></p>' : '') +
            freshnessHtml(data);

        wireTimeline(ctx);

        /* Complementary products, from the order the proxy recognised.
           Lazy, non blocking and completely silent on failure: it is
           attached after the status is already painted, it renders
           nothing at all when it cannot find a decent row, and a throw
           in here may never take the status or the card down. */
        try {
            /* Off unless the theme setting turns it on. The strip shipped with
               its headings hardcoded in Dutch ("Maak je bandje compleet"), so
               every non-NL market read Dutch under an English or French order,
               and its product matching was loose enough to suggest screen
               protectors for watches the customer does not own. Switched off
               on all markets until both are fixed; the flag makes turning it
               back on a checkbox rather than a code change. */
            if (CS_CONFIG.showRecommendations === true &&
                window.SwbPnlRecos && typeof window.SwbPnlRecos.attach === 'function') {
                ctx.recos = window.SwbPnlRecos.attach(ctx.block, data.order);
            }
        } catch (recosError) {
            ctx.recos = null;
        }

        /* The live carrier status now owns the shipment story in this
           card, so the card's own estimate engine steps aside. */
        ctx.card.classList.remove('swb-pnl-pending');
        ctx.card.classList.add('swb-pnl-live');
        if (delivered || banner) ctx.card.classList.add('swb-pnl-supersedes');
        else ctx.card.classList.remove('swb-pnl-supersedes');
    }

    /* Direct link to PostNL's own consumer track-and-trace page, which holds
       the status for every destination we ship to. Rendered only when we have
       both a barcode and a postcode. */
    /* The "official status page" link shown when a live lookup fails.

       This used to always build a jouw.postnl.nl URL, which is wrong for any
       parcel PostNL does not carry: a failed Belgian LandMark lookup offered a
       PostNL page that could never find it, and a German Warenpost parcel the
       same. So prefer the tracking URL the shipping platform put on the
       fulfilment (right per carrier by construction), and only fall back to
       building a PostNL URL when the barcode really is a PostNL one. If
       neither applies, render no link at all rather than a dead end. */
    function officialLinkHtml(barcode, postcode, ctx) {
        var url = '';

        var direct = (ctx && typeof ctx.trackUrl === 'string') ? ctx.trackUrl.trim() : '';
        if (/^https?:\/\//i.test(direct)) {
            url = direct;
        } else {
            var code = normBarcode(barcode);
            var zip = normPostcode(postcode);
            var isPnl = /^3S/.test(code) || /^[A-Z]{2}[0-9]{9}NL$/.test(code);
            if (!isPnl || !BARCODE_RE.test(code) || !zip) return '';
            url = 'https://jouw.postnl.nl/track-and-trace/' + encodeURIComponent(code) +
                  '-' + M.country + '-' + encodeURIComponent(zip);
        }
        if (!url) return '';

        return '<a class="swb-pnl-official" href="' + esc(url) + '" target="_blank" rel="noopener">' +
               esc(S('official_page', 'Bekijk de officiële statuspagina')) + '</a>';
    }

    function renderFallback(ctx, kind, barcode, postcode) {
        clearWatchdog(ctx);
        var key = Object.prototype.hasOwnProperty.call(FALLBACK_COPY, String(kind)) ? String(kind) : 'unavailable';
        var copy = FALLBACK_COPY[key];

        // A failed live lookup is not authoritative, so the card's own
        // estimate engine comes back.
        ctx.card.classList.remove('swb-pnl-pending');
        ctx.card.classList.remove('swb-pnl-live');
        ctx.card.classList.remove('swb-pnl-supersedes');

        /* The caller is authoritative about what goes in the fields. An
           explicit empty string means "do not prefill this one", which is
           what the no_tracking route wants: the order's own tracking value
           is a placeholder like "Not yet shipped" there, and normalising
           that would drop NOTYETSHIPPED into the customer's field. */
        var prefillCode = normBarcode(typeof barcode === 'string' ? barcode : ctx.barcode);
        var prefillZip = displayPostcode(typeof postcode === 'string' ? postcode : ctx.zip);
        var codeId = ctx.id + '-code';
        var zipId = ctx.id + '-zip';

        ctx.state = 'fallback';

        /* The order card above already carries the barcode and the postcode.
           When both are present and well formed there is nothing for the
           customer to type, so a second form asking for the same two values
           reads as a broken feature. In that case the fallback collapses into
           one inline note under the shipment line plus the official status
           link, and a retry for the failures that are only temporary. The full
           form stays for the routes where a value really is missing. */
        var haveCode = BARCODE_RE.test(prefillCode);
        var haveZip = !!normPostcode(prefillZip);
        var canRetry = (key === 'unavailable' || key === 'api_error');

        /* Only the routes where the failure is on our side or the carrier's
           can collapse. The routes that need the customer to supply or correct
           a value always get the form, whatever the prefill happens to look
           like: "Not yet shipped" normalises to NOTYETSHIPPED, which passes a
           shape test but is not a barcode. */
        var CUSTOMER_MUST_ACT = { no_tracking: 1, no_zip: 1, invalid: 1 };

        if (haveCode && haveZip && !CUSTOMER_MUST_ACT[key]) {
            ctx.block.className = 'swb-pnl swb-pnl--fallback swb-pnl--fallback-inline';
            ctx.block.innerHTML =
                brandHtml(ctx && (ctx.carrierLabel || ctx.carrier)) +
                '<div class="swb-pnl-fb swb-pnl-fb--inline">' +
                '<span class="swb-pnl-fb-icon">' + ICONS.alert + '</span>' +
                '<span class="swb-pnl-fb-body">' +
                '<span class="swb-pnl-fb-lead">' + esc(copy.lead) + '</span>' +
                '<span class="swb-pnl-fb-msg">' + esc(copy.message) + '</span>' +
                '</span></div>' +
                '<div class="swb-pnl-fb-actions">' +
                officialLinkHtml(prefillCode, prefillZip, ctx) +
                (canRetry ? '<button type="button" class="swb-pnl-retry" data-swb-pnl-retry>' +
                    esc(S('fb_retry', 'Opnieuw proberen')) + '</button>' : '') +
                '</div>';

            var retryBtn = ctx.block.querySelector('[data-swb-pnl-retry]');
            if (retryBtn) {
                retryBtn.addEventListener('click', function () {
                    retryBtn.disabled = true;
                    run(ctx, prefillCode, normPostcode(prefillZip));
                });
            }
            return;
        }

        ctx.block.className = 'swb-pnl swb-pnl--fallback';
        ctx.block.innerHTML =
            // No shipment exists yet on the no_tracking route, so the
            // "Verzonden met PostNL" chip would be a false claim there.
            (key === 'no_tracking' ? '' : brandHtml(ctx && (ctx.carrierLabel || ctx.carrier))) +
            '<div class="swb-pnl-fb">' +
            '<span class="swb-pnl-fb-icon">' + ICONS.alert + '</span>' +
            '<span class="swb-pnl-fb-body">' +
            '<span class="swb-pnl-fb-lead">' + esc(copy.lead) + '</span>' +
            '<span class="swb-pnl-fb-msg">' + esc(copy.message) + '</span>' +
            '</span></div>' +
            officialLinkHtml(prefillCode, prefillZip, ctx) +
            '<div class="swb-pnl-fields">' +
            '<div class="swb-pnl-field">' +
            '<label for="' + codeId + '">' + LABELS.fieldBarcode + '</label>' +
            '<input type="text" id="' + codeId + '" data-swb-pnl-code autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="3SHDUT123456789" value="' + esc(prefillCode) + '">' +
            '</div>' +
            '<div class="swb-pnl-field">' +
            '<label for="' + zipId + '">' + LABELS.fieldPostcode + '</label>' +
            '<input type="text" id="' + zipId + '" data-swb-pnl-zip autocomplete="postal-code" autocapitalize="characters" spellcheck="false" placeholder="' + esc(M.ph) + '" value="' + esc(prefillZip) + '">' +
            '</div></div>' +
            '<p class="swb-pnl-fb-error" data-swb-pnl-error hidden></p>' +
            '<button type="button" class="swb-pnl-btn" data-swb-pnl-submit>' + LABELS.submit + '</button>';

        wireFallback(ctx);
    }

    /* ---- wiring -------------------------------------------- */

    function wireTimeline(ctx) {
        var timeline = ctx.block.querySelector('[data-swb-pnl-timeline]');
        var toggle = ctx.block.querySelector('[data-swb-pnl-toggle]');
        if (!timeline || !toggle) return;

        // Open on desktop, collapsed on mobile so the card stays compact.
        var desktop = false;
        try {
            desktop = window.matchMedia('(min-width: 768px)').matches;
        } catch (e) {
            desktop = false;
        }
        if (desktop) {
            timeline.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
        }
        // A real button driving a class: a display:flex summary element
        // does not toggle on iOS Safari.
        toggle.addEventListener('click', function () {
            var open = timeline.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
    }

    function wireFallback(ctx) {
        var codeInput = ctx.block.querySelector('[data-swb-pnl-code]');
        var zipInput = ctx.block.querySelector('[data-swb-pnl-zip]');
        var errorEl = ctx.block.querySelector('[data-swb-pnl-error]');
        var button = ctx.block.querySelector('[data-swb-pnl-submit]');
        if (!button) return;

        function fail(message) {
            if (!errorEl) return;
            errorEl.textContent = message;
            errorEl.removeAttribute('hidden');
        }

        function submit() {
            if (errorEl) {
                errorEl.textContent = '';
                errorEl.setAttribute('hidden', 'hidden');
            }
            var code = normBarcode(codeInput ? codeInput.value : '');
            var zip = normPostcode(zipInput ? zipInput.value : '');
            if (!BARCODE_RE.test(code)) {
                fail(LABELS.errBarcode);
                if (codeInput) codeInput.focus();
                return;
            }
            if (!POSTCODE_RE.test(zip)) {
                fail(LABELS.errPostcode);
                if (zipInput) zipInput.focus();
                return;
            }
            button.disabled = true;
            button.textContent = LABELS.submitBusy;
            run(ctx, code, zip);
        }

        button.addEventListener('click', submit);
        [codeInput, zipInput].forEach(function (input) {
            if (!input) return;
            input.addEventListener('keydown', function (event) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    submit();
                }
            });
        });
    }

    /* ---- the run ------------------------------------------- */

    function run(ctx, barcode, postcode) {
        renderLoading(ctx);
        armWatchdog(ctx, barcode, postcode);

        fetchStatus(barcode, postcode, ctx && ctx.carrier, ctx && ctx.country).then(function (data) {
            if (ctx.state !== 'loading') return;
            if (!data || typeof data !== 'object' || data.ok !== true) {
                var kind = (data && data.ok === false) ? String(data.error || 'unavailable') : 'unavailable';
                renderFallback(ctx, kind, barcode, postcode);
                return;
            }
            if (!hasSubstance(data)) {
                renderFallback(ctx, 'unavailable', barcode, postcode);
                return;
            }
            try {
                renderResult(ctx, data, barcode);
            } catch (e) {
                renderFallback(ctx, 'unavailable', barcode, postcode);
            }
        }).catch(function () {
            if (ctx.state !== 'loading') return;
            renderFallback(ctx, 'unavailable', barcode, postcode);
        });
    }

    /* ---- entry point --------------------------------------- */

    /* Called by lookupOrder once the order card is on screen.
       opts.carrier      the carrier the card already decided to show
       opts.hasTracking  the verdict of the card's own hasValidTracking */
    function attach(card, data, opts) {
        if (!card || !data || typeof data !== 'object') return;
        if (card.querySelector('[data-swb-pnl]')) return;
        opts = opts || {};

        // Merchant kill switch on the section (data-live-tracking on the
        // results host). Scoped to this card, so a second copy of the
        // widget on one page can never answer for the first.
        try {
            var host = card.closest ? card.closest('[data-live-tracking]') : null;
            if (host && host.getAttribute('data-live-tracking') === 'off') return;
        } catch (e) { /* no closest support: the feature simply stays on */ }

        var addr = (data.shippingAddress && typeof data.shippingAddress === 'object') ? data.shippingAddress : {};

        // Gate: every market we serve ships through PostNL, so every market
        // gets the live status. We no longer require the DISPLAYED carrier to
        // be PostNL (it is the local doorstep partner outside NL) nor the
        // destination to be the Netherlands. An unsupported market keeps
        // exactly the behaviour it has today.
        if (!MARKET_CFG[MKT]) return;
        // Markets whose barcode range PostNL has not authorised skip the live
        // lookup and fall back to the card's estimate + Track Package link.
        if (LIVE_MARKETS.indexOf(MKT) === -1) return;

        /* The market list is a manual switch; this is the real safety net.
           A market can be on the list and still receive a parcel from another
           carrier's network (Belgium ships LandMark Global but its customers
           use the Dutch storefront, which IS on the list), so gate on the
           shipment itself, not just on where the shopper is. */
        if (!isTrackableBarcode((data && data.trackingNumber) || '', (data && data.carrier) || '')) return;

        /* Point the {carrier} substitution at THIS parcel's carrier, then
           rebuild the labels that embed it. LABELS and the help/step copy are
           built at module load, so without this a Belgian parcel would keep
           the market's "PostNL" in every sentence. */
        ACTIVE_CARRIER = String((opts && opts.carrier) || CARRIER);
        LABELS.deliveredOn = S('delivered_on');
        LABELS.timelineSource = S('timeline_source');
        LABELS.eventsEmptyEarly = S('events_empty_early');
        PHASE_HELP[1] = S('help_1');
        PHASE_HELP[2] = S('help_2');
        PHASE_HELP[3] = S('help_3');
        PHASE_HELP.delivered = S('help_delivered');
        PHASE_HELP.deliveredLetterbox = S('help_delivered_letterbox');
        FALLBACK_COPY.no_tracking.message = S('fb_no_tracking_msg');
        FALLBACK_COPY.no_zip.message = S('fb_no_zip_msg');

        // A cancelled or refunded order has no live shipment to show.
        var fin = String(data.financialStatus || '');
        if (fin === 'Refunded' || fin === 'partially_refunded' || data.status === 'Cancelled') return;

        var block = document.createElement('div');
        block.className = 'swb-pnl';
        block.setAttribute('data-swb-pnl', '');

        // Under the existing order details, above the line items.
        var anchor = card.querySelector('.order-mobile-tracking') ||
                     card.querySelector('.order-details') ||
                     card.querySelector('.order-card-body-v2');
        if (!anchor) return;
        if (anchor.classList.contains('order-card-body-v2')) anchor.insertAdjacentElement('afterbegin', block);
        else anchor.insertAdjacentElement('afterend', block);

        uid += 1;
        var ctx = {
            id: 'swbPnl' + uid,
            block: block,
            card: card,
            recos: null,
            watchdog: null,
            state: 'idle',
            barcode: normBarcode(data.trackingNumber),
            zip: normPostcode(addr.zip),
            /* The carrier's own tracking link, straight off the fulfilment.
               Used by the fallback so a failed lookup points at the carrier
               that actually has the parcel, not at PostNL. */
            trackUrl: (data && typeof data.trackingUrl === 'string') ? data.trackingUrl : '',
            /* Shopify's tracking_company. Sent to the proxy so it can route on
               the carrier rather than guess from the barcode format. */
            carrier: (data && typeof data.carrier === 'string') ? data.carrier : '',
            /* The parcel's own destination, not the storefront's market: a
               Belgian order on the Dutch site needs BE or the proxy refuses
               the postcode. Falls back to the market when absent. */
            country: String((addr && addr.countryCode) || M.country || '').toUpperCase(),
            /* What the card decided to call the carrier ("Bpost"), as opposed
               to what Shopify records ("Landmark Global") which is what the
               routing needs. */
            carrierLabel: String((opts && opts.carrier) || '')
        };

        var hasTracking = (opts.hasTracking === true) ||
            (opts.hasTracking !== false && BARCODE_RE.test(ctx.barcode));

        if (!hasTracking || !BARCODE_RE.test(ctx.barcode)) {
            // A sentinel like "Not yet shipped" normalises to a string that
            // looks like a barcode, so drop it here as well and nothing
            // downstream can resurrect it.
            ctx.barcode = '';
            renderFallback(ctx, 'no_tracking', '', ctx.zip);
            return;
        }
        /* Test the postcode against the DESTINATION country, not the market.

           A storefront serves more than one country: Belgian customers shop on
           the Dutch site, and a Belgian postcode is four digits ("2610") while
           the Dutch pattern demands four digits plus two letters. Using the
           market's pattern therefore rejected every Belgian parcel here and
           showed the "check your postcode" panel for a perfectly valid one. */
        if (!postcodeLooksValid(ctx.zip, addr.countryCode)) {
            renderFallback(ctx, 'no_zip', ctx.barcode, '');
            return;
        }
        run(ctx, ctx.barcode, ctx.zip);
    }

    window.SwbPnlTracking = { attach: attach };
})();

/* ============================================================
   COMPLEMENTARY PRODUCTS UNDER THE LIVE POSTNL BLOCK (SWB)

   The proxy that answers the live status also hands back the order
   it recognised:
       order: { name, items: [ { productId, title, handle, image,
                                 productType, tags } ] }
   That is the only source used here. The CS lookup's own lineItems
   carry no product id, so they can never drive a recommendation.

   The rule is a complementary FLIP, ported from the standalone
   tracking page this block replaced:
     bought a strap (tag STRAP)          -> show accessories
     bought an accessory (ACCESSORY,
     CASE, SCREEN, CHARGER, UPSELL)      -> show straps
   and always for the watch they actually own. product_type is empty
   on every BWJ product, so tags are the only classifier. Model tags
   are the ALL CAPS tokens that are not brand, material, category or
   pipeline noise, which drops ALLPRODUCTS, gh*, n8n_batch*,
   filtervariant-*, variant_* and smartcoll_* on the regex alone.

   Scope rules this block never breaks:
     - It runs only after the live status has rendered, lazily, and a
       failure here is completely silent. It may never touch the
       tracking block, the failsafe or the order card.
     - Fewer than 3 usable products means no row at all. Never a stub,
       never a hanging spinner, never an empty heading.
     - 4 cards maximum. The standalone page showed 8; this is an
       inline context and the card stays light.
     - Every link points at our own storefront.
     - No em dashes and no en dashes in customer facing copy.
     - Namespaced swb-pnl-rec* so it can never collide with the CS
       page's own components.

   Everything between the ENGINE START and ENGINE END markers is
   DOM free and is exercised directly against the live storefront by
   the validation harness.
   ============================================================ */
(function () {
    'use strict';

    /* Inline context, not the standalone page. */
    var RECS_LIMIT = 4;
    var RECS_MIN = 3;

    var COPY = {
        accessory: {
            title: 'Maak je bandje compleet',
            subFor: 'Deze accessoires passen op je ',
            subPlain: 'Deze accessoires passen op je smartwatch.'
        },
        strap: {
            title: 'Nog een bandje erbij?',
            subFor: 'Deze bandjes passen op je ',
            subPlain: 'Deze bandjes passen op je smartwatch.'
        }
    };

    function esc(value) {
        return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    /* ============================================================
       ENGINE START
       ============================================================ */

    function shopRoot() {
        var root = '/';
        try {
            if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
                root = String(window.Shopify.routes.root);
            }
        } catch (e) {
            root = '/';
        }
        if (!root) root = '/';
        if (root.charAt(root.length - 1) !== '/') root += '/';
        return root;
    }

    function shopCurrency() {
        try {
            if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
                return String(window.Shopify.currency.active).toUpperCase();
            }
        } catch (e) { /* fall through to the euro */ }
        return 'EUR';
    }

    /* Verified live on smartwatchbanden.nl: straps carry STRAP,
       accessories carry ACCESSORY plus one of CASE, SCREEN, CHARGER.
       product_type is an empty string on every endpoint, so tags are
       the only reliable classifier here. */
    var TAG_STRAP = 'STRAP';
    var TAG_ACCESSORY_SET = { ACCESSORY: 1, CASE: 1, SCREEN: 1, CHARGER: 1, UPSELL: 1 };

    var TAG_CATEGORY_SET = {
        STRAP: 1, ACCESSORY: 1, CASE: 1, SCREEN: 1, CHARGER: 1, UPSELL: 1,
        ALLPRODUCTS: 1, BUNDLE: 1, GIFT: 1, SALE: 1, NEW: 1, OUTLET: 1
    };
    var TAG_MATERIAL_SET = {
        SILICONE: 1, SILICONEN: 1, NYLON: 1, MILANESE: 1, LEATHER: 1, LEDER: 1,
        METAL: 1, METALEN: 1, STEEL: 1, STAAL: 1, RVS: 1, MESH: 1, TPU: 1,
        RUBBER: 1, CANVAS: 1, BRAIDED: 1, GEVLOCHTEN: 1, MAGNETIC: 1,
        TITANIUM: 1, KERAMIEK: 1, CERAMIC: 1, GLASS: 1, GLAS: 1
    };
    var TAG_BRAND_SET = {
        GARMIN: 1, SAMSUNG: 1, APPLE: 1, FITBIT: 1, HUAWEI: 1, POLAR: 1,
        AMAZFIT: 1, XIAOMI: 1, SUUNTO: 1, WITHINGS: 1, GOOGLE: 1, FOSSIL: 1,
        TOMTOM: 1, OPPO: 1, ONEPLUS: 1, HONOR: 1, TICWATCH: 1, MOBVOI: 1,
        COROS: 1, NOTHING: 1, REDMI: 1, UNIVERSEEL: 1, UNIVERSAL: 1
    };

    var BRAND_WORDS = [
        'garmin', 'samsung', 'apple', 'fitbit', 'huawei', 'polar', 'amazfit',
        'xiaomi', 'suunto', 'withings', 'google', 'fossil', 'tomtom', 'oppo',
        'oneplus', 'honor', 'ticwatch', 'mobvoi', 'coros', 'nothing', 'redmi'
    ];

    /* Regex literals on purpose: in a JS string a backslash b is a
       BACKSPACE, so a pattern assembled from strings matches nothing. */
    var ACCESSORY_TITLE_RE = /(screenprotector|screen protector|schermbeschermer|protector|hard\s*case|\bcase\b|hoesje|cover|bumper|beschermhoes|etui|oplader|oplaadkabel|charger|charging|dock|kabel|\bcable\b|one-click|\btool\b)/i;
    var STRAP_TITLE_RE = /(bandje|bandjes|\bband\b|\bbanden\b|strap|straps|loop|armband|horlogeband)/i;

    var COLOUR_WORDS_RE = new RegExp('\\b(' + [
        'zwart', 'wit', 'grijs', 'donkergrijs', 'lichtgrijs', 'antraciet',
        'blauw', 'donkerblauw', 'lichtblauw', 'navy', 'kobalt', 'aqua',
        'groen', 'donkergroen', 'lichtgroen', 'olijf', 'khaki', 'mint',
        'rood', 'bordeaux', 'roze', 'paars', 'lavendel', 'violet', 'lila',
        'beige', 'bruin', 'taupe', 'creme', 'ivoor', 'geel', 'oranje',
        'goud', 'zilver', 'rosegoud', 'rose', 'brons', 'turquoise',
        'transparant', 'multicolour', 'multicolor', 'regenboog', 'rainbow',
        'sterrenlicht', 'starlight', 'dragonfruit', 'camo', 'marine', 'mix',
        'black', 'white', 'grey', 'gray', 'blue', 'green', 'red', 'pink',
        'purple', 'silver', 'gold', 'dark', 'light', 'clear'
    ].join('|') + ')\\b', 'g');

    function upperTagMap(tags) {
        var map = {};
        if (!Array.isArray(tags)) return map;
        tags.forEach(function (t) {
            if (typeof t === 'string' && t) map[t.toUpperCase()] = 1;
        });
        return map;
    }

    function classifyFromTitle(title) {
        var t = String(title || '');
        if (ACCESSORY_TITLE_RE.test(t)) return 'accessory';
        if (STRAP_TITLE_RE.test(t)) return 'strap';
        return '';
    }

    function classifyProduct(tags, title) {
        var map = upperTagMap(tags);
        if (map[TAG_STRAP]) return 'strap';
        var key;
        for (key in TAG_ACCESSORY_SET) {
            if (map[key]) return 'accessory';
        }
        return classifyFromTitle(title);
    }

    /* Model tags are the ALL CAPS tokens that are not brand, material,
       category or pipeline noise: VIVOACTIVE6, VIVOSMART5, FENIX5X. */
    function isModelTag(tag) {
        if (typeof tag !== 'string') return false;
        if (!/^[A-Z][A-Z0-9]{2,}$/.test(tag)) return false;
        if (TAG_CATEGORY_SET[tag]) return false;
        if (TAG_MATERIAL_SET[tag]) return false;
        if (TAG_BRAND_SET[tag]) return false;
        return true;
    }

    function modelTagsOf(tags) {
        var out = [];
        if (!Array.isArray(tags)) return out;
        tags.forEach(function (t) {
            if (isModelTag(t) && out.indexOf(t) === -1) out.push(t);
        });
        return out;
    }

    function brandTagOf(tags) {
        var found = '';
        if (!Array.isArray(tags)) return found;
        tags.forEach(function (t) {
            if (found) return;
            if (typeof t === 'string' && TAG_BRAND_SET[t.toUpperCase()]) found = t.toUpperCase();
        });
        return found;
    }

    // VIVOACTIVE6 becomes "vivoactive 6", FENIX5X becomes "fenix 5x".
    function modelTagToWords(tag) {
        var m = /^([A-Z]+?)(\d[A-Z0-9]*)$/.exec(String(tag || ''));
        if (m) return (m[1] + ' ' + m[2]).toLowerCase();
        return String(tag || '').toLowerCase();
    }

    function brandFromTitle(title) {
        var t = String(title || '').toLowerCase();
        var found = '';
        BRAND_WORDS.forEach(function (b) {
            if (found) return;
            if (t.indexOf(b) !== -1) found = b;
        });
        return found;
    }

    // "Garmin Vivosmart 5 siliconen bandje (bordeaux)" becomes "vivosmart 5"
    function modelWordsFromTitle(title) {
        var t = String(title || '').toLowerCase();
        t = t.replace(/\([^)]*\)/g, ' ');
        t = t.replace(/[^a-z0-9]+/g, ' ').trim();
        var stop = {
            siliconen: 1, silicone: 1, nylon: 1, milanese: 1, leren: 1, leer: 1,
            leather: 1, metalen: 1, staal: 1, stalen: 1, mesh: 1, gevlochten: 1,
            braided: 1, bandje: 1, bandjes: 1, band: 1, banden: 1, strap: 1,
            loop: 1, armband: 1, hard: 1, case: 1, hoesje: 1, cover: 1,
            screenprotector: 1, protector: 1, schermbeschermer: 1, oplader: 1,
            oplaadkabel: 1, charger: 1, kabel: 1, cable: 1, met: 1, voor: 1,
            classic: 1, sport: 1, alpine: 1, glas: 1, glass: 1, tool: 1,
            click: 1, one: 1, universeel: 1, bandz: 1
        };
        var words = t.split(' ');
        var out = [];
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!w) continue;
            if (BRAND_WORDS.indexOf(w) !== -1) continue;
            if (stop[w]) break;
            out.push(w);
            if (out.length >= 4) break;
        }
        return out.join(' ');
    }

    function toCents(value) {
        if (typeof value === 'number' && isFinite(value)) return Math.round(value);
        if (typeof value === 'string' && value) {
            var n = parseFloat(value.replace(',', '.'));
            if (isFinite(n)) return Math.round(n * 100);
        }
        return null;
    }

    function pickImage(product) {
        var fi = product.featured_image;
        if (typeof fi === 'string' && fi) return fi;
        if (fi && typeof fi === 'object' && typeof fi.url === 'string' && fi.url) return fi.url;
        if (typeof product.image === 'string' && product.image) return product.image;
        if (product.image && typeof product.image === 'object' && typeof product.image.url === 'string') return product.image.url;
        return '';
    }

    /* recommendations/products.json returns CENTS, search/suggest.json
       returns euro STRINGS. Normalise both into one shape in cents. */
    function normProduct(raw) {
        if (!raw || typeof raw !== 'object') return null;
        var id = parseInt(raw.id, 10);
        if (isNaN(id) || id <= 0) return null;
        if (raw.available === false) return null;
        var handle = typeof raw.handle === 'string' ? raw.handle : '';
        /* The url is rebuilt from the handle rather than taken from the
           payload, so the pr_rec_id and _psq tracking parameters that
           search and recommendations bolt on never travel with the link. */
        if (!handle) return null;
        var priceCents = toCents(raw.price !== undefined ? raw.price : raw.price_min);
        if (priceCents === null) return null;
        var compareCents = toCents(
            raw.compare_at_price !== undefined && raw.compare_at_price !== null
                ? raw.compare_at_price
                : raw.compare_at_price_max
        );
        if (compareCents !== null && compareCents <= priceCents) compareCents = null;
        return {
            id: id,
            title: String(raw.title || ''),
            handle: handle.toLowerCase(),
            url: shopRoot() + 'products/' + handle,
            image: pickImage(raw),
            priceCents: priceCents,
            compareCents: compareCents,
            tags: Array.isArray(raw.tags) ? raw.tags : [],
            matchesModel: false
        };
    }

    /* Normalised stem used to cap near identical products. Strips the
       parenthetical colour, a trailing colour segment after a dash and
       any standalone colour word, but keeps the model words intact. */
    function titleStem(title) {
        var t = String(title || '').toLowerCase();
        t = t.replace(/\([^)]*\)/g, ' ');
        /* Escaped rather than literal so this source file itself stays
           free of em and en dashes; product TITLES do contain them. */
        t = t.replace(/\s*[-\u2013\u2014][^-\u2013\u2014]*$/, function (seg) {
            COLOUR_WORDS_RE.lastIndex = 0;
            var isColour = COLOUR_WORDS_RE.test(seg);
            COLOUR_WORDS_RE.lastIndex = 0;
            return isColour ? ' ' : seg;
        });
        t = t.replace(/[^a-z0-9]+/g, ' ');
        t = t.replace(COLOUR_WORDS_RE, ' ');
        COLOUR_WORDS_RE.lastIndex = 0;
        t = t.replace(/\s+/g, ' ').trim();
        return t;
    }

    function fetchJson(url) {
        return fetch(url, {
            headers: { 'Accept': 'application/json' },
            credentials: 'same-origin'
        }).then(function (res) {
            return res.ok ? res.json() : null;
        }).catch(function () {
            return null;
        });
    }

    function relatedUrl(productId, limit) {
        return shopRoot() + 'recommendations/products.json?product_id=' +
            encodeURIComponent(productId) + '&intent=related&limit=' + limit;
    }

    function collectionUrl(handle, limit) {
        return shopRoot() + 'collections/' + encodeURIComponent(handle) +
            '/products.json?limit=' + limit;
    }

    function suggestUrl(query, limit) {
        return shopRoot() + 'search/suggest.json?q=' + encodeURIComponent(query) +
            '&resources[type]=product&resources[limit]=' + limit;
    }

    function fetchRelated(productId) {
        return fetchJson(relatedUrl(productId, 10)).then(function (json) {
            return (json && Array.isArray(json.products)) ? json.products : [];
        });
    }

    /* Kept from the standalone engine so the source order stays intact.
       On BWJ this answers 200 with an empty products array today, so it
       costs one tiny request and contributes nothing; it starts working
       by itself the day those collections are exposed. */
    function fetchCollection(handle) {
        return fetchJson(collectionUrl(handle, 50)).then(function (json) {
            return (json && Array.isArray(json.products)) ? json.products : [];
        });
    }

    function fetchSuggest(query) {
        return fetchJson(suggestUrl(query, 10)).then(function (json) {
            if (!json || !json.resources || !json.resources.results) return [];
            var products = json.resources.results.products;
            return Array.isArray(products) ? products : [];
        });
    }

    // Reads the purchased items and works out what to show instead.
    function readPurchase(order) {
        var out = {
            ids: {},
            handles: {},
            seedIds: [],
            bought: '',
            want: 'accessory',
            brand: '',
            modelWords: '',
            modelTags: [],
            hasModel: false
        };
        var items = (order && Array.isArray(order.items)) ? order.items : [];
        var sawStrap = false;
        var sawAccessory = false;

        items.forEach(function (item) {
            if (!item) return;
            var pid = parseInt(item.productId, 10);
            if (!isNaN(pid) && pid > 0) {
                out.ids[pid] = true;
                if (out.seedIds.indexOf(pid) === -1 && out.seedIds.length < 2) out.seedIds.push(pid);
            }
            if (typeof item.handle === 'string' && item.handle) {
                out.handles[item.handle.toLowerCase()] = true;
            }

            var tags = Array.isArray(item.tags) ? item.tags : null;
            var title = item.title || '';
            /* Graceful degradation: no tags on the payload means classify
               from the title with Dutch and English keywords. */
            var cat = tags ? classifyProduct(tags, title) : classifyFromTitle(title);
            if (cat === 'strap') sawStrap = true;
            else if (cat === 'accessory') sawAccessory = true;

            if (tags) {
                var brand = brandTagOf(tags);
                if (brand && !out.brand) out.brand = brand.toLowerCase();
                modelTagsOf(tags).forEach(function (m) {
                    if (out.modelTags.indexOf(m) === -1) out.modelTags.push(m);
                });
            }
            if (!out.brand) {
                var tb = brandFromTitle(title);
                if (tb) out.brand = tb;
            }
            if (!out.modelWords) {
                var mw = modelWordsFromTitle(title);
                if (mw) out.modelWords = mw;
            }
        });

        if (!out.modelWords && out.modelTags.length) {
            out.modelWords = modelTagToWords(out.modelTags[0]);
        }
        out.hasModel = !!(out.modelTags.length || out.modelWords);

        if (sawStrap && sawAccessory) out.bought = 'mixed';
        else if (sawStrap) out.bought = 'strap';
        else if (sawAccessory) out.bought = 'accessory';

        /* The flip: a strap buyer gets accessories, an accessory buyer
           gets straps, a mixed order prefers accessories. */
        out.want = (out.bought === 'accessory') ? 'strap' : 'accessory';
        return out;
    }

    function categoryWordsFor(want) {
        return want === 'strap'
            ? ['bandje']
            : ['screenprotector', 'hard case', 'oplader'];
    }

    function collectionHandleFor(want, brand) {
        if (!brand) return '';
        return want === 'strap' ? brand + '-bandjes' : brand + '-accessoires';
    }

    /* Does this product fit the watch that was actually bought? A model
       tag match is the strong signal; the model words appearing as whole
       tokens in the title is the fallback for products missing the tag. */
    function fitsModel(purchase, product) {
        var map = upperTagMap(product.tags);
        var hit = false;
        purchase.modelTags.forEach(function (m) {
            if (map[m]) hit = true;
        });
        if (hit) return true;
        if (!purchase.modelWords) return false;
        var tokens = {};
        String(product.title || '').toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .split(' ')
            .forEach(function (w) { if (w) tokens[w] = 1; });
        var wanted = purchase.modelWords.split(' ');
        for (var i = 0; i < wanted.length; i++) {
            if (wanted[i] && !tokens[wanted[i]]) return false;
        }
        return true;
    }

    /* Collector with dedupe: excludes the purchased items, the wrong
       category, anything that does not fit the customer's watch, and
       caps near identical titles at 2 per stem so the row is never one
       product in four colours. */
    function makeCollector(purchase) {
        var seenIds = {};
        var stemCount = {};
        var items = [];

        function accept(raw) {
            var p = normProduct(raw);
            if (!p) return;
            if (seenIds[p.id]) return;
            if (purchase.ids[p.id]) return;
            if (p.handle && purchase.handles[p.handle]) return;
            if (classifyProduct(p.tags, p.title) !== purchase.want) return;

            var stem = titleStem(p.title);
            var count = stemCount[stem] || 0;
            if (count >= 2) return;

            var matchesModel = fitsModel(purchase, p);
            /* Fit is the whole purchase criterion on this store, so when
               we know the customer's watch we never show something that
               does not fit it. Better no row than a wrong fit upsell. */
            if (purchase.hasModel && !matchesModel) return;
            p.matchesModel = matchesModel;

            stemCount[stem] = count + 1;
            seenIds[p.id] = true;
            items.push(p);
        }

        return {
            add: function (list) { (list || []).forEach(accept); },
            count: function () { return items.length; },
            result: function () {
                var primary = items.filter(function (p) { return p.matchesModel; });
                var rest = items.filter(function (p) { return !p.matchesModel; });
                return primary.concat(rest).slice(0, RECS_LIMIT);
            }
        };
    }

    /* Sourcing priority, stopping as soon as the row is full:
         1. brand collection for the opposite category, model filtered,
            plus predictive search on brand + model words + category word
         2. intent=related on the purchased ids, filtered by category
         3. predictive search on brand + a category word (brand level) */
    function gatherComplementary(purchase) {
        var collector = makeCollector(purchase);
        var catWords = categoryWordsFor(purchase.want);

        function stageOne() {
            var jobs = [];
            var handle = collectionHandleFor(purchase.want, purchase.brand);
            if (handle) jobs.push(fetchCollection(handle));
            if (purchase.modelWords) {
                catWords.forEach(function (cat) {
                    var q = (purchase.brand ? purchase.brand + ' ' : '') + purchase.modelWords + ' ' + cat;
                    jobs.push(fetchSuggest(q));
                });
            }
            if (!jobs.length) return Promise.resolve();
            return Promise.all(jobs).then(function (lists) {
                lists.forEach(function (list) { collector.add(list); });
            });
        }

        function stageTwo() {
            if (collector.count() >= RECS_LIMIT) return Promise.resolve();
            if (!purchase.seedIds.length) return Promise.resolve();
            return Promise.all(purchase.seedIds.map(fetchRelated)).then(function (lists) {
                lists.forEach(function (list) { collector.add(list); });
            });
        }

        function stageThree() {
            if (collector.count() >= RECS_MIN) return Promise.resolve();
            if (!purchase.brand) return Promise.resolve();
            return Promise.all(catWords.map(function (cat) {
                return fetchSuggest(purchase.brand + ' ' + cat);
            })).then(function (lists) {
                lists.forEach(function (list) { collector.add(list); });
            });
        }

        return stageOne()
            .then(stageTwo)
            .then(stageThree)
            .then(function () {
                var products = collector.result();
                // Fewer than 3 keepers means no row at all.
                if (products.length < RECS_MIN) return [];
                return products;
            })
            .catch(function () {
                return [];
            });
    }

    function titleCase(words) {
        return String(words || '').split(' ').map(function (w) {
            return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
        }).join(' ');
    }

    /* Only ever built from a model TAG, which is clean by construction.
       The title derived model words are good enough to filter with but
       not good enough to print. */
    function deviceLabel(purchase) {
        if (!purchase.modelTags.length) return '';
        var model = titleCase(modelTagToWords(purchase.modelTags[0]));
        if (!model) return '';
        var brand = purchase.brand ? titleCase(purchase.brand) : '';
        var label = brand ? brand + ' ' + model : model;
        return label.length > 32 ? '' : label;
    }

    // Headings follow what is in the row, never the other way round.
    function recsCopy(purchase) {
        var base = COPY[purchase.want] || COPY.accessory;
        var device = deviceLabel(purchase);
        return {
            title: base.title,
            sub: device ? base.subFor + device + '.' : base.subPlain
        };
    }

    function money(cents) {
        var n = Number(cents);
        if (!isFinite(n)) return '';
        var value = n / 100;
        try {
            var loc = (function () {
              var short = String((window.CS_CONFIG && window.CS_CONFIG.locale) || 'nl').toLowerCase();
              var map = { nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', en: 'en-GB' };
              return map[short] || short || 'nl-NL';
          })();
          return new Intl.NumberFormat(loc, {
                style: 'currency',
                currency: shopCurrency(),
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        } catch (e) {
            return '€ ' + value.toFixed(2).replace('.', ',');
        }
    }

    /* ============================================================
       ENGINE END
       ============================================================ */

    function imgSrc(src) {
        if (typeof src !== 'string' || !src) return '';
        var url = src;
        if (url.indexOf('//') === 0) url = 'https:' + url;
        if (url.indexOf('http') !== 0) return '';
        if (url.indexOf('width=') === -1) {
            url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=400';
        }
        return url;
    }

    function cardHtml(product) {
        var src = imgSrc(product.image);
        var media = src
            ? '<img class="swb-pnl-rec-img" src="' + esc(src) + '" alt="' + esc(product.title) + '" loading="eager" decoding="async" width="400" height="400">'
            : '<span class="swb-pnl-rec-img swb-pnl-rec-img--empty" aria-hidden="true"></span>';

        return '<li class="swb-pnl-rec-item"><a class="swb-pnl-rec-card" href="' + esc(product.url) + '">' +
            '<span class="swb-pnl-rec-media">' + media + '</span>' +
            '<span class="swb-pnl-rec-body">' +
            '<span class="swb-pnl-rec-title">' + esc(product.title) + '</span>' +
            '<span class="swb-pnl-rec-price">' + esc(money(product.priceCents)) +
            (product.compareCents ? '<s class="swb-pnl-rec-compare">' + esc(money(product.compareCents)) + '</s>' : '') +
            '</span></span></a></li>';
    }

    function rowHtml(purchase, products) {
        var cards = products.map(cardHtml).join('');
        if (!cards) return '';
        var copy = recsCopy(purchase);
        return '<div class="swb-pnl-recos-head">' +
            '<h3 class="swb-pnl-recos-title">' + esc(copy.title) + '</h3>' +
            '<p class="swb-pnl-recos-sub">' + esc(copy.sub) + '</p>' +
            '</div><ul class="swb-pnl-rec-grid">' + cards + '</ul>';
    }

    /* ---- entry point --------------------------------------- */

    /* Called by the live status block once it has painted a result.
       afterEl  the rendered tracking block, this row goes right under it
       order    the order the PostNL proxy recognised
       Returns the host element so the caller can drop it on a re-render,
       or null when there is nothing to do. */
    function attach(afterEl, order) {
        if (!afterEl || !afterEl.parentNode) return null;
        if (!order || typeof order !== 'object') return null;
        if (!Array.isArray(order.items) || !order.items.length) return null;
        if (typeof window.fetch !== 'function') return null;

        var host = document.createElement('div');
        host.className = 'swb-pnl-recos';
        host.setAttribute('data-swb-pnl-recos', '');
        host.setAttribute('hidden', 'hidden');
        afterEl.insertAdjacentElement('afterend', host);

        function drop() {
            if (host.parentNode) host.parentNode.removeChild(host);
        }

        function start() {
            var purchase;
            try {
                purchase = readPurchase(order);
            } catch (e) {
                drop();
                return;
            }
            if (!purchase.seedIds.length) {
                drop();
                return;
            }
            gatherComplementary(purchase).then(function (products) {
                try {
                    if (!products || products.length < RECS_MIN) {
                        drop();
                        return;
                    }
                    var html = rowHtml(purchase, products);
                    if (!html) {
                        drop();
                        return;
                    }
                    host.innerHTML = html;
                    host.removeAttribute('hidden');
                } catch (e) {
                    drop();
                }
            }).catch(drop);
        }

        /* Lazy and non blocking. The status is already painted and this
           may never hold it, or the order card, up. */
        try {
            if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(start, { timeout: 1500 });
            } else {
                window.setTimeout(start, 120);
            }
        } catch (e) {
            window.setTimeout(start, 120);
        }
        return host;
    }

    window.SwbPnlRecos = { attach: attach };
})();

/* ============================================================
   SERVICE HEALTH PROBE (n8n / order lookup maintenance notice)
   Smartwatchbanden / BWJ customer service

   The maintenance notice above the order-lookup widget is
   server-rendered ON. This routine removes it automatically as
   soon as the automation server answers correctly again, with
   no redeploy needed.

   Health proof: POST {"barcode":"PING","postcode":"0000AA"} to the
   status webhook. A healthy server rejects that barcode through its
   own validation path and answers HTTP 200 with
   {"ok":false,"error":"invalid", ...}. ONLY that exact shape counts
   as healthy. A 5xx, a Cloudflare 522, an HTML body, a CORS or
   network rejection and a timeout all count as still unhealthy, so
   the notice simply stays as rendered.

   The verdict is cached in sessionStorage for 3 minutes so a visitor
   browsing several service pages probes at most once per TTL.
   Everything here is non-blocking and failure silent.
   ============================================================ */
(function () {
    'use strict';

    var PROBE_URL  = 'https://actium.automagically.nl/webhook/ping';
    var CACHE_KEY  = 'swb_n8n_health_v1';
    var TTL_MS     = 3 * 60 * 1000;
    var TIMEOUT_MS = 5000;

    function notices() {
        try {
            return document.querySelectorAll('[data-cs-maint]');
        } catch (e) {
            return [];
        }
    }

    function hideNotices() {
        var nodes = notices();
        for (var i = 0; i < nodes.length; i++) {
            try {
                nodes[i].setAttribute('hidden', 'hidden');
                nodes[i].style.display = 'none';
            } catch (e) { /* ignore */ }
        }
    }

    function showNotices() {
        var nodes = notices();
        for (var i = 0; i < nodes.length; i++) {
            try {
                nodes[i].removeAttribute('hidden');
                nodes[i].style.display = '';
            } catch (e) { /* ignore */ }
        }
    }

    /* Returns true (healthy), false (unhealthy) or null (no fresh verdict). */
    function readCache() {
        try {
            var raw = window.sessionStorage.getItem(CACHE_KEY);
            if (!raw) { return null; }
            var parsed = JSON.parse(raw);
            if (!parsed || typeof parsed.t !== 'number') { return null; }
            if (Date.now() - parsed.t > TTL_MS) { return null; }
            return parsed.healthy === true;
        } catch (e) {
            return null;
        }
    }

    function writeCache(healthy) {
        try {
            window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                healthy: healthy === true,
                t: Date.now()
            }));
        } catch (e) { /* private mode throws, verdict just is not cached */ }
    }

    function probe() {
        var timer = null;
        var options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
            cache: 'no-store'
        };

        function clear() {
            if (timer) { window.clearTimeout(timer); timer = null; }
        }

        try {
            if (typeof window.AbortController === 'function') {
                var controller = new window.AbortController();
                options.signal = controller.signal;
                timer = window.setTimeout(function () {
                    try { controller.abort(); } catch (e) { /* ignore */ }
                }, TIMEOUT_MS);
            }
        } catch (e) { /* no AbortController, fetch still resolves or rejects */ }

        return window.fetch(PROBE_URL, options).then(function (res) {
            clear();
            if (!res) { return false; }
            /* A 4xx means n8n ITSELF answered, so the server is up and the
               order lookup is fine; only the probe path is wrong. Treating that
               as "down" would show every customer a false maintenance notice,
               which is worse than missing one. So we fail OPEN on 4xx and only
               count a real infrastructure failure (5xx, 52x, network, timeout)
               as unhealthy. */
            if (res.status >= 500) { return false; }
            if (res.ok !== true) { return true; }
            return res.text().then(function (raw) {
                var data = null;
                try { data = JSON.parse(raw); } catch (e) { return false; }
                if (!data || typeof data !== 'object') { return false; }
                return data.ok === true;
            }, function () {
                return false;
            });
        }, function () {
            clear();
            return false;
        });
    }

    function run() {
        /* Setting is off, or this page has no notice: make no request at all. */
        if (!notices().length) { return; }
        if (typeof window.fetch !== 'function') { return; }

        /* The notice is rendered hidden, so a healthy page never flashes it.
           It is revealed ONLY on positive evidence that n8n is unhealthy. A
           known-unhealthy verdict from this session reveals it with no request. */
        var cached = readCache();
        if (cached === true)  { return; }
        if (cached === false) { showNotices(); return; }

        try {
            probe().then(function (healthy) {
                writeCache(healthy);
                if (!healthy) { showNotices(); }
            })['catch'](function () { /* silent */ });
        } catch (e) { /* silent */ }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
})();

/* cache-bust 20260902T115835Z */
